// src/components/auth/AuthView.tsx
import { useState as useState9 } from "react";
import { useTranslation as useTranslation8 } from "react-i18next";

// src/components/reusable/AuthScaffold.tsx
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Text } from "@buyscout/components";
import clsx from "clsx";

// src/hooks/useMediaQuery.ts
import { useCallback, useSyncExternalStore } from "react";
var BELOW_SM = "(width < 600px)";
var BELOW_MD = "(width < 900px)";
var BELOW_LG = "(width < 1200px)";
var REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
function useMediaQuery(query) {
  const subscribe = useCallback((onChange) => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return () => {
      };
    }
    const list = window.matchMedia(query);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);
  return useSyncExternalStore(
    subscribe,
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches,
    () => false
  );
}

// src/components/stats/typeRamp.ts
var lgType = {
  "large-title": "text-(length:--bs-lg-type-large-title-size) leading-(--bs-lg-type-large-title-leading) tracking-(--bs-lg-type-large-title-tracking)",
  "title-1": "text-(length:--bs-lg-type-title-1-size) leading-(--bs-lg-type-title-1-leading) tracking-(--bs-lg-type-title-1-tracking)",
  "title-3": "text-(length:--bs-lg-type-title-3-size) leading-(--bs-lg-type-title-3-leading) tracking-(--bs-lg-type-title-3-tracking)",
  "card-title": "text-(length:--bs-lg-type-card-title-size) leading-(--bs-lg-type-card-title-leading) tracking-(--bs-lg-type-card-title-tracking)",
  "section": "text-(length:--bs-lg-type-section-size) leading-(--bs-lg-type-section-leading) tracking-(--bs-lg-type-section-tracking)",
  "headline": "text-(length:--bs-lg-type-headline-size) leading-(--bs-lg-type-headline-leading) tracking-(--bs-lg-type-headline-tracking)",
  "body": "text-(length:--bs-lg-type-body-size) leading-(--bs-lg-type-body-leading) tracking-(--bs-lg-type-body-tracking)",
  "callout": "text-(length:--bs-lg-type-callout-size) leading-(--bs-lg-type-callout-leading) tracking-(--bs-lg-type-callout-tracking)",
  "subheadline": "text-(length:--bs-lg-type-subheadline-size) leading-(--bs-lg-type-subheadline-leading) tracking-(--bs-lg-type-subheadline-tracking)"
};
var lgTypeOverride = {
  "large-title": "!text-(length:--bs-lg-type-large-title-size) !leading-(--bs-lg-type-large-title-leading) !tracking-(--bs-lg-type-large-title-tracking)",
  "title-1": "!text-(length:--bs-lg-type-title-1-size) !leading-(--bs-lg-type-title-1-leading) !tracking-(--bs-lg-type-title-1-tracking)",
  "title-3": "!text-(length:--bs-lg-type-title-3-size) !leading-(--bs-lg-type-title-3-leading) !tracking-(--bs-lg-type-title-3-tracking)",
  "card-title": "!text-(length:--bs-lg-type-card-title-size) !leading-(--bs-lg-type-card-title-leading) !tracking-(--bs-lg-type-card-title-tracking)",
  "section": "!text-(length:--bs-lg-type-section-size) !leading-(--bs-lg-type-section-leading) !tracking-(--bs-lg-type-section-tracking)",
  "headline": "!text-(length:--bs-lg-type-headline-size) !leading-(--bs-lg-type-headline-leading) !tracking-(--bs-lg-type-headline-tracking)",
  "body": "!text-(length:--bs-lg-type-body-size) !leading-(--bs-lg-type-body-leading) !tracking-(--bs-lg-type-body-tracking)",
  "callout": "!text-(length:--bs-lg-type-callout-size) !leading-(--bs-lg-type-callout-leading) !tracking-(--bs-lg-type-callout-tracking)",
  "subheadline": "!text-(length:--bs-lg-type-subheadline-size) !leading-(--bs-lg-type-subheadline-leading) !tracking-(--bs-lg-type-subheadline-tracking)"
};

// src/components/reusable/Icon.tsx
import { Icon as IconifyIcon } from "@iconify/react";
import { jsx } from "react/jsx-runtime";
function Icon({ icon, fallback, ...rest }) {
  const { className, style, color, width, height, inline, fontSize } = rest;
  const placeholder = /* @__PURE__ */ jsx(
    "svg",
    {
      "aria-hidden": true,
      className,
      fontSize,
      width: width ?? height ?? "1em",
      height: height ?? width ?? "1em",
      style: { color, verticalAlign: inline ? "-0.125em" : void 0, ...style }
    }
  );
  return /* @__PURE__ */ jsx(IconifyIcon, { icon, ...rest, children: fallback ?? placeholder });
}

// src/components/reusable/AuthScaffold.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var SHOWCASE_SRC = "/images/misc/auth-showcase.jpg";
var HERO_INK = "text-[#343f52] [:where(.buyscout-theme-dark)_&]:text-(--bs-text-primary)";
var HERO_INK_SECONDARY = "text-[#343f52] [:where(.buyscout-theme-dark)_&]:text-(--bs-text-secondary)";
var ROOT = "flex min-h-screen gap-6 bg-(--bs-lg-surface) p-6 text-(--bs-text-primary) max-[900px]:gap-0 max-[900px]:p-0";
var FORM_PANEL = "relative flex flex-auto flex-col px-12 py-5 max-[900px]:px-5 max-[900px]:py-6";
var FORM_HEADER = "flex min-h-[42px] flex-none items-center";
var FORM_BODY = "flex flex-auto flex-col items-center [justify-content:safe_center] py-6";
var BRAND_NAME = "text-(length:--bs-lg-type-title-2-size) leading-(--bs-lg-type-title-2-leading) tracking-(--bs-lg-type-title-2-tracking) font-semibold text-balance break-keep text-(--color-text-primary)";
var BRAND_PANEL = "relative isolate flex flex-[0_0_50%] flex-col justify-end overflow-hidden rounded-(--radius-bento) bg-[#e9e4dd] p-7 text-[#343f52] max-[900px]:hidden";
var SCRIM = "pointer-events-none absolute inset-0 z-1 bg-[linear-gradient(to_bottom,rgba(31,39,66,0.16)_0%,rgba(31,39,66,0)_30%,rgba(31,39,66,0.12)_100%)]";
var GLASS_COPY = "relative z-2 rounded-(--radius-bento-nested) bg-(--bs-lg-panel-glass-bg) p-6 [box-shadow:var(--bs-lg-panel-glass-shadow)] [-webkit-backdrop-filter:var(--bs-lg-panel-glass-filter)] [backdrop-filter:var(--bs-lg-panel-glass-filter)]";
var TITLE = clsx(lgType["large-title"], "mt-0 font-normal [&_b]:block [&_b]:font-semibold [&_b]:text-current", HERO_INK);
var SUBTITLE = clsx(lgType["title-3"], "mt-2 max-w-[400px]", HERO_INK_SECONDARY);
var COPY_PANE = "pointer-events-none [grid-area:1/1] opacity-0 [transition:opacity_var(--bs-lg-spring-smooth-duration)_var(--bs-lg-spring-smooth)] data-active:pointer-events-auto data-active:opacity-100 motion-reduce:[transition:none]";
var FORM_HEADING_TITLE = clsx(lgTypeOverride["title-1"], "!mb-1 max-[900px]:!mt-8");
var TOGGLE_ROW = "mt-4 flex items-center gap-2 [&_label]:!text-(length:--bs-lg-type-headline-size) [&_label]:!leading-(--bs-lg-type-headline-leading) [&_label]:!tracking-[0] [&_label]:!text-(--color-text-secondary) [&_label]:cursor-pointer";
function AuthFormHeading({ title, subtitle, className }) {
  return /* @__PURE__ */ jsxs("div", { className: clsx("mb-12", className), children: [
    /* @__PURE__ */ jsx2(Text, { variant: "heading-2", as: "h1", className: FORM_HEADING_TITLE, children: title }),
    /* @__PURE__ */ jsx2(Text, { variant: "body-secondary", as: "p", children: subtitle })
  ] });
}
function AuthToggleRow({ children }) {
  return /* @__PURE__ */ jsx2("div", { className: TOGGLE_ROW, children });
}
function AuthBrandLockup({ name }) {
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-3", children: [
    /* @__PURE__ */ jsx2("span", { className: "grid size-[38px] flex-none place-items-center overflow-hidden rounded-xl", children: /* @__PURE__ */ jsx2("img", { className: "size-full object-contain", src: "/images/misc/buyscout-icon.png", alt: "" }) }),
    /* @__PURE__ */ jsx2("b", { className: BRAND_NAME, children: name })
  ] });
}
var BACK_SLOT = "ms-[calc(-1*var(--bs-lg-control-padding-md))]";
var CAPABILITIES = [
  {
    key: "performance",
    icon: "lucide:chart-column",
    label: "Performance",
    lead: "Know exactly",
    emphasis: "what's working.",
    subtitle: "Track conversations, engagement, and add-to-cart lift \u2014 see your AI agent turn browsers into buyers in real time."
  },
  {
    key: "conversations",
    icon: "lucide:messages-square",
    label: "Conversations",
    lead: "Every conversation,",
    emphasis: "in one place.",
    subtitle: "Open any chat your agent has had \u2014 what shoppers asked, how it answered, and where it closed the sale."
  },
  {
    key: "knowledge_base",
    icon: "lucide:book-open",
    label: "Knowledge",
    lead: "Teach it once,",
    emphasis: "it never forgets.",
    subtitle: "Add your catalog, policies, and FAQs as knowledge articles \u2014 your agent learns them and answers every shopper accurately."
  },
  {
    key: "settings",
    icon: "lucide:settings",
    label: "Settings",
    lead: "Your store,",
    emphasis: "your rules.",
    subtitle: "Configure upsell triggers, prompts, greetings, discounts, and voice \u2014 shape exactly how your agent sells and sounds."
  },
  {
    key: "upsell",
    icon: "lucide:shopping-cart",
    label: "Upsell",
    lead: "Bigger carts,",
    emphasis: "on autopilot.",
    subtitle: "Set upsell and cross-sell rules once \u2014 your agent suggests the right add-on at the right moment, hands-free."
  }
];
var AUTOPLAY_MS = 5500;
function AuthScaffold(props) {
  const { templateName, back, children } = props;
  const { t } = useTranslation();
  const compact = useMediaQuery(BELOW_MD);
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const [activeCapability, setActiveCapability] = useState(0);
  const [paused, setPaused] = useState(false);
  const [manualSelectNonce, setManualSelectNonce] = useState(0);
  useEffect(() => {
    if (compact || paused || reducedMotion) {
      return;
    }
    const timer = setInterval(() => {
      setActiveCapability((index) => (index + 1) % CAPABILITIES.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [compact, paused, reducedMotion, manualSelectNonce]);
  const selectCapability = (index) => {
    setActiveCapability(index);
    setManualSelectNonce((nonce) => nonce + 1);
  };
  return /* @__PURE__ */ jsxs("div", { className: ROOT, children: [
    /* @__PURE__ */ jsxs("section", { className: FORM_PANEL, children: [
      /* @__PURE__ */ jsx2("div", { className: FORM_HEADER, children: back ? /* @__PURE__ */ jsx2("div", { className: BACK_SLOT, children: /* @__PURE__ */ jsxs(Button, { variant: "borderless", color: "neutral", size: "md", onClick: back.onClick, children: [
        /* @__PURE__ */ jsx2(Icon, { icon: "lucide:chevron-left", width: 20, height: 20 }),
        back.label
      ] }) }) : /* @__PURE__ */ jsx2(AuthBrandLockup, { name: templateName }) }),
      /* @__PURE__ */ jsx2("div", { className: FORM_BODY, children: /* @__PURE__ */ jsx2("div", { className: "w-full max-w-[400px]", children }) })
    ] }),
    !compact && /* @__PURE__ */ jsxs("aside", { className: BRAND_PANEL, "aria-hidden": true, onMouseEnter: () => setPaused(true), onMouseLeave: () => setPaused(false), children: [
      /* @__PURE__ */ jsx2("img", { className: "absolute inset-0 z-0 size-full object-cover object-[center_32%]", src: SHOWCASE_SRC, alt: "" }),
      /* @__PURE__ */ jsx2("span", { className: SCRIM }),
      /* @__PURE__ */ jsxs("div", { className: GLASS_COPY, children: [
        /* @__PURE__ */ jsx2("div", { className: "grid", children: CAPABILITIES.map((capability, index) => /* @__PURE__ */ jsxs("div", { className: COPY_PANE, "data-active": index === activeCapability || void 0, children: [
          /* @__PURE__ */ jsxs("p", { className: TITLE, children: [
            t(`shared.auth.brand.aspect.${capability.key}.headline_lead`, capability.lead),
            /* @__PURE__ */ jsx2("b", { children: t(`shared.auth.brand.aspect.${capability.key}.headline_emphasis`, capability.emphasis) })
          ] }),
          /* @__PURE__ */ jsx2("p", { className: SUBTITLE, children: t(`shared.auth.brand.aspect.${capability.key}.subtitle`, capability.subtitle) })
        ] }, capability.key)) }),
        /* @__PURE__ */ jsx2("div", { className: "mt-6 flex flex-wrap gap-2", children: CAPABILITIES.map((capability, index) => {
          const pressed = index === activeCapability;
          return /* @__PURE__ */ jsxs(
            Button,
            {
              tabIndex: -1,
              size: "md",
              shape: "pill",
              "aria-pressed": pressed,
              ...pressed ? { variant: "prominent", color: "ink" } : { variant: "bordered", color: "neutral" },
              onClick: () => selectCapability(index),
              children: [
                /* @__PURE__ */ jsx2(Icon, { icon: capability.icon }),
                t(`shared.auth.brand.capability.${capability.key}`, capability.label)
              ]
            },
            capability.key
          );
        }) })
      ] })
    ] })
  ] });
}

// src/hooks/useResendCountdown.ts
import { useEffect as useEffect2, useState as useState2 } from "react";

// src/utils/verification-code.ts
var RESEND_COOLDOWN_SECONDS = 30;
var TOO_MANY_REQUESTS = 429;
function isRefusedRequest(error) {
  return error instanceof Object && "status" in error && typeof error.status === "number";
}
async function readRetryAfterSeconds(response) {
  const body = response.error ?? (response.bodyUsed || !response.json ? null : await response.json().catch(() => null));
  const seconds = body?.retryAfterSeconds;
  return typeof seconds === "number" ? seconds : void 0;
}
async function acceptVerificationCooldown(send) {
  try {
    await send();
  } catch (error) {
    if (isRefusedRequest(error) && error.status === TOO_MANY_REQUESTS) {
      return { retryAfterSeconds: await readRetryAfterSeconds(error) };
    }
    throw error;
  }
}
function resendCooldownSeconds(result) {
  const seconds = result ? result.retryAfterSeconds : void 0;
  return typeof seconds === "number" && Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : RESEND_COOLDOWN_SECONDS;
}
function normalizeVerificationCode(value) {
  return value.replace(/\s+/g, "");
}
function formatCountdown(seconds) {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

// src/hooks/useResendCountdown.ts
function useResendCountdown(enabled) {
  const [resendAt, setResendAt] = useState2(0);
  const [now, setNow] = useState2(0);
  const secondsLeft = Math.max(0, Math.ceil((resendAt - now) / 1e3));
  useEffect2(() => {
    if (secondsLeft === 0) {
      return;
    }
    const timer = setTimeout(() => setNow(Date.now()), 1e3);
    return () => clearTimeout(timer);
  }, [secondsLeft, now]);
  const restart = (result) => {
    if (!enabled) {
      return;
    }
    const sentAt = Date.now();
    setNow(sentAt);
    setResendAt(sentAt + resendCooldownSeconds(result) * 1e3);
  };
  return { secondsLeft, restart };
}

// src/types/auth.ts
var AuthFormStage = /* @__PURE__ */ ((AuthFormStage2) => {
  AuthFormStage2[AuthFormStage2["Welcome"] = 0] = "Welcome";
  AuthFormStage2[AuthFormStage2["SignIn"] = 1] = "SignIn";
  AuthFormStage2[AuthFormStage2["SignUp"] = 2] = "SignUp";
  AuthFormStage2[AuthFormStage2["Verification"] = 3] = "Verification";
  return AuthFormStage2;
})(AuthFormStage || {});

// src/components/auth/SignInForm.tsx
import { useMemo, useState as useState3 } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation as useTranslation2 } from "react-i18next";
import { Button as Button2, Switch, TextField } from "@buyscout/components";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Fragment, jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function SignInFormInner(props) {
  const { subtitle, email, onSubmit, onResetPassword } = props;
  const { t } = useTranslation2();
  const [rememberMe, setRememberMe] = useState3(false);
  const [loading, setLoading] = useState3(false);
  const schema = useMemo(() => yup.object().shape({
    email: yup.string().email(t("shared.auth.sign_in.validation.email_invalid", "Please enter a valid email address.")).required(t("shared.auth.sign_in.validation.email_required", "Email is required.")),
    password: yup.string().required(t("shared.auth.sign_in.validation.password_required", "Password is required."))
  }), [t]);
  const defaultValues = {
    email,
    password: ""
  };
  const {
    control,
    setError,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });
  const handleSubmit_ = async (data) => {
    setLoading(true);
    try {
      await onSubmit(data, rememberMe);
    } catch {
      setError("password", {
        type: "manual",
        message: t("shared.auth.sign_in.error.invalid_credentials", "Email or Password is invalid")
      });
    } finally {
      setLoading(false);
    }
  };
  return /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx3(
      AuthFormHeading,
      {
        title: t("shared.auth.sign_in.title", "Sign In"),
        subtitle: subtitle ?? t("shared.auth.sign_in.subtitle", "Welcome back! Please sign in in to your account.")
      }
    ),
    /* @__PURE__ */ jsxs2("form", { noValidate: true, onSubmit: handleSubmit(handleSubmit_), children: [
      /* @__PURE__ */ jsx3(
        Controller,
        {
          name: "email",
          control,
          rules: { required: true },
          render: ({ field: { value, onChange, onBlur } }) => /* @__PURE__ */ jsx3(
            TextField,
            {
              className: "mb-4",
              fullWidth: true,
              size: "xl",
              type: "email",
              autoComplete: "username",
              startIcon: "mail",
              readOnly: true,
              label: t("shared.auth.sign_in.field.email_label", "Email"),
              spellCheck: false,
              value,
              onBlur,
              onChange,
              error: Boolean(errors.email),
              helperText: errors.email?.message,
              placeholder: t("shared.auth.sign_in.field.email_placeholder", "name@netprism.com")
            }
          )
        }
      ),
      /* @__PURE__ */ jsx3(
        Controller,
        {
          name: "password",
          control,
          rules: { required: true },
          render: ({ field: { value, onChange, onBlur } }) => /* @__PURE__ */ jsx3(
            TextField,
            {
              fullWidth: true,
              reserveHelperSpace: true,
              size: "xl",
              type: "password",
              autoComplete: "current-password",
              startIcon: "lock",
              autoFocus: true,
              label: t("shared.auth.sign_in.field.password_label", "Password"),
              value,
              onBlur,
              onChange,
              error: Boolean(errors.password),
              helperText: errors.password?.message,
              placeholder: t("shared.auth.sign_in.field.password_placeholder", "Required")
            }
          )
        }
      ),
      /* @__PURE__ */ jsxs2(AuthToggleRow, { children: [
        /* @__PURE__ */ jsx3(Switch, { id: "sign-in-remember", size: "sm", checked: rememberMe, onCheckedChange: setRememberMe }),
        /* @__PURE__ */ jsx3("label", { htmlFor: "sign-in-remember", children: t("shared.auth.sign_in.remember_me", "Remember Me") })
      ] }),
      /* @__PURE__ */ jsx3(
        Button2,
        {
          className: "mt-8",
          variant: "prominent",
          color: "primary",
          size: "xl",
          fullWidth: true,
          type: "submit",
          loading,
          children: t("shared.auth.sign_in.button.submit", "Sign In")
        }
      ),
      /* @__PURE__ */ jsx3(
        Button2,
        {
          className: "mt-2",
          variant: "borderless",
          color: "primary",
          size: "xl",
          fullWidth: true,
          onClick: () => onResetPassword(email),
          children: t("shared.auth.sign_in.button.reset_password", "Reset Password")
        }
      )
    ] })
  ] });
}
function SignInForm(props) {
  const { i18n } = useTranslation2();
  return /* @__PURE__ */ jsx3(SignInFormInner, { ...props }, i18n.language);
}

// src/components/auth/SignUpForm.tsx
import { useMemo as useMemo2, useState as useState4 } from "react";
import { Controller as Controller2, useForm as useForm2 } from "react-hook-form";
import { useTranslation as useTranslation3 } from "react-i18next";
import { Button as Button3, Switch as Switch2, TextField as TextField2 } from "@buyscout/components";
import { yupResolver as yupResolver2 } from "@hookform/resolvers/yup";
import * as yup2 from "yup";
import { Fragment as Fragment2, jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function SignUpFormInner(props) {
  const { subtitle, email, onSubmit, onSignUpSubmit } = props;
  const { t } = useTranslation3();
  const [rememberMe, setRememberMe] = useState4(false);
  const [loading, setLoading] = useState4(false);
  const schema = useMemo2(() => yup2.object().shape({
    name: yup2.string().transform((value) => value.trim()).min(2, t("shared.auth.sign_up.validation.name_min", "Name must be at least 2 characters long.")).max(50, t("shared.auth.sign_up.validation.name_max", "Name cannot be longer than 50 characters.")).required(t("shared.auth.sign_up.validation.name_required", "Name is required.")),
    email: yup2.string().email(t("shared.auth.sign_up.validation.email_invalid", "Please enter a valid email address.")).required(t("shared.auth.sign_up.validation.email_required", "Email is required.")),
    password: yup2.string().min(8, t("shared.auth.sign_up.validation.password_min", "Password must be at least 8 characters long.")).max(128, t("shared.auth.sign_up.validation.password_max", "Password cannot be longer than 128 characters.")).required(t("shared.auth.sign_up.validation.password_required", "Password is required."))
  }), [t]);
  const defaultValues = {
    email,
    name: "",
    password: ""
  };
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isValid }
  } = useForm2({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver2(schema)
  });
  const handleSubmit_ = async (data) => {
    setLoading(true);
    try {
      await onSubmit(data, rememberMe);
      onSignUpSubmit(data.name, data.password, rememberMe);
    } catch {
      setError("email", { message: t("shared.auth.sign_up.error.generic", "An error occurred during verification. Please try again.") });
    } finally {
      setLoading(false);
    }
  };
  return /* @__PURE__ */ jsxs3(Fragment2, { children: [
    /* @__PURE__ */ jsx4(
      AuthFormHeading,
      {
        title: t("shared.auth.sign_up.title", "Sign Up"),
        subtitle: subtitle ?? t("shared.auth.sign_up.subtitle", "It looks like you are new here. We just need a bit more information.")
      }
    ),
    /* @__PURE__ */ jsxs3("form", { noValidate: true, onSubmit: handleSubmit(handleSubmit_), children: [
      /* @__PURE__ */ jsx4(
        Controller2,
        {
          name: "email",
          control,
          rules: { required: true },
          render: ({ field: { value, onChange, onBlur } }) => /* @__PURE__ */ jsx4(
            TextField2,
            {
              className: "mb-4",
              fullWidth: true,
              size: "xl",
              type: "email",
              autoComplete: "username",
              startIcon: "mail",
              readOnly: true,
              label: t("shared.auth.sign_up.field.email_label", "Email"),
              value,
              onBlur,
              onChange,
              error: Boolean(errors.email),
              helperText: errors.email?.message,
              placeholder: t("shared.auth.sign_up.field.email_placeholder", "name@netprism.com")
            }
          )
        }
      ),
      /* @__PURE__ */ jsx4(
        Controller2,
        {
          name: "name",
          control,
          rules: { required: true },
          render: ({ field: { value, onChange, onBlur } }) => /* @__PURE__ */ jsx4(
            TextField2,
            {
              className: "mb-4",
              fullWidth: true,
              size: "xl",
              autoComplete: "name",
              startIcon: "person",
              autoFocus: true,
              label: t("shared.auth.sign_up.field.name_label", "Name"),
              value,
              onBlur,
              onChange,
              error: Boolean(errors.name),
              helperText: errors.name?.message,
              placeholder: t("shared.auth.sign_up.field.name_placeholder", "Full Name")
            }
          )
        }
      ),
      /* @__PURE__ */ jsx4(
        Controller2,
        {
          name: "password",
          control,
          rules: { required: true },
          render: ({ field: { value, onChange, onBlur } }) => /* @__PURE__ */ jsx4(
            TextField2,
            {
              fullWidth: true,
              reserveHelperSpace: true,
              size: "xl",
              type: "password",
              autoComplete: "new-password",
              startIcon: "lock",
              label: t("shared.auth.sign_up.field.password_label", "Password"),
              value,
              onBlur,
              onChange,
              error: Boolean(errors.password),
              helperText: errors.password?.message,
              placeholder: t("shared.auth.sign_up.field.password_placeholder", "Required")
            }
          )
        }
      ),
      /* @__PURE__ */ jsxs3(AuthToggleRow, { children: [
        /* @__PURE__ */ jsx4(Switch2, { id: "sign-up-remember", size: "sm", checked: rememberMe, onCheckedChange: setRememberMe }),
        /* @__PURE__ */ jsx4("label", { htmlFor: "sign-up-remember", children: t("shared.auth.sign_up.remember_me", "Remember Me") })
      ] }),
      /* @__PURE__ */ jsx4(
        Button3,
        {
          className: "mt-8",
          variant: "prominent",
          color: "primary",
          size: "xl",
          fullWidth: true,
          type: "submit",
          loading,
          disabled: !isValid,
          children: t("shared.auth.sign_up.button.continue", "Continue")
        }
      )
    ] })
  ] });
}
function SignUpForm(props) {
  const { i18n } = useTranslation3();
  return /* @__PURE__ */ jsx4(SignUpFormInner, { ...props }, i18n.language);
}

// src/components/auth/VerificationCodeForm.tsx
import { useCallback as useCallback2, useMemo as useMemo3, useState as useState6 } from "react";
import { Controller as Controller3, useForm as useForm3 } from "react-hook-form";
import { useTranslation as useTranslation5 } from "react-i18next";
import { Button as Button5, TextField as TextField3 } from "@buyscout/components";
import { yupResolver as yupResolver3 } from "@hookform/resolvers/yup";
import * as yup3 from "yup";

// src/components/auth/ResendCodeRow.tsx
import { useEffect as useEffect3, useRef, useState as useState5 } from "react";
import { useTranslation as useTranslation4 } from "react-i18next";
import { Button as Button4, Text as Text2, toast } from "@buyscout/components";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var RESEND_ROW = "mt-4 flex flex-col items-center gap-1 text-center";
function ResendCodeRow({ email, secondsLeft, onResend, focusCode }) {
  const { t } = useTranslation4();
  const [resending, setResending] = useState5(false);
  const focusAfterResendRef = useRef(null);
  const resendButtonRef = useRef(null);
  useEffect3(() => {
    const focusAfterResend = focusAfterResendRef.current;
    if (!focusAfterResend || resending) {
      return;
    }
    focusAfterResendRef.current = null;
    const focused = document.activeElement;
    if (focused && focused !== document.body && focused !== resendButtonRef.current) {
      return;
    }
    if (focusAfterResend === "code") {
      focusCode();
    } else {
      resendButtonRef.current?.focus();
    }
  }, [resending, focusCode]);
  const handleResend = async () => {
    setResending(true);
    try {
      const result = await onResend();
      if (!result?.retryAfterSeconds) {
        toast.success(t("shared.auth.verification.resend.sent", "We sent a new code to {{email}}.", { email }));
      }
      focusAfterResendRef.current = "code";
    } catch {
      toast.error(t("shared.auth.verification.resend.error", "We couldn't send a new code. Please try again."));
      focusAfterResendRef.current = "resend";
    } finally {
      setResending(false);
    }
  };
  return /* @__PURE__ */ jsxs4("div", { className: RESEND_ROW, children: [
    /* @__PURE__ */ jsx5(Text2, { variant: "body-secondary", as: "p", children: t("shared.auth.verification.resend.hint", "Didn't get it? Check spam.") }),
    /* @__PURE__ */ jsx5(
      Button4,
      {
        variant: "borderless",
        color: "primary",
        size: "md",
        ref: resendButtonRef,
        disabled: resending || secondsLeft > 0,
        onClick: handleResend,
        children: secondsLeft > 0 ? /* @__PURE__ */ jsx5("span", { className: "tabular-nums", children: t("shared.auth.verification.resend.countdown", "Resend code in {{time}}", { time: formatCountdown(secondsLeft) }) }) : t("shared.auth.verification.resend.button", "Resend code")
      }
    )
  ] });
}

// src/components/auth/VerificationCodeForm.tsx
import { Fragment as Fragment3, jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
function VerificationCodeFormInner(props) {
  const { subtitle, email, signUpData, resend } = props;
  const { t } = useTranslation5();
  const [loading, setLoading] = useState6(false);
  const schema = useMemo3(() => yup3.object().shape({
    code: yup3.string().transform(normalizeVerificationCode).required(t("shared.auth.verification.validation.code_required", "Verification code is required."))
  }), [t]);
  const defaultValues = {
    code: ""
  };
  const {
    control,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors, isValid }
  } = useForm3({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver3(schema)
  });
  const handleSubmitForm = async (data) => {
    setLoading(true);
    try {
      await props.onSubmit(data, signUpData);
    } catch {
      setError("code", { message: t("shared.auth.verification.error.generic", "An error occurred during verification. Please try again.") });
    } finally {
      setLoading(false);
    }
  };
  const focusCode = useCallback2(() => setFocus("code"), [setFocus]);
  return /* @__PURE__ */ jsxs5(Fragment3, { children: [
    /* @__PURE__ */ jsx6(
      AuthFormHeading,
      {
        title: t("shared.auth.verification.title", "Email Verification"),
        subtitle: subtitle ?? t("shared.auth.verification.subtitle", "A verification code has been sent to: {{email}}", { email })
      }
    ),
    /* @__PURE__ */ jsxs5("form", { noValidate: true, onSubmit: handleSubmit(handleSubmitForm), children: [
      /* @__PURE__ */ jsx6(
        Controller3,
        {
          name: "code",
          control,
          rules: { required: true },
          render: ({ field: { value, onChange, onBlur, ref } }) => /* @__PURE__ */ jsx6(
            TextField3,
            {
              ref,
              fullWidth: true,
              reserveHelperSpace: true,
              size: "xl",
              autoFocus: true,
              autoComplete: "one-time-code",
              inputMode: "numeric",
              spellCheck: false,
              label: t("shared.auth.verification.field.code_label", "Code"),
              value,
              onBlur,
              onChange: (event) => onChange(normalizeVerificationCode(event.target.value)),
              error: Boolean(errors.code),
              helperText: errors.code?.message,
              placeholder: t("shared.auth.verification.field.code_placeholder", "\u2022 \u2022 \u2022 \u2022 \u2022 \u2022")
            }
          )
        }
      ),
      /* @__PURE__ */ jsx6(
        Button5,
        {
          className: "mt-8",
          variant: "prominent",
          color: "primary",
          size: "xl",
          fullWidth: true,
          type: "submit",
          loading,
          disabled: !isValid,
          children: t("shared.auth.verification.button.confirm", "Confirm")
        }
      )
    ] }),
    resend && /* @__PURE__ */ jsx6(ResendCodeRow, { email, secondsLeft: resend.secondsLeft, onResend: resend.onResend, focusCode })
  ] });
}
function VerificationCodeForm(props) {
  const { i18n } = useTranslation5();
  return /* @__PURE__ */ jsx6(VerificationCodeFormInner, { ...props }, i18n.language);
}

// src/components/auth/WelcomeForm.tsx
import { useCallback as useCallback4, useMemo as useMemo4, useState as useState8 } from "react";
import { Controller as Controller4, useForm as useForm4 } from "react-hook-form";
import { useTranslation as useTranslation7 } from "react-i18next";
import { Button as Button6, Link, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Text as Text3, TextField as TextField4, toast as toast2 } from "@buyscout/components";
import { yupResolver as yupResolver4 } from "@hookform/resolvers/yup";
import clsx2 from "clsx";
import * as yup4 from "yup";

// src/components/reusable/GoogleSignInButton.tsx
import { useCallback as useCallback3, useEffect as useEffect4, useRef as useRef2, useState as useState7 } from "react";
import { useTranslation as useTranslation6 } from "react-i18next";
import { GoogleButton } from "@buyscout/components";
import { Fragment as Fragment4, jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
function GoogleSignInButton(props) {
  const {
    callback,
    clientId,
    beforeSignIn,
    loading = false
  } = props;
  const { t } = useTranslation6();
  const [googleLoaded, setGoogleLoaded] = useState7(false);
  const googleButtonRef = useRef2(null);
  const callbackRef = useRef2(callback);
  useEffect4(() => {
    callbackRef.current = callback;
  }, [callback]);
  useEffect4(() => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => callbackRef.current(response),
          ux_mode: "popup",
          use_fedcm_for_prompt: true,
          cancel_on_tap_outside: false,
          auto_select: false
        });
        setGoogleLoaded(true);
      } else {
        console.error("Google Identity Services initialize failed");
      }
    };
    script.onerror = () => {
      console.error("Load Google script error");
    };
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      if (window.google && window.google.accounts) {
        window.google.accounts.id.cancel();
      }
    };
  }, [clientId]);
  useEffect4(() => {
    if (googleLoaded && googleButtonRef.current && window.google) {
      window.google.accounts.id.renderButton(
        googleButtonRef.current,
        {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin_with",
          shape: "rectangular",
          logo_alignment: "left"
        }
      );
    }
  }, [googleLoaded]);
  const onClick = useCallback3(() => {
    if (beforeSignIn && !beforeSignIn()) {
      return;
    }
    googleButtonRef.current?.querySelector('div[role="button"]')?.click();
  }, [beforeSignIn]);
  return /* @__PURE__ */ jsxs6(Fragment4, { children: [
    /* @__PURE__ */ jsx7("div", { hidden: true, ref: googleButtonRef }),
    /* @__PURE__ */ jsx7(
      GoogleButton,
      {
        fullWidth: true,
        size: "xl",
        loading,
        disabled: !googleLoaded,
        onClick,
        children: t("shared.google_button.continue_with_google", "Continue with Google")
      }
    )
  ] });
}

// src/constants/countries.ts
var SUPPORTED_COUNTRIES = [
  { code: "US", label: "United States", flag: "\u{1F1FA}\u{1F1F8}" },
  { code: "GB", label: "United Kingdom", flag: "\u{1F1EC}\u{1F1E7}" },
  { code: "CA", label: "Canada", flag: "\u{1F1E8}\u{1F1E6}" },
  { code: "DE", label: "Germany", flag: "\u{1F1E9}\u{1F1EA}" },
  { code: "FR", label: "France", flag: "\u{1F1EB}\u{1F1F7}" },
  { code: "IT", label: "Italy", flag: "\u{1F1EE}\u{1F1F9}" },
  { code: "ES", label: "Spain", flag: "\u{1F1EA}\u{1F1F8}" },
  { code: "NL", label: "Netherlands", flag: "\u{1F1F3}\u{1F1F1}" },
  { code: "AU", label: "Australia", flag: "\u{1F1E6}\u{1F1FA}" },
  { code: "JP", label: "Japan", flag: "\u{1F1EF}\u{1F1F5}" }
];

// src/constants/urls.ts
var BUYSCOUT_URLS = {
  BASE: "https://www.buyscout.ai",
  HOME: "https://www.buyscout.ai",
  FAQ: "https://www.buyscout.ai/agent/faq",
  TERMS: "https://www.buyscout.ai/agent/terms",
  PRIVACY: "https://www.buyscout.ai/agent/privacy"
};

// src/utils/chat-message-format.tsx
function formatToolName(toolName) {
  return toolName.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()).trim();
}

// src/utils/chunk-reload.ts
var RELOAD_TIMESTAMP_KEY = "buyscout:chunk-reload-at";
var RELOAD_COOLDOWN_MS = 1e4;
function installChunkReloadHandler(onPreloadError) {
  if (typeof window === "undefined") return;
  window.addEventListener("vite:preloadError", (event) => {
    let lastReloadAt = 0;
    try {
      lastReloadAt = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? 0);
    } catch {
    }
    if (!Number.isFinite(lastReloadAt)) lastReloadAt = 0;
    if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) return;
    event.preventDefault();
    try {
      sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
    } catch {
    }
    try {
      onPreloadError?.(event.payload);
    } catch {
    }
    window.location.reload();
  });
}

// src/utils/date-range.ts
import dayjs from "dayjs";
function getDateRange(startDate, endDate) {
  const days = dayjs(endDate).diff(startDate, "days");
  return [...Array.from({ length: days + 1 }).keys()].map((i) => dayjs(startDate).add(i, "days").format("MM/dd/yyyy"));
}
var StatisticsTimeRange = /* @__PURE__ */ ((StatisticsTimeRange2) => {
  StatisticsTimeRange2["PastWeek"] = "Past Week";
  StatisticsTimeRange2["PastMonth"] = "Past Month";
  StatisticsTimeRange2["PastQuarter"] = "Past Quarter";
  StatisticsTimeRange2["AllTime"] = "All Time";
  return StatisticsTimeRange2;
})(StatisticsTimeRange || {});
function getStatisticsTimeRangeDates(timeRange) {
  const now = dayjs();
  switch (timeRange) {
    case "Past Week" /* PastWeek */:
      return {
        startDate: now.subtract(7, "days").toISOString(),
        endDate: now.toISOString()
      };
    case "Past Month" /* PastMonth */:
      return {
        startDate: now.subtract(1, "month").toISOString(),
        endDate: now.toISOString()
      };
    case "Past Quarter" /* PastQuarter */:
      return {
        startDate: now.subtract(3, "months").toISOString(),
        endDate: now.toISOString()
      };
    case "All Time" /* AllTime */:
      return {
        startDate: void 0,
        endDate: void 0
      };
    default:
      return {
        startDate: now.subtract(1, "month").toISOString(),
        endDate: now.toISOString()
      };
  }
}
function getPreviousStatisticsTimeRangeDates(timeRange) {
  const now = dayjs();
  switch (timeRange) {
    case "Past Week" /* PastWeek */:
      return {
        startDate: now.subtract(14, "days").toISOString(),
        endDate: now.subtract(7, "days").toISOString()
      };
    case "Past Month" /* PastMonth */:
      return {
        startDate: now.subtract(2, "months").toISOString(),
        endDate: now.subtract(1, "month").toISOString()
      };
    case "Past Quarter" /* PastQuarter */:
      return {
        startDate: now.subtract(6, "months").toISOString(),
        endDate: now.subtract(3, "months").toISOString()
      };
    case "All Time" /* AllTime */:
    default:
      return {
        startDate: void 0,
        endDate: void 0
      };
  }
}

// src/utils/detect-user-country.ts
function detectUserCountry(supportedCodes) {
  const supported = new Set(supportedCodes);
  const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const locale of locales) {
    const region = locale.split("-")[1]?.toUpperCase();
    if (region && supported.has(region)) {
      return region;
    }
  }
  return "";
}

// src/utils/message.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
function formatMessageContent(content) {
  const parts = [];
  const regex = /\*\*(.*?)\*\*/g;
  const matches = Array.from(content.matchAll(regex));
  let lastIndex = 0;
  matches.forEach((match) => {
    if (match.index !== void 0 && match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }
    parts.push(
      /* @__PURE__ */ jsx8("strong", { children: match[1] }, match.index)
    );
    if (match.index !== void 0) {
      lastIndex = match.index + match[0].length;
    }
  });
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }
  return parts.length > 0 ? parts : [content];
}

// src/types/reset-password.ts
var ResetPasswordStep = /* @__PURE__ */ ((ResetPasswordStep2) => {
  ResetPasswordStep2[ResetPasswordStep2["Email"] = 0] = "Email";
  ResetPasswordStep2[ResetPasswordStep2["Verification"] = 1] = "Verification";
  return ResetPasswordStep2;
})(ResetPasswordStep || {});
var ResetPasswordError = class extends Error {
  kind;
  constructor(kind, options) {
    super(`Password reset failed: ${kind}`, options);
    this.name = "ResetPasswordError";
    this.kind = kind;
  }
};

// src/utils/reset-password.ts
var BAD_REQUEST = 400;
async function sendPasswordResetCode(lookUp, send) {
  let available;
  try {
    ({ available } = await lookUp());
  } catch (error) {
    throw new ResetPasswordError("send-failed", { cause: error });
  }
  if (available) {
    throw new ResetPasswordError("no-account");
  }
  try {
    return await send();
  } catch (error) {
    throw new ResetPasswordError("send-failed", { cause: error });
  }
}
function resetPasswordFailure(error) {
  const status = error instanceof Object && "status" in error ? error.status : void 0;
  return new ResetPasswordError(status === BAD_REQUEST ? "invalid-code" : "failed", { cause: error });
}

// src/utils/return-url.ts
function getReturnUrl(search, fallback, origin = window.location.origin) {
  const returnUrl = new URLSearchParams(search).get("returnUrl");
  if (!returnUrl) {
    return fallback;
  }
  let url;
  try {
    url = new URL(returnUrl, origin);
  } catch {
    return fallback;
  }
  if (url.origin !== origin || url.pathname.startsWith("//")) {
    return fallback;
  }
  return url.pathname + url.search + url.hash;
}

// src/components/auth/WelcomeForm.tsx
import { Fragment as Fragment5, jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
var FIELD_LABEL = clsx2(lgType.callout, "mb-1.5 block font-medium text-(--color-text-secondary)");
var ERROR_TEXT = clsx2(lgType.callout, "mt-1 text-(--color-error)");
function WelcomeFormInner(props) {
  const {
    subtitle,
    googleClientId,
    googleLoading,
    onEmailSubmit,
    onGoogleSignIn,
    onCheckEmail
  } = props;
  const { t } = useTranslation7();
  const [loading, setLoading] = useState8(false);
  const schema = useMemo4(() => yup4.object().shape({
    countryCode: yup4.string().required(t("shared.auth.welcome.validation.country_code_required", "Country code is required.")),
    email: yup4.string().email(t("shared.auth.welcome.validation.email_invalid", "Please enter a valid email address.")).required(t("shared.auth.welcome.validation.email_required", "Email is required."))
  }), [t]);
  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors, isValid }
  } = useForm4({
    defaultValues: {
      countryCode: detectUserCountry(SUPPORTED_COUNTRIES.map((c) => c.code)),
      email: ""
    },
    mode: "onChange",
    resolver: yupResolver4(schema)
  });
  const ensureCountrySelected = useCallback4(() => {
    if (getValues("countryCode")) {
      return true;
    }
    void trigger("countryCode");
    toast2.error(t("shared.auth.welcome.toast.select_country_first", "Please select your country / region first."));
    return false;
  }, [getValues, trigger, t]);
  const handleGoogleSignIn = useCallback4(
    async (response) => {
      await onGoogleSignIn?.(response, getValues("countryCode"));
    },
    [onGoogleSignIn, getValues]
  );
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (onCheckEmail) {
        const result = await onCheckEmail(data.email);
        onEmailSubmit(result.username, result.available, data.countryCode);
      } else {
        onEmailSubmit(data.email, true, data.countryCode);
      }
    } catch (error) {
      console.error(error);
      toast2.error(error.message || t("shared.auth.welcome.toast.check_username_error", "An error occurred while checking the username."));
    } finally {
      setLoading(false);
    }
  };
  return /* @__PURE__ */ jsxs7(Fragment5, { children: [
    /* @__PURE__ */ jsx9(
      AuthFormHeading,
      {
        title: t("shared.auth.welcome.title", "Welcome"),
        subtitle: subtitle ?? t("shared.auth.welcome.subtitle", "Enter your email to get started.")
      }
    ),
    /* @__PURE__ */ jsxs7("form", { noValidate: true, onSubmit: handleSubmit(onSubmit), children: [
      /* @__PURE__ */ jsxs7("div", { className: "mb-4", children: [
        /* @__PURE__ */ jsx9("label", { className: FIELD_LABEL, htmlFor: "auth-country", children: t("shared.auth.welcome.field.country_region_label", "Country / Region") }),
        /* @__PURE__ */ jsx9(
          Controller4,
          {
            name: "countryCode",
            control,
            rules: { required: true },
            render: ({ field: { value, onChange } }) => /* @__PURE__ */ jsxs7(Select, { value: value || void 0, onValueChange: onChange, disabled: loading, children: [
              /* @__PURE__ */ jsx9(
                SelectTrigger,
                {
                  id: "auth-country",
                  size: "xl",
                  style: { width: "100%" },
                  "aria-invalid": errors.countryCode ? true : void 0,
                  "aria-describedby": errors.countryCode ? "auth-country-error" : void 0,
                  children: /* @__PURE__ */ jsx9(SelectValue, { placeholder: t("shared.auth.welcome.field.country_region_placeholder", "Select your country / region") })
                }
              ),
              /* @__PURE__ */ jsx9(SelectContent, { children: SUPPORTED_COUNTRIES.map((country) => /* @__PURE__ */ jsx9(SelectItem, { value: country.code, children: t(`shared.auth.welcome.country.${country.code.toLowerCase()}`, `${country.flag} ${country.label}`) }, country.code)) })
            ] })
          }
        ),
        errors.countryCode ? /* @__PURE__ */ jsx9("p", { className: ERROR_TEXT, id: "auth-country-error", children: errors.countryCode.message }) : null
      ] }),
      /* @__PURE__ */ jsx9(
        Controller4,
        {
          name: "email",
          control,
          rules: { required: true },
          render: ({ field: { value, onChange, onBlur } }) => /* @__PURE__ */ jsx9(
            TextField4,
            {
              className: "mb-4",
              fullWidth: true,
              reserveHelperSpace: true,
              size: "xl",
              type: "email",
              autoComplete: "username",
              startIcon: "mail",
              label: t("shared.auth.welcome.field.email_label", "Email"),
              spellCheck: false,
              disabled: loading,
              value,
              autoFocus: true,
              onBlur,
              onChange,
              error: Boolean(errors.email),
              helperText: errors.email?.message,
              placeholder: t("shared.auth.welcome.field.email_placeholder", "name@example.com")
            }
          )
        }
      ),
      /* @__PURE__ */ jsx9(
        Button6,
        {
          className: "mt-4",
          variant: "prominent",
          color: "primary",
          size: "xl",
          fullWidth: true,
          type: "submit",
          loading,
          disabled: !isValid,
          children: t("shared.auth.welcome.button.continue", "Continue")
        }
      ),
      googleClientId && onGoogleSignIn && /* @__PURE__ */ jsx9("div", { className: "mt-3", children: /* @__PURE__ */ jsx9(
        GoogleSignInButton,
        {
          callback: handleGoogleSignIn,
          beforeSignIn: ensureCountrySelected,
          clientId: googleClientId,
          loading: googleLoading
        }
      ) }),
      /* @__PURE__ */ jsxs7(Text3, { variant: "body-secondary", as: "p", className: "mt-4 text-center", children: [
        /* @__PURE__ */ jsx9("span", { children: t("shared.auth.welcome.terms.prefix", "By continuing, you agree to our ") }),
        /* @__PURE__ */ jsx9(Link, { external: true, href: BUYSCOUT_URLS.TERMS, children: t("shared.auth.welcome.terms.terms_link", "Terms and Conditions") }),
        /* @__PURE__ */ jsx9("span", { children: t("shared.auth.welcome.terms.conjunction", " and ") }),
        /* @__PURE__ */ jsx9(Link, { external: true, href: BUYSCOUT_URLS.PRIVACY, children: t("shared.auth.welcome.terms.privacy_link", "Privacy Policy") }),
        /* @__PURE__ */ jsx9("span", { children: "." })
      ] })
    ] })
  ] });
}
function WelcomeForm(props) {
  const { i18n } = useTranslation7();
  return /* @__PURE__ */ jsx9(WelcomeFormInner, { ...props }, i18n.language);
}

// src/components/auth/AuthView.tsx
import { jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
var BANNER = "mb-6";
function AuthView(props) {
  const { callbacks, templateName, google, context } = props;
  const { googleClientId, googleLoading } = google ?? {};
  const { onResendCode } = callbacks;
  const subtitles = context?.subtitles;
  const { t } = useTranslation8();
  const resendCountdown = useResendCountdown(Boolean(onResendCode));
  const [stage, setStage] = useState9(0 /* Welcome */);
  const [email, setEmail] = useState9("");
  const [countryCode, setCountryCode] = useState9("");
  const [signUpData, setSignUpData] = useState9({
    email: "",
    password: "",
    name: "",
    rememberMe: false,
    countryCode: ""
  });
  const handleGoBack = () => {
    switch (stage) {
      case 1 /* SignIn */:
      case 2 /* SignUp */:
        setStage(0 /* Welcome */);
        break;
      case 3 /* Verification */:
        setStage(2 /* SignUp */);
        break;
      default:
        console.warn("Cannot go back from current stage");
    }
  };
  const getBackButtonText = () => {
    switch (stage) {
      case 1 /* SignIn */:
      case 2 /* SignUp */:
        return t("shared.auth.back_button.welcome", "Welcome");
      case 3 /* Verification */:
        return t("shared.auth.back_button.sign_up", "Sign Up");
      default:
        return null;
    }
  };
  const handleEmailSubmit = (email2, isSignUp, countryCode2) => {
    setEmail(email2);
    setCountryCode(countryCode2);
    setStage(isSignUp ? 2 /* SignUp */ : 1 /* SignIn */);
    callbacks.onEmailSubmit(email2, isSignUp);
  };
  const handleSignUpSubmit = (name, password, rememberMe) => {
    setSignUpData({ password, name, rememberMe, email, countryCode });
    setStage(3 /* Verification */);
  };
  const handleCodeRequest = async (data, rememberMe) => {
    resendCountdown.restart(await callbacks.onSignUpSubmit(data, rememberMe));
  };
  const handleResendCode = onResendCode && (async () => {
    const result = await onResendCode(signUpData);
    resendCountdown.restart(result);
    return result;
  });
  const renderStageForm = () => {
    switch (stage) {
      case 0 /* Welcome */:
        return /* @__PURE__ */ jsx10(
          WelcomeForm,
          {
            subtitle: subtitles?.[0 /* Welcome */],
            googleClientId,
            googleLoading,
            onEmailSubmit: handleEmailSubmit,
            onGoogleSignIn: callbacks.onGoogleSignIn,
            onCheckEmail: callbacks.onCheckEmail
          }
        );
      case 1 /* SignIn */:
        return /* @__PURE__ */ jsx10(
          SignInForm,
          {
            subtitle: subtitles?.[1 /* SignIn */],
            email,
            onSubmit: callbacks.onSignInSubmit,
            onResetPassword: callbacks.onResetPassword
          }
        );
      case 2 /* SignUp */:
        return /* @__PURE__ */ jsx10(
          SignUpForm,
          {
            subtitle: subtitles?.[2 /* SignUp */],
            email,
            onSubmit: handleCodeRequest,
            onSignUpSubmit: handleSignUpSubmit
          }
        );
      case 3 /* Verification */:
        return /* @__PURE__ */ jsx10(
          VerificationCodeForm,
          {
            subtitle: subtitles?.[3 /* Verification */],
            email,
            signUpData,
            resend: handleResendCode && { secondsLeft: resendCountdown.secondsLeft, onResend: handleResendCode },
            onSubmit: callbacks.onVerificationSubmit
          }
        );
      default:
        return null;
    }
  };
  const backText = getBackButtonText();
  return /* @__PURE__ */ jsxs8(AuthScaffold, { templateName, back: backText ? { label: backText, onClick: handleGoBack } : void 0, children: [
    context?.banner ? /* @__PURE__ */ jsx10("div", { className: BANNER, children: context.banner }) : null,
    renderStageForm()
  ] });
}

// src/components/charts/BarChart.tsx
import { useTranslation as useTranslation10 } from "react-i18next";
import clsx5 from "clsx";

// src/components/charts/chartStyles.ts
import clsx3 from "clsx";
var DEFAULT_CHART_HEIGHT = 240;
var CHART_HEIGHT = "h-[var(--chart-height,240px)]";
var SERIES_STROKE = {
  primary: "var(--color-primary)",
  previous: "var(--bs-text-secondary)"
};
var visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0
};
var AXIS_TEXT = clsx3(lgType.subheadline, "whitespace-nowrap text-(--bs-text-secondary) tabular-nums");
var READOUT_TEXT = clsx3(
  lgType.callout,
  "min-h-(--bs-lg-type-callout-leading) min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-(--bs-text-secondary) tabular-nums"
);
var FOCUS_RING = "focus-visible:rounded-sm focus-visible:[outline:var(--bs-lg-focus-ring-width)_solid_var(--bs-lg-focus-ring-color)] focus-visible:[outline-offset:var(--bs-lg-focus-ring-offset)]";
var CHART_ROW_GAP = "mb-2";
var CHART_ROW_GAP_Y = "gap-y-2";
var AXIS_ROW_GAP = "mt-1.5";

// src/components/charts/ChartDataTable.tsx
import { jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
function ChartDataTable({ caption, labelHeader, labels, columns }) {
  return /* @__PURE__ */ jsx11("div", { style: visuallyHidden, children: /* @__PURE__ */ jsxs9("table", { children: [
    /* @__PURE__ */ jsx11("caption", { children: caption }),
    /* @__PURE__ */ jsx11("thead", { children: /* @__PURE__ */ jsxs9("tr", { children: [
      /* @__PURE__ */ jsx11("th", { scope: "col", children: labelHeader }),
      columns.map((column) => /* @__PURE__ */ jsx11("th", { scope: "col", children: column.header }, column.id))
    ] }) }),
    /* @__PURE__ */ jsx11("tbody", { children: labels.map((label, index) => (
      // eslint-disable-next-line react/no-array-index-key -- labels may repeat; the row's place is its identity.
      /* @__PURE__ */ jsxs9("tr", { children: [
        /* @__PURE__ */ jsx11("th", { scope: "row", children: label }),
        columns.map((column) => /* @__PURE__ */ jsx11("td", { children: column.cells[index] ?? "\u2014" }, column.id))
      ] }, index)
    )) })
  ] }) });
}

// src/components/charts/chartMath.ts
var isNumber = (value) => Number.isFinite(value);
function plotRuns(values, width, height, top, { slots = values.length, padTop = 0, padBottom = 0 }) {
  const count = Math.min(values.length, Math.max(0, slots));
  const step = slots > 1 ? width / (slots - 1) : 0;
  const usable = height - padTop - padBottom;
  const y = (value) => padTop + usable * (1 - (top > 0 ? Math.min(1, Math.max(0, value / top)) : 0));
  const runs = [];
  let run = null;
  for (let index = 0; index < count; index++) {
    const value = values[index];
    if (!isNumber(value)) {
      run = null;
      continue;
    }
    if (!run) {
      run = [];
      runs.push(run);
    }
    run.push([(index * step).toFixed(1), y(value).toFixed(1)]);
  }
  return runs;
}
function linePath(values, width, height, top, options = {}) {
  return plotRuns(values, width, height, top, options).map((run) => run.map(([x, y], index) => `${index === 0 ? "M" : "L"}${x},${y}`).join(" ")).join(" ");
}
function areaPath(values, width, height, top, options = {}) {
  const base = (height - (options.padBottom ?? 0)).toFixed(1);
  return plotRuns(values, width, height, top, options).filter((run) => run.length > 1).map((run) => `M${run[0][0]},${base} ${run.map(([x, y]) => `L${x},${y}`).join(" ")} L${run[run.length - 1][0]},${base} Z`).join(" ");
}
var round = (value) => Number(value.toPrecision(12));
function niceAxisTop(max, steps, { integer = false } = {}) {
  const count = Math.max(1, Math.floor(steps));
  if (!isNumber(max) || max <= 0) return count;
  const raw = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((factor) => round(factor * magnitude)).filter((candidate) => !integer || Number.isInteger(candidate)).find((candidate) => candidate >= raw) ?? (integer ? Math.max(1, Math.ceil(raw)) : round(10 * magnitude));
  return round(step * count);
}
function niceAxis(max, { integer = false } = {}) {
  return [3, 4].map((steps) => ({ top: niceAxisTop(max, steps, { integer }), steps })).reduce((best, next) => next.top < best.top ? next : best);
}
function axisTickValues(top, steps) {
  const count = Math.max(1, Math.floor(steps));
  return Array.from({ length: count + 1 }, (_, index) => round(top * index / count));
}
function axisTickIndexes(length, count) {
  if (length <= 0 || count <= 0) return [];
  if (length <= count) return Array.from({ length }, (_, index) => index);
  if (count === 1) return [length - 1];
  return Array.from({ length: count }, (_, index) => Math.round(index * (length - 1) / (count - 1)));
}
function seriesMax(...series) {
  let max = 0;
  for (const values of series) {
    for (const value of values) {
      if (isNumber(value) && value > max) max = value;
    }
  }
  return max;
}
function pointRatio(index, length) {
  return length > 1 ? index / (length - 1) : 0;
}
function barCenterRatio(index, length) {
  return length > 0 ? (index + 0.5) / length : 0;
}
function indexAtRatio(ratio, length) {
  if (length <= 0 || !isNumber(ratio)) return null;
  return Math.round(Math.min(1, Math.max(0, ratio)) * (length - 1));
}
function barIndexAtRatio(ratio, length) {
  if (length <= 0 || !isNumber(ratio)) return null;
  return Math.min(length - 1, Math.max(0, Math.floor(Math.min(1, Math.max(0, ratio)) * length)));
}
function scrubIndex(current, key, length) {
  if (length <= 0) return void 0;
  const last = length - 1;
  const from = current ?? last;
  const page = Math.max(1, Math.round(length / 10));
  const clamp = (index) => Math.min(last, Math.max(0, index));
  switch (key) {
    case "ArrowLeft":
    case "ArrowDown":
      return clamp(from - 1);
    case "ArrowRight":
    case "ArrowUp":
      return clamp(from + 1);
    case "PageDown":
      return clamp(from - page);
    case "PageUp":
      return clamp(from + page);
    case "Home":
      return 0;
    case "End":
      return last;
    default:
      return void 0;
  }
}
function runningTotals(values) {
  let sum = 0;
  return values.map((value) => sum += isNumber(value) ? value : 0);
}
function bestIndex(values) {
  let best = null;
  values.forEach((value, index) => {
    if (isNumber(value) && value > 0 && (best === null || value > values[best])) best = index;
  });
  return best;
}
function percentShares(values) {
  const counts = values.map((value) => isNumber(value) && value > 0 ? value : 0);
  const total = counts.reduce((sum, value) => sum + value, 0);
  if (total === 0) return counts.map(() => 0);
  const exact = counts.map((value) => value / total * 100);
  const shares = exact.map(Math.floor);
  let left = 100 - shares.reduce((sum, share) => sum + share, 0);
  const byRemainder = exact.map((value, index) => ({ index, remainder: value - shares[index] })).sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const { index } of byRemainder) {
    if (left <= 0) break;
    shares[index] += 1;
    left -= 1;
  }
  return shares;
}

// src/components/charts/ChartScaffold.tsx
import { useEffect as useEffect5, useRef as useRef3 } from "react";
import clsx4 from "clsx";
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
var SCAN_EASING = "cubic-bezier(.45,0,.55,1)";
var SCAN_KEYFRAMES = [
  { offset: 0, left: "-20%", opacity: 0, easing: SCAN_EASING },
  { offset: 0.15, opacity: 1, easing: SCAN_EASING },
  { offset: 0.85, opacity: 1, easing: SCAN_EASING },
  { offset: 1, left: "100%", opacity: 0 }
];
function Scanner() {
  const ref = useRef3(null);
  useEffect5(() => {
    const animation = ref.current?.animate?.(SCAN_KEYFRAMES, { duration: 1600, iterations: Infinity });
    return () => animation?.cancel();
  }, []);
  return /* @__PURE__ */ jsx12(
    "div",
    {
      ref,
      className: "absolute bottom-0 w-[17%] border-t-2 border-dashed border-(--bs-lg-ink-tertiary) motion-reduce:hidden",
      style: { left: "-20%", opacity: 0 }
    }
  );
}
function ChartScaffold({ pending, gridlines = 3, className }) {
  return /* @__PURE__ */ jsxs10("div", { "aria-hidden": true, className: clsx4("pointer-events-none absolute inset-0 overflow-hidden", className), "data-chart-scaffold": true, children: [
    Array.from({ length: gridlines }, (_, index) => /* @__PURE__ */ jsx12("div", { className: "absolute inset-x-0 border-t border-(--bs-lg-hairline)", style: { top: `${index / gridlines * 100}%` } }, index)),
    /* @__PURE__ */ jsx12("div", { className: "absolute inset-x-0 bottom-0 border-t border-(--bs-lg-fill-1)" }),
    pending ? /* @__PURE__ */ jsx12(Scanner, {}) : null
  ] });
}

// src/components/charts/useChartScrub.ts
import { useCallback as useCallback5, useEffect as useEffect6, useRef as useRef4, useState as useState10 } from "react";
function useChartScrub({ length, indexAt, value, onChange }) {
  const [inner, setInner] = useState10(null);
  const controlled = value !== void 0;
  const raw = controlled ? value : inner;
  const index = raw !== null && raw >= 0 && raw < length ? raw : null;
  const indexRef = useRef4(index);
  indexRef.current = index;
  const plotRef = useRef4(null);
  const select = useCallback5((next) => {
    if (next === indexRef.current) return;
    indexRef.current = next;
    if (!controlled) setInner(next);
    onChange?.(next);
  }, [controlled, onChange]);
  const active = index !== null;
  useEffect6(() => {
    if (!active) return;
    const clear = (event) => {
      if (!plotRef.current?.contains(event.target)) select(null);
    };
    document.addEventListener("pointerdown", clear);
    return () => document.removeEventListener("pointerdown", clear);
  }, [active, select]);
  const pick = (event) => {
    if (length === 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    select(indexAt((event.clientX - rect.left) / rect.width, length));
  };
  const plotProps = {
    ref: plotRef,
    onPointerDown: pick,
    onPointerMove: pick,
    onPointerLeave: (event) => {
      if (event.pointerType === "mouse") select(null);
    },
    onKeyDown: (event) => {
      const next = scrubIndex(indexRef.current, event.key, length);
      if (next === void 0) return;
      event.preventDefault();
      select(next);
    },
    // Only a keyboard focus starts on a point; a click focuses too, and its pointer already picked one.
    onFocus: (event) => {
      if (indexRef.current === null && length > 0 && event.currentTarget.matches(":focus-visible")) select(length - 1);
    },
    onBlur: () => select(null)
  };
  return { index, plotProps };
}

// src/components/charts/useChartText.ts
import { useMemo as useMemo5 } from "react";
import { useTranslation as useTranslation9 } from "react-i18next";
function listFormat(locale) {
  try {
    const format = new Intl.ListFormat(locale, { type: "unit", style: "short" });
    return (items) => format.format(items);
  } catch {
    return (items) => items.join(", ");
  }
}
function useChartText(locale) {
  const { t, i18n } = useTranslation9();
  const language = locale ?? i18n.language;
  return useMemo5(() => {
    const list = listFormat(language);
    const options = { interpolation: { escapeValue: false } };
    return {
      describePoint: (label, values) => t("shared.charts.point", "{{label}}: {{values}}", {
        ...options,
        label,
        values: list(values.map((item) => t("shared.charts.series_value", "{{series}} {{value}}", { ...options, ...item })))
      }),
      labelHeader: t("shared.charts.table.label", "Date"),
      locale: language
    };
  }, [language, t]);
}

// src/components/charts/BarChart.tsx
import { jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
var ROOT2 = clsx5("relative grid min-w-0 grid-rows-[auto_minmax(0,1fr)_auto]", CHART_HEIGHT);
var READOUT = clsx5(READOUT_TEXT, CHART_ROW_GAP);
var PLOT = clsx5("relative flex min-h-0 touch-pan-y items-end", FOCUS_RING);
var COLUMN = "relative flex h-full min-w-0 flex-[1_1_0] items-end justify-center";
var GHOST = "absolute inset-x-0 bottom-0 rounded-t-[3px] bg-(--bs-lg-fill-2)";
var BAR = "relative rounded-t-[3px] bg-(--color-primary) [transition:background-color_120ms_ease-out] data-dimmed:bg-(--color-gold-soft)";
var X_AXIS = clsx5(AXIS_TEXT, "relative h-(--bs-lg-type-subheadline-leading)", AXIS_ROW_GAP);
var X_TICK = "absolute top-0";
function xTickStyle(index, length) {
  if (index === 0) return { left: 0 };
  if (index === length - 1) return { right: 0 };
  return { left: `${barCenterRatio(index, length) * 100}%`, transform: "translateX(-50%)" };
}
var identity = (label) => label;
var finite = (value) => value !== void 0 && Number.isFinite(value) ? value : null;
function BarChart(props) {
  const {
    values,
    previous,
    labels,
    formatValue,
    formatLabel = identity,
    label,
    seriesLabel,
    previousLabel,
    summary,
    renderReadout,
    hoveredIndex,
    onHoveredIndexChange,
    xTicks = 4,
    locale,
    height,
    className
  } = props;
  const { t } = useTranslation10();
  const text = useChartText(locale);
  const length = labels.length > 0 ? labels.length : values.length;
  const max = Math.max(1, seriesMax(values, previous ?? []));
  const { index, plotProps } = useChartScrub({ length, indexAt: barIndexAtRatio, value: hoveredIndex, onChange: onHoveredIndexChange });
  const currentName = seriesLabel ?? t("shared.charts.current_period", "This period");
  const previousName = previousLabel ?? t("shared.charts.previous_period", "Last period");
  const describe = (at) => {
    const items = [{ series: currentName, value: finite(values[at]) }];
    if (previous) items.push({ series: previousName, value: finite(previous[at]) });
    return text.describePoint(
      formatLabel(labels[at] ?? ""),
      items.flatMap((item) => item.value === null ? [] : [{ series: item.series, value: formatValue(item.value) }])
    );
  };
  const focusIndex = index ?? length - 1;
  const share = (value) => `${Math.max(0, value ?? 0) / max * 100}%`;
  return /* @__PURE__ */ jsxs11("div", { className: clsx5(ROOT2, className), style: height === void 0 ? void 0 : { "--chart-height": typeof height === "number" ? `${height}px` : height }, children: [
    /* @__PURE__ */ jsx13("div", { className: READOUT, children: index === null ? summary : renderReadout?.(index) ?? describe(index) }),
    /* @__PURE__ */ jsxs11(
      "div",
      {
        className: PLOT,
        ...plotProps,
        style: { gap: length > 45 ? 1 : 3 },
        ...length > 0 && {
          "role": "slider",
          "tabIndex": 0,
          "aria-label": label,
          "aria-valuemin": 0,
          "aria-valuemax": length - 1,
          "aria-valuenow": focusIndex,
          "aria-valuetext": describe(focusIndex)
        },
        children: [
          /* @__PURE__ */ jsx13(ChartScaffold, {}),
          Array.from({ length }, (_, at) => {
            const value = finite(values[at]);
            return /* @__PURE__ */ jsxs11("div", { className: COLUMN, "aria-hidden": true, children: [
              previous ? /* @__PURE__ */ jsx13("div", { className: GHOST, style: { height: share(finite(previous[at])) } }) : null,
              /* @__PURE__ */ jsx13(
                "div",
                {
                  className: BAR,
                  "data-dimmed": index !== null && index !== at ? "" : void 0,
                  style: { width: previous ? "60%" : "100%", height: share(value), minHeight: (value ?? 0) > 0 ? 2 : 0 }
                }
              )
            ] }, `${labels[at] ?? ""}:${at}`);
          })
        ]
      }
    ),
    /* @__PURE__ */ jsx13("div", { className: X_AXIS, "aria-hidden": true, children: axisTickIndexes(length, xTicks).map((at) => /* @__PURE__ */ jsx13("span", { className: X_TICK, style: xTickStyle(at, length), children: formatLabel(labels[at] ?? "") }, at)) }),
    /* @__PURE__ */ jsx13(
      ChartDataTable,
      {
        caption: label,
        labelHeader: text.labelHeader,
        labels: Array.from({ length }, (_, at) => formatLabel(labels[at] ?? "")),
        columns: [
          { id: "current", header: currentName, cells: Array.from({ length }, (_, at) => {
            const value = finite(values[at]);
            return value === null ? null : formatValue(value);
          }) },
          ...previous ? [{ id: "previous", header: previousName, cells: Array.from({ length }, (_, at) => {
            const value = finite(previous[at]);
            return value === null ? null : formatValue(value);
          }) }] : []
        ]
      }
    )
  ] });
}

// src/components/charts/ChartCard.tsx
import { useTranslation as useTranslation11 } from "react-i18next";
import { Button as Button7, LoadingActivityMute } from "@buyscout/components";
import clsx8 from "clsx";

// src/components/patterns/DashboardPageState.tsx
import { forwardRef, useId, useImperativeHandle, useRef as useRef5 } from "react";
import { Callout, CalloutActions, CalloutBody, CalloutContent, CalloutIcon, CalloutTitle, EmptyState, EmptyStateActions, EmptyStateDescription, EmptyStateTitle } from "@buyscout/components";
import clsx6 from "clsx";
import { Fragment as Fragment6, jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
var DashboardPageState = forwardRef(
  function DashboardPageState2({ title, description, action, tone = "neutral", plain = false, ...props }, ref) {
    const titleId = useId();
    const descriptionId = useId();
    const rootRef = useRef5(null);
    useImperativeHandle(ref, () => rootRef.current);
    const labelling = { "aria-labelledby": titleId, "aria-describedby": description ? descriptionId : void 0 };
    if (tone === "error" && !plain) {
      return /* @__PURE__ */ jsxs12(Callout, { color: "error", role: "alert", ...labelling, ...props, ref: rootRef, "data-tone": "error", children: [
        /* @__PURE__ */ jsx14(CalloutIcon, { children: /* @__PURE__ */ jsx14(Icon, { icon: "mdi:alert-circle-outline" }) }),
        /* @__PURE__ */ jsxs12(CalloutContent, { children: [
          /* @__PURE__ */ jsx14(CalloutTitle, { id: titleId, "data-dashboard-state-title": true, children: title }),
          description ? /* @__PURE__ */ jsx14(CalloutBody, { id: descriptionId, children: description }) : null,
          action ? /* @__PURE__ */ jsx14(CalloutActions, { children: action }) : null
        ] })
      ] });
    }
    const tight = tone === "error";
    const body = /* @__PURE__ */ jsxs12(Fragment6, { children: [
      /* @__PURE__ */ jsx14(EmptyStateTitle, { id: titleId, "data-dashboard-state-title": true, children: title }),
      description ? /* @__PURE__ */ jsx14(EmptyStateDescription, { id: descriptionId, className: tight ? lgTypeOverride.headline : void 0, children: description }) : null,
      action ? /* @__PURE__ */ jsx14(EmptyStateActions, { className: tight ? "!mt-2" : void 0, children: action }) : null
    ] });
    if (tight) {
      const { className, ...rest } = props;
      return /* @__PURE__ */ jsx14(EmptyState, { variant: "plain", role: "alert", ...labelling, ...rest, className: clsx6("!gap-1 !px-0 !py-2", className), ref: rootRef, "data-tone": "error", children: body });
    }
    return /* @__PURE__ */ jsx14(EmptyState, { variant: "plain", role: "status", ...labelling, ...props, ref: rootRef, "data-tone": "neutral", children: body });
  }
);
DashboardPageState.displayName = "DashboardPageState";

// src/components/patterns/DashboardPanel.tsx
import { forwardRef as forwardRef2 } from "react";
import { Text as Text4 } from "@buyscout/components";
import clsx7 from "clsx";

// src/hooks/useArrival.ts
import { useCallback as useCallback6, useState as useState11 } from "react";
var ARRIVAL_FADE = "motion-safe:animate-[fadeIn_150ms_ease-out]";
function useArrival(pending) {
  const [wasPending, setWasPending] = useState11(pending);
  const [arrived, setArrived] = useState11(false);
  if (pending !== wasPending) {
    setWasPending(pending);
    setArrived(!pending);
  }
  const onAnimationEnd = useCallback6((event) => {
    if (event.animationName === "fadeIn") setArrived(false);
  }, []);
  return { arrived, fade: arrived ? ARRIVAL_FADE : void 0, onAnimationEnd };
}

// src/components/patterns/DashboardPanel.tsx
import { jsx as jsx15, jsxs as jsxs13 } from "react/jsx-runtime";
var ROOT3 = clsx7(
  "min-w-0 rounded-2xl bg-(--bs-lg-surface) p-5 [box-shadow:var(--bs-lg-card-shadow)] contrast-more:[box-shadow:inset_0_0_0_1px_var(--bs-lg-ink-secondary)]",
  "[&>:last-child]:mb-0 [&>[data-dashboard-panel-heading]:has(+:not([data-dashboard-save-footer]))]:pb-4",
  "motion-safe:[&[data-arrived]>:not([data-dashboard-panel-heading]):not([data-dashboard-save-footer])]:animate-[fadeIn_150ms_ease-out]"
);
var DashboardPanel = forwardRef2(
  function DashboardPanel2({ title, titleProps, subheader, action, children, footer, loading = false, className, onAnimationEnd, ...props }, ref) {
    const arrival = useArrival(loading);
    return /* @__PURE__ */ jsxs13(
      "section",
      {
        ...props,
        ref,
        className: clsx7(ROOT3, className),
        "aria-busy": loading || props["aria-busy"],
        "data-dashboard-panel": true,
        "data-arrived": arrival.arrived || void 0,
        onAnimationEnd: (event) => {
          arrival.onAnimationEnd(event);
          onAnimationEnd?.(event);
        },
        children: [
          Boolean(title || subheader || action) && /* @__PURE__ */ jsxs13("div", { className: "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1", "data-dashboard-panel-heading": true, children: [
            title ? /* @__PURE__ */ jsx15("div", { className: "col-1 row-1 flex min-h-8 min-w-0 items-center", children: /* @__PURE__ */ jsx15(Text4, { as: "h2", variant: "heading-3", ...titleProps, className: clsx7(lgTypeOverride["card-title"], titleProps?.className), children: title }) }) : null,
            action ? /* @__PURE__ */ jsx15("div", { className: "col-2 row-1 max-w-full text-(length:--bs-lg-type-body-size) leading-(--bs-lg-type-body-leading)", children: action }) : null,
            subheader ? /* @__PURE__ */ jsx15("div", { className: "col-span-full leading-[1.4] text-pretty", children: /* @__PURE__ */ jsx15(Text4, { variant: "caption", as: "p", children: subheader }) }) : null
          ] }),
          children,
          footer
        ]
      }
    );
  }
);
DashboardPanel.displayName = "DashboardPanel";

// src/components/charts/ChartCard.tsx
import { jsx as jsx16, jsxs as jsxs14 } from "react/jsx-runtime";
var SLOT = "col-1 min-w-0 data-held:invisible";
var EMPTY_MESSAGE = clsx8(lgType.body, "absolute inset-0 m-0 grid place-items-center p-4 text-center text-(--bs-text-secondary)");
function PlaceholderFrame({ pending, children }) {
  return /* @__PURE__ */ jsxs14("div", { className: clsx8("grid grid-rows-[auto_minmax(0,1fr)_auto]", CHART_HEIGHT), children: [
    /* @__PURE__ */ jsx16("div", { className: clsx8("h-(--bs-lg-type-callout-leading)", CHART_ROW_GAP) }),
    /* @__PURE__ */ jsxs14("div", { className: "relative min-h-0", children: [
      /* @__PURE__ */ jsx16(ChartScaffold, { pending }),
      children
    ] }),
    /* @__PURE__ */ jsx16("div", { className: clsx8("h-(--bs-lg-type-subheadline-leading)", AXIS_ROW_GAP) })
  ] });
}
function ChartCard(props) {
  const {
    ref,
    title,
    subheader,
    info,
    controls,
    headline,
    legend,
    state,
    emptyMessage,
    errorTitle,
    errorMessage: errorMessage2,
    onRetry,
    children,
    height = DEFAULT_CHART_HEIGHT,
    style,
    ...rest
  } = props;
  const { t } = useTranslation11();
  const failed = state === "error";
  const chartHeight = typeof height === "number" ? `${height}px` : height;
  const compact = useMediaQuery(BELOW_SM);
  const compactControls = Boolean(controls) && compact;
  const held = failed ? "" : void 0;
  let next = 1;
  const controlsRow = compactControls ? next++ : 0;
  const firstHeldRow = next;
  const headlineRow = headline ? next++ : 0;
  const legendRow = legend ? next++ : 0;
  const chartRow = next;
  let chart;
  if (state === "ready") chart = children;
  else if (state === "empty") chart = /* @__PURE__ */ jsx16(PlaceholderFrame, { children: /* @__PURE__ */ jsx16("p", { className: EMPTY_MESSAGE, children: emptyMessage ?? t("shared.charts.empty", "No data") }) });
  else chart = /* @__PURE__ */ jsx16(PlaceholderFrame, { pending: state === "loading" });
  return /* @__PURE__ */ jsx16(
    DashboardPanel,
    {
      ...rest,
      ref,
      style: { ...style, "--chart-height": chartHeight },
      title: info ? /* @__PURE__ */ jsxs14("span", { className: "inline-flex items-center gap-1", children: [
        title,
        info
      ] }) : title,
      subheader,
      action: compactControls ? void 0 : controls,
      "aria-busy": state === "loading" || void 0,
      "data-chart-state": state,
      children: /* @__PURE__ */ jsxs14("div", { className: clsx8("grid min-w-0 grid-cols-[minmax(0,1fr)]", CHART_ROW_GAP_Y), children: [
        compactControls ? /* @__PURE__ */ jsx16("div", { className: "flex min-w-0 flex-wrap items-center gap-2", style: { gridRow: controlsRow }, children: controls }) : null,
        headline ? /* @__PURE__ */ jsx16("div", { className: SLOT, "data-held": held, "aria-hidden": failed || void 0, style: { gridRow: headlineRow }, children: /* @__PURE__ */ jsx16(LoadingActivityMute, { muted: failed, children: headline }) }) : null,
        legend ? /* @__PURE__ */ jsx16("div", { className: SLOT, "data-held": held, "aria-hidden": failed || void 0, style: { gridRow: legendRow }, children: /* @__PURE__ */ jsx16(LoadingActivityMute, { muted: failed, children: legend }) }) : null,
        /* @__PURE__ */ jsx16("div", { className: SLOT, "data-held": held, "aria-hidden": failed || void 0, style: { gridRow: chartRow }, children: /* @__PURE__ */ jsx16(LoadingActivityMute, { muted: failed, children: chart }) }),
        failed ? (
          // Spans the headline, legend and chart rows, which stay laid out but unseen beneath it to hold the height.
          /* @__PURE__ */ jsx16("div", { className: "col-1 grid min-w-0 content-center", style: { gridRow: `${firstHeldRow} / ${chartRow + 1}` }, children: /* @__PURE__ */ jsx16(
            DashboardPageState,
            {
              tone: "error",
              plain: true,
              title: errorTitle ?? t("shared.charts.error_title", "Couldn\u2019t load this chart"),
              description: errorMessage2 ?? t("shared.data_state.request_failed", "The request failed. Try again in a moment."),
              action: onRetry ? /* @__PURE__ */ jsx16(Button7, { variant: "bordered", size: "sm", onClick: onRetry, children: t("shared.data_state.retry", "Retry") }) : void 0
            }
          ) })
        ) : null
      ] })
    }
  );
}

// src/components/charts/ChartLegend.tsx
import clsx9 from "clsx";
import { jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
var SWATCH = {
  primary: "border-t-[3px] border-solid border-(--color-primary)",
  previous: "border-t-2 border-dashed border-(--bs-text-secondary)"
};
function ChartLegend({ items, children, className }) {
  return /* @__PURE__ */ jsxs15("div", { className: clsx9(lgType.callout, "flex flex-wrap justify-between gap-x-3 gap-y-1 text-(--bs-text-secondary) tabular-nums", className), children: [
    /* @__PURE__ */ jsx17("div", { className: "flex flex-wrap gap-x-3.5 gap-y-1", children: items.map((item) => /* @__PURE__ */ jsxs15("span", { children: [
      /* @__PURE__ */ jsx17("span", { className: clsx9("me-1.5 inline-block w-4 align-middle", SWATCH[item.tone]), "aria-hidden": true, "data-tone": item.tone }),
      item.label
    ] }, item.id)) }),
    children ? /* @__PURE__ */ jsx17("span", { children }) : null
  ] });
}

// src/components/charts/LineChart.tsx
import { useId as useId2 } from "react";
import clsx10 from "clsx";
import { Fragment as Fragment7, jsx as jsx18, jsxs as jsxs16 } from "react/jsx-runtime";
var VIEW_WIDTH = 600;
var VIEW_HEIGHT = 180;
var ROOT4 = clsx10("relative grid min-w-0 grid-cols-[auto_minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] gap-x-2", CHART_HEIGHT);
var READOUT2 = clsx10(READOUT_TEXT, "col-span-full", CHART_ROW_GAP);
var Y_TICK = clsx10(AXIS_TEXT, "absolute end-0 -translate-y-1/2");
var PLOT2 = clsx10("relative min-h-0 touch-pan-y", FOCUS_RING);
var SVG = "absolute inset-0 block size-full overflow-visible";
var RULE = "pointer-events-none absolute top-0 bottom-0 border-s border-(--bs-lg-separator)";
var DOT = "pointer-events-none absolute size-[9px] -translate-1/2 rounded-full bg-(--color-primary) [box-shadow:0_0_0_2px_var(--bs-lg-surface)]";
var X_AXIS2 = clsx10(AXIS_TEXT, "relative col-2 h-(--bs-lg-type-subheadline-leading)", AXIS_ROW_GAP);
var X_TICK2 = "absolute top-0";
function xTickStyle2(index, length) {
  if (index === 0) return { left: 0 };
  if (index === length - 1) return { right: 0 };
  return { left: `${pointRatio(index, length) * 100}%`, transform: "translateX(-50%)" };
}
var identity2 = (label) => label;
function LineChart(props) {
  const {
    series,
    labels,
    formatValue,
    formatPointValue = formatValue,
    formatLabel = identity2,
    label,
    summary,
    renderReadout,
    hoveredIndex,
    onHoveredIndexChange,
    gridlines: fixedGridlines,
    xTicks = 4,
    integerAxis,
    area,
    locale,
    height,
    className
  } = props;
  const text = useChartText(locale);
  const slots = labels.length > 0 ? labels.length : Math.max(0, ...series.map((item) => item.values.length));
  const max = seriesMax(...series.map((item) => item.values));
  const { top, steps: gridlines } = fixedGridlines === void 0 ? niceAxis(max, { integer: integerAxis }) : { top: niceAxisTop(max, fixedGridlines, { integer: integerAxis }), steps: fixedGridlines };
  const fadeId = `chart-fade${useId2().replace(/\W/g, "")}`;
  const { index, plotProps } = useChartScrub({ length: slots, indexAt: indexAtRatio, value: hoveredIndex, onChange: onHoveredIndexChange });
  const valueAt = (item, at) => {
    const value = item.values[at];
    return value === void 0 || !Number.isFinite(value) ? null : value;
  };
  const ordered = [...series].sort((a, b) => Number(a.tone === "primary") - Number(b.tone === "primary"));
  const describe = (at) => text.describePoint(
    formatLabel(labels[at] ?? ""),
    [...ordered].reverse().flatMap((item) => {
      const value = valueAt(item, at);
      return value === null ? [] : [{ series: item.label, value: formatPointValue(value) }];
    })
  );
  const focusIndex = index ?? slots - 1;
  return /* @__PURE__ */ jsxs16("div", { className: clsx10(ROOT4, className), style: height === void 0 ? void 0 : { "--chart-height": typeof height === "number" ? `${height}px` : height }, children: [
    /* @__PURE__ */ jsx18("div", { className: READOUT2, children: index === null ? summary : renderReadout?.(index) ?? describe(index) }),
    /* @__PURE__ */ jsx18("div", { className: "relative min-w-7", "aria-hidden": true, children: axisTickValues(top, gridlines).map((value, step) => /* @__PURE__ */ jsx18("span", { className: Y_TICK, style: { top: `${(1 - step / gridlines) * 100}%` }, children: formatValue(value) }, value)) }),
    /* @__PURE__ */ jsxs16(
      "div",
      {
        className: PLOT2,
        ...plotProps,
        ...slots > 0 && {
          "role": "slider",
          "tabIndex": 0,
          "aria-label": label,
          "aria-valuemin": 0,
          "aria-valuemax": slots - 1,
          "aria-valuenow": focusIndex,
          "aria-valuetext": describe(focusIndex)
        },
        children: [
          /* @__PURE__ */ jsx18(ChartScaffold, { gridlines }),
          /* @__PURE__ */ jsxs16("svg", { className: SVG, viewBox: `0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`, preserveAspectRatio: "none", "aria-hidden": true, children: [
            area ? /* @__PURE__ */ jsxs16(Fragment7, { children: [
              /* @__PURE__ */ jsx18("defs", { children: /* @__PURE__ */ jsxs16("linearGradient", { id: fadeId, x1: "0", y1: "0", x2: "0", y2: "1", children: [
                /* @__PURE__ */ jsx18("stop", { offset: "0", stopColor: SERIES_STROKE.primary, stopOpacity: 0.28 }),
                /* @__PURE__ */ jsx18("stop", { offset: "1", stopColor: SERIES_STROKE.primary, stopOpacity: 0 })
              ] }) }),
              series.filter((item) => item.tone === "primary").map((item) => /* @__PURE__ */ jsx18("path", { d: areaPath(item.values, VIEW_WIDTH, VIEW_HEIGHT, top, { slots }), fill: `url(#${fadeId})` }, item.id))
            ] }) : null,
            ordered.map((item) => /* @__PURE__ */ jsx18(
              "path",
              {
                d: linePath(item.values, VIEW_WIDTH, VIEW_HEIGHT, top, { slots }),
                fill: "none",
                stroke: SERIES_STROKE[item.tone],
                ...item.tone === "primary" ? { strokeWidth: 2.5 } : { strokeWidth: 1.5, strokeOpacity: 0.5, strokeDasharray: "5 5" },
                strokeLinejoin: "round",
                vectorEffect: "non-scaling-stroke"
              },
              item.id
            ))
          ] }),
          index === null ? null : /* @__PURE__ */ jsxs16(Fragment7, { children: [
            /* @__PURE__ */ jsx18("div", { className: RULE, "aria-hidden": true, style: { left: `${pointRatio(index, slots) * 100}%` } }),
            series.filter((item) => item.tone === "primary").map((item) => {
              const value = valueAt(item, index);
              return value === null ? null : /* @__PURE__ */ jsx18("div", { className: DOT, "aria-hidden": true, style: { left: `${pointRatio(index, slots) * 100}%`, top: `${(1 - Math.min(1, Math.max(0, value / top))) * 100}%` } }, item.id);
            })
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx18("div", { className: X_AXIS2, "aria-hidden": true, children: axisTickIndexes(slots, xTicks).map((at) => /* @__PURE__ */ jsx18("span", { className: X_TICK2, style: xTickStyle2(at, slots), children: formatLabel(labels[at] ?? "") }, at)) }),
    /* @__PURE__ */ jsx18(
      ChartDataTable,
      {
        caption: label,
        labelHeader: text.labelHeader,
        labels: Array.from({ length: slots }, (_, at) => formatLabel(labels[at] ?? "")),
        columns: series.map((item) => ({
          id: item.id,
          header: item.label,
          cells: Array.from({ length: slots }, (_, at) => {
            const value = valueAt(item, at);
            return value === null ? null : formatPointValue(value);
          })
        }))
      }
    )
  ] });
}

// src/components/charts/Sparkline.tsx
import { useTranslation as useTranslation12 } from "react-i18next";
import clsx11 from "clsx";
import { Fragment as Fragment8, jsx as jsx19, jsxs as jsxs17 } from "react/jsx-runtime";
var VIEW_WIDTH2 = 120;
var PAD = { padTop: 2, padBottom: 2 };
var finite2 = (value) => value !== void 0 && Number.isFinite(value) ? value : null;
function Sparkline({ values, previous, label, labels, formatLabel = (raw) => raw, formatValue, height = 32, className }) {
  const { t } = useTranslation12();
  const text = useChartText();
  const top = Math.max(1, seriesMax(values, previous ?? []));
  const slots = labels.length > 0 ? labels.length : Math.max(values.length, previous?.length ?? 0);
  const cells = (series) => Array.from({ length: slots }, (_, at) => {
    const value = finite2(series[at]);
    return value === null ? null : formatValue(value);
  });
  return /* @__PURE__ */ jsxs17("div", { className: clsx11("relative min-w-0", className), style: { height }, children: [
    /* @__PURE__ */ jsx19("div", { className: "absolute inset-x-0 bottom-0 border-t border-(--bs-lg-fill-1)", "aria-hidden": true }),
    values.length === 0 ? null : /* @__PURE__ */ jsxs17(Fragment8, { children: [
      /* @__PURE__ */ jsxs17("svg", { className: "relative block w-full", viewBox: `0 0 ${VIEW_WIDTH2} ${height}`, height, preserveAspectRatio: "none", "aria-hidden": true, children: [
        previous?.length ? /* @__PURE__ */ jsx19("path", { d: linePath(previous, VIEW_WIDTH2, height, top, { slots, ...PAD }), fill: "none", stroke: SERIES_STROKE.previous, strokeOpacity: 0.45, strokeWidth: 1.5, strokeDasharray: "3 3", vectorEffect: "non-scaling-stroke" }) : null,
        /* @__PURE__ */ jsx19("path", { d: linePath(values, VIEW_WIDTH2, height, top, { slots, ...PAD }), fill: "none", stroke: SERIES_STROKE.primary, strokeWidth: 2, strokeLinejoin: "round", vectorEffect: "non-scaling-stroke" })
      ] }),
      /* @__PURE__ */ jsx19(
        ChartDataTable,
        {
          caption: label,
          labelHeader: text.labelHeader,
          labels: Array.from({ length: slots }, (_, at) => formatLabel(labels[at] ?? "")),
          columns: [
            { id: "current", header: t("shared.charts.current_period", "This period"), cells: cells(values) },
            ...previous?.length ? [{ id: "previous", header: t("shared.charts.previous_period", "Last period"), cells: cells(previous) }] : []
          ]
        }
      )
    ] })
  ] });
}

// src/components/charts/timeRange.ts
var DEFAULT_TIME_RANGE_PRESETS = [7, 30, 90, "custom"];
var DEFAULT_MAX_CUSTOM_DAYS = 366;
var DAY_MS = 24 * 60 * 60 * 1e3;
var pad = (value) => String(value).padStart(2, "0");
function toDateKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
var wallClockFormats = /* @__PURE__ */ new Map();
function wallClockAt(instant, timeZone) {
  let format = wallClockFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    wallClockFormats.set(timeZone, format);
  }
  const fields = {};
  for (const part of format.formatToParts(instant)) {
    if (part.type !== "literal") fields[part.type] = Number(part.value);
  }
  return Date.UTC(fields.year ?? 1970, (fields.month ?? 1) - 1, fields.day ?? 1, fields.hour ?? 0, fields.minute ?? 0, fields.second ?? 0);
}
function offsetMsAt(instant, timeZone) {
  const wholeSecond = Math.floor(instant / 1e3) * 1e3;
  return wallClockAt(wholeSecond, timeZone) - wholeSecond;
}
function dateKeyInZone(date, timeZone) {
  return new Date(wallClockAt(date.getTime(), timeZone)).toISOString().slice(0, 10);
}
function zonedMidnight(key, timeZone) {
  const day = parseDateKey(key);
  if (!day) return null;
  const wallClock = Date.UTC(day.getFullYear(), day.getMonth(), day.getDate());
  const before = offsetMsAt(wallClock - DAY_MS, timeZone);
  const after = offsetMsAt(wallClock + DAY_MS, timeZone);
  const matching = [wallClock - before, wallClock - after].filter((instant) => instant + offsetMsAt(instant, timeZone) === wallClock).sort((a, b) => a - b);
  return new Date(matching[0] ?? wallClock - before);
}
function calendarToday(now, timeZone) {
  return timeZone && parseDateKey(dateKeyInZone(now, timeZone)) || addDays(now, 0);
}
function todayKey(now, timeZone) {
  return toDateKey(calendarToday(now, timeZone));
}
function parseDateKey(key) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  const date = new Date(year, month, day);
  return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day ? date : null;
}
function addDays(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}
var daysBetween = (start, end) => Math.round((end.getTime() - start.getTime()) / DAY_MS);
function presetKey(days) {
  return `${days}d`;
}
function timeRangeBounds(range, now = /* @__PURE__ */ new Date(), timeZone) {
  const today = calendarToday(now, timeZone);
  const midnight = (day) => timeZone && zonedMidnight(toDateKey(day), timeZone) || day;
  if (range.kind === "preset") {
    const days = Math.max(1, Math.floor(range.days));
    const first2 = addDays(today, -(days - 1));
    return { start: midnight(first2), end: now, from: toDateKey(first2), to: toDateKey(today), days };
  }
  const first = parseDateKey(range.from) ?? today;
  const last = parseDateKey(range.to) ?? first;
  const after = addDays(last, 1);
  return { start: midnight(first), end: midnight(after), from: toDateKey(first), to: toDateKey(last), days: Math.max(1, daysBetween(first, after)) };
}
function checkCustomRange(from, to, { maxDays = DEFAULT_MAX_CUSTOM_DAYS, now, timeZone } = {}) {
  const start = parseDateKey(from);
  const last = parseDateKey(to);
  if (!start || !last) return "invalid";
  if (last < start) return "order";
  if (now && toDateKey(last) > todayKey(now, timeZone)) return "future";
  if (daysBetween(start, addDays(last, 1)) > maxDays) return "too_long";
  return null;
}
function customTimeRange(from, to, options) {
  return checkCustomRange(from, to, options) ? null : { kind: "custom", from, to };
}
function clampCustomRange(from, to, { maxDays = DEFAULT_MAX_CUSTOM_DAYS, now = /* @__PURE__ */ new Date(), timeZone } = {}) {
  const today = calendarToday(now, timeZone);
  let last = parseDateKey(to) ?? today;
  if (last > today) last = today;
  let start = parseDateKey(from) ?? last;
  if (start > last) start = last;
  const earliest = addDays(last, -(Math.max(1, maxDays) - 1));
  if (start < earliest) start = earliest;
  return { from: toDateKey(start), to: toDateKey(last) };
}
function isSameTimeRange(a, b) {
  if (a.kind === "preset") return b.kind === "preset" && a.days === b.days;
  return b.kind === "custom" && a.from === b.from && a.to === b.to;
}
function formatTimeRangeLabel(from, to, locale, now = /* @__PURE__ */ new Date()) {
  const start = parseDateKey(from);
  const last = parseDateKey(to);
  if (!start || !last) return `${from} \u2013 ${to}`;
  const showYear = start.getFullYear() !== last.getFullYear() || last.getFullYear() !== now.getFullYear();
  const format = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", ...showYear && { year: "numeric" } });
  if (from === to) return format.format(start);
  return typeof format.formatRange === "function" ? format.formatRange(start, last) : `${format.format(start)} \u2013 ${format.format(last)}`;
}
function timeRangeSearchParams(range) {
  return range.kind === "custom" ? { from: range.from, to: range.to } : { range: presetKey(range.days) };
}
function parseTimeRangeSearchParams(params, { presets = DEFAULT_TIME_RANGE_PRESETS, fallback = { kind: "preset", days: 30 }, ...options } = {}) {
  const preset = /^(\d+)d$/.exec(params.get("range") ?? "");
  if (preset && presets.includes(Number(preset[1]))) return { kind: "preset", days: Number(preset[1]) };
  const from = params.get("from");
  const to = params.get("to");
  if (from && to && presets.includes("custom")) return customTimeRange(from, to, options) ?? fallback;
  return fallback;
}

// src/components/charts/TimeRangePicker.tsx
import { useRef as useRef6, useState as useState12 } from "react";
import { useTranslation as useTranslation13 } from "react-i18next";
import {
  Button as Button8,
  Popover,
  PopoverAnchor,
  PopoverContent,
  SegmentedControl,
  SegmentedControlItem,
  Select as Select2,
  SelectContent as SelectContent2,
  SelectItem as SelectItem2,
  SelectTrigger as SelectTrigger2,
  SelectValue as SelectValue2,
  TextField as TextField5
} from "@buyscout/components";
import clsx12 from "clsx";
import { jsx as jsx20, jsxs as jsxs18 } from "react/jsx-runtime";
function fieldAtFault(issue, from) {
  if (issue === "too_long") return "from";
  if (issue === "invalid") return parseDateKey(from) ? "to" : "from";
  return "to";
}
var CUSTOM = "custom";
var ARROW_KEYS = /* @__PURE__ */ new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]);
function TimeRangePicker(props) {
  const {
    value,
    onChange,
    presets = DEFAULT_TIME_RANGE_PRESETS,
    now: nowProp,
    timeZone,
    maxCustomDays = DEFAULT_MAX_CUSTOM_DAYS,
    locale: localeProp,
    "aria-label": ariaLabel,
    className
  } = props;
  const { t, i18n } = useTranslation13();
  const locale = localeProp ?? i18n.language;
  const [mountedAt] = useState12(() => /* @__PURE__ */ new Date());
  const now = nowProp ?? mountedAt;
  const today = todayKey(now, timeZone);
  const allowCustom = presets.includes(CUSTOM);
  const [open, setOpen] = useState12(false);
  const [from, setFrom] = useState12("");
  const [to, setTo] = useState12("");
  const issue = checkCustomRange(from, to, { maxDays: maxCustomDays, now, timeZone });
  const openerRef = useRef6(null);
  const interactedOutsideRef = useRef6(false);
  const openCustom = (opener) => {
    openerRef.current = opener;
    interactedOutsideRef.current = false;
    const bounds = timeRangeBounds(value, now, timeZone);
    const draft = clampCustomRange(bounds.from, bounds.to, { maxDays: maxCustomDays, now, timeZone });
    setFrom(draft.from);
    setTo(draft.to);
    setOpen(true);
  };
  const apply = (event) => {
    event.preventDefault();
    if (issue) return;
    onChange({ kind: "custom", from, to });
    setOpen(false);
  };
  const problems = {
    invalid: t("shared.charts.range.invalid", "Enter a start and an end date."),
    order: t("shared.charts.range.order", "Pick an end date on or after the start date."),
    future: t("shared.charts.range.future", "Pick dates up to today."),
    too_long: t("shared.charts.range.too_long", "Pick a range of up to {{count}} days.", { count: maxCustomDays })
  };
  const problem = issue ? problems[issue] : void 0;
  const faultyField = issue ? fieldAtFault(issue, from) : null;
  const selected = value.kind === "custom" ? CUSTOM : presetKey(value.days);
  const compact = useMediaQuery(BELOW_SM);
  const label = ariaLabel ?? t("shared.charts.range.label", "Period");
  const presetLabel = (preset) => preset === CUSTOM ? t("shared.charts.range.custom", "Custom") : t("shared.charts.range.days", "{{count}} days", { count: preset });
  const optionValue = (preset) => preset === CUSTOM ? CUSTOM : presetKey(preset);
  const choosePreset = (next) => onChange({ kind: "preset", days: Number.parseInt(next, 10) });
  const selectTriggerRef = useRef6(null);
  const openCustomAfterCloseRef = useRef6(false);
  const selectOption = (next) => {
    if (next !== CUSTOM) choosePreset(next);
    else openCustomAfterCloseRef.current = true;
  };
  const arrowedRef = useRef6(false);
  return /* @__PURE__ */ jsxs18(Popover, { open, onOpenChange: (next) => next ? openCustom(openerRef.current) : setOpen(false), children: [
    /* @__PURE__ */ jsx20(PopoverAnchor, { asChild: true, children: /* @__PURE__ */ jsxs18("div", { className: clsx12("flex flex-wrap items-center gap-2", className), children: [
      compact ? /* @__PURE__ */ jsxs18(Select2, { value: selected, onValueChange: selectOption, children: [
        /* @__PURE__ */ jsx20(SelectTrigger2, { ref: selectTriggerRef, size: "sm", "aria-label": label, children: /* @__PURE__ */ jsx20(SelectValue2, {}) }),
        /* @__PURE__ */ jsx20(
          SelectContent2,
          {
            onCloseAutoFocus: (event) => {
              if (!openCustomAfterCloseRef.current) return;
              openCustomAfterCloseRef.current = false;
              event.preventDefault();
              openCustom(selectTriggerRef.current);
            },
            children: presets.map((preset) => /* @__PURE__ */ jsx20(SelectItem2, { value: optionValue(preset), children: presetLabel(preset) }, optionValue(preset)))
          }
        )
      ] }) : /* @__PURE__ */ jsx20(
        SegmentedControl,
        {
          size: "sm",
          "aria-label": label,
          value: selected,
          onValueChange: (next) => {
            if (next !== CUSTOM) choosePreset(next);
          },
          onKeyDownCapture: (event) => {
            arrowedRef.current = ARROW_KEYS.has(event.key);
          },
          onKeyUpCapture: () => {
            arrowedRef.current = false;
          },
          onPointerDownCapture: () => {
            arrowedRef.current = false;
          },
          children: presets.map((preset) => preset === CUSTOM ? /* @__PURE__ */ jsx20(
            SegmentedControlItem,
            {
              value: CUSTOM,
              onClick: (event) => {
                if (!arrowedRef.current) openCustom(event.currentTarget);
              },
              onKeyDownCapture: (event) => {
                if (event.key === "Enter") openCustom(event.currentTarget);
              },
              children: presetLabel(preset)
            },
            CUSTOM
          ) : /* @__PURE__ */ jsx20(SegmentedControlItem, { value: presetKey(preset), children: presetLabel(preset) }, preset))
        }
      ),
      value.kind === "custom" && allowCustom ? /* @__PURE__ */ jsx20(Button8, { variant: "borderless", color: "neutral", size: "sm", "aria-haspopup": "dialog", "aria-expanded": open, onClick: (event) => openCustom(event.currentTarget), children: formatTimeRangeLabel(value.from, value.to, locale, now) }) : null
    ] }) }),
    allowCustom ? /* @__PURE__ */ jsx20(
      PopoverContent,
      {
        align: "end",
        "aria-label": t("shared.charts.range.custom_label", "Custom period"),
        onInteractOutside: () => {
          interactedOutsideRef.current = true;
        },
        onCloseAutoFocus: (event) => {
          event.preventDefault();
          if (!interactedOutsideRef.current && openerRef.current?.isConnected) openerRef.current.focus();
        },
        children: /* @__PURE__ */ jsxs18("form", { className: "grid w-[260px] max-w-full gap-3", onSubmit: apply, noValidate: true, children: [
          /* @__PURE__ */ jsx20(
            TextField5,
            {
              type: "date",
              label: t("shared.charts.range.from", "From"),
              value: from,
              max: to || today,
              onChange: (event) => setFrom(event.target.value),
              error: faultyField === "from",
              helperText: faultyField === "from" ? problem : void 0
            }
          ),
          /* @__PURE__ */ jsx20(
            TextField5,
            {
              type: "date",
              label: t("shared.charts.range.to", "To"),
              value: to,
              min: from || void 0,
              max: today,
              onChange: (event) => setTo(event.target.value),
              error: faultyField === "to",
              helperText: faultyField === "to" ? problem : void 0
            }
          ),
          /* @__PURE__ */ jsx20(Button8, { type: "submit", size: "sm", disabled: Boolean(issue), children: t("shared.charts.range.apply", "Apply") })
        ] })
      }
    ) : null
  ] });
}

// src/components/chat/channel-conversation.ts
function toMailbox({ address, name }) {
  return name ? { address, name } : { address };
}
function toConversationEmailMessage(conversation, message) {
  const { email } = message;
  if (email) {
    return {
      subject: email.subject || void 0,
      fromName: email.from.name || void 0,
      fromAddress: email.from.address,
      replyTo: email.replyTo ? toMailbox(email.replyTo) : void 0,
      to: email.to.map(toMailbox),
      cc: (email.cc ?? []).map(toMailbox),
      html: email.htmlTooLarge ? void 0 : email.html ?? void 0,
      htmlTooLarge: email.htmlTooLarge
    };
  }
  return conversation.channel === "email" && message.role === "user" ? { fromAddress: conversation.fromUserId } : void 0;
}
function conversationSubject(conversation) {
  if (conversation.subject) return conversation.subject;
  return conversation.messages.findLast((message) => message.email?.subject)?.email?.subject;
}
function toChannelChatSession(conversation) {
  const subject = conversationSubject(conversation);
  return {
    id: conversation.id,
    createdAt: conversation.createdAt,
    ...conversation.storeID !== void 0 ? { storeID: conversation.storeID } : {},
    ...conversation.storeColor !== void 0 ? { storeColor: conversation.storeColor } : {},
    ...subject ? { subject } : {},
    chatMessage: conversation.messages.map((message) => ({
      id: message.id,
      role: message.role,
      content: message.content,
      sentBy: message.sentBy ?? null,
      createdAt: message.createdAt,
      metadata: message.metadata ?? null,
      attachments: message.attachments ?? [],
      email: toConversationEmailMessage(conversation, message)
    }))
  };
}

// src/components/chat/ChatWrapper.tsx
import { useEffect as useEffect7 } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Conversations, Link as Link2 } from "@buyscout/components";
import { jsx as jsx21 } from "react/jsx-runtime";
function RouterUserLink({ href, children }) {
  return /* @__PURE__ */ jsx21(Link2, { asChild: true, children: /* @__PURE__ */ jsx21(RouterLink, { to: href, children }) });
}
function ChatWrapper(props) {
  const { onFetchChatSessions } = props;
  useEffect7(() => {
    onFetchChatSessions?.({ page: 1, per: 10 });
  }, [onFetchChatSessions]);
  return /* @__PURE__ */ jsx21(Conversations, { UserLink: RouterUserLink, ...props });
}
var ChatWrapper_default = ChatWrapper;

// src/components/chat/ToolCallResultDialog.tsx
import { useTranslation as useTranslation14 } from "react-i18next";
import { Badge, Button as Button9, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle, Skeleton, Text as Text5 } from "@buyscout/components";
import clsx13 from "clsx";

// src/components/reusable/VisuallyHidden.tsx
import { jsx as jsx22 } from "react/jsx-runtime";
var hidden = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0
};
function VisuallyHidden({ children }) {
  return /* @__PURE__ */ jsx22("span", { style: hidden, children });
}

// src/components/chat/ToolCallResultDialog.tsx
import { jsx as jsx23, jsxs as jsxs19 } from "react/jsx-runtime";
var CODE_WELL = clsx13(lgType.callout, "m-0 rounded-(--bs-lg-field-radius-md)");
var PLAIN_FILL = "bg-(--bs-lg-fill-3)";
function ToolCallResultDialog({ open, onClose, data, loading = false }) {
  const { t } = useTranslation14();
  return /* @__PURE__ */ jsx23(Dialog, { open, onOpenChange: (next) => {
    if (!next) onClose();
  }, children: /* @__PURE__ */ jsxs19(DialogContent, { className: "!max-w-[900px]", children: [
    /* @__PURE__ */ jsx23(DialogHeader, { children: /* @__PURE__ */ jsx23(DialogTitle, { children: t("shared.chat.tool_result.title", "Tool Call Result") }) }),
    /* @__PURE__ */ jsxs19(DialogBody, { children: [
      loading && /* @__PURE__ */ jsxs19("div", { className: "flex flex-col gap-4", "aria-busy": "true", children: [
        /* @__PURE__ */ jsx23(VisuallyHidden, { children: t("shared.chat.tool_result.loading", "Loading") }),
        /* @__PURE__ */ jsxs19("div", { className: "min-h-[21px] flex flex-wrap items-center gap-x-6 gap-y-1", children: [
          /* @__PURE__ */ jsx23(Skeleton, { width: 132, height: 14 }),
          /* @__PURE__ */ jsx23(Skeleton, { width: 104, height: 14 }),
          /* @__PURE__ */ jsx23(Skeleton, { width: 188, height: 14 })
        ] }),
        /* @__PURE__ */ jsxs19("div", { children: [
          /* @__PURE__ */ jsx23("div", { className: "min-h-[21px] flex items-center mb-1", children: /* @__PURE__ */ jsx23(Skeleton, { width: 82, height: 14 }) }),
          /* @__PURE__ */ jsx23(CodeBlockSkeleton, { lines: 5 })
        ] }),
        /* @__PURE__ */ jsxs19("div", { children: [
          /* @__PURE__ */ jsx23("div", { className: "min-h-[21px] flex items-center mb-1", children: /* @__PURE__ */ jsx23(Skeleton, { width: 152, height: 14 }) }),
          /* @__PURE__ */ jsx23(CodeBlockSkeleton, { lines: 16 })
        ] })
      ] }),
      !loading && !data && /* @__PURE__ */ jsx23(Text5, { variant: "body-secondary", as: "p", className: "text-center py-8", children: t("shared.chat.tool_result.no_data", "No data") }),
      !loading && data && /* @__PURE__ */ jsxs19("div", { className: "flex flex-col gap-4", children: [
        /* @__PURE__ */ jsxs19("div", { className: "flex flex-wrap gap-x-6 gap-y-1", children: [
          /* @__PURE__ */ jsxs19(Text5, { variant: "body-secondary", as: "p", children: [
            /* @__PURE__ */ jsx23("strong", { children: t("shared.chat.tool_result.field.tool", "Tool:") }),
            " ",
            data.toolName
          ] }),
          /* @__PURE__ */ jsxs19(Text5, { variant: "body-secondary", as: "p", children: [
            /* @__PURE__ */ jsx23("strong", { children: t("shared.chat.tool_result.field.status", "Status:") }),
            " ",
            /* @__PURE__ */ jsx23(Badge, { variant: "soft", color: data.status === "success" ? "success" : "error", size: "sm", children: data.status })
          ] }),
          /* @__PURE__ */ jsxs19(Text5, { variant: "body-secondary", as: "p", children: [
            /* @__PURE__ */ jsx23("strong", { children: t("shared.chat.tool_result.field.time", "Time:") }),
            " ",
            new Date(data.createdAt).toLocaleString()
          ] })
        ] }),
        /* @__PURE__ */ jsxs19("div", { children: [
          /* @__PURE__ */ jsx23(Text5, { variant: "subtitle-2", as: "h3", className: "mb-1", children: t("shared.chat.tool_result.section.arguments", "Arguments") }),
          /* @__PURE__ */ jsx23("pre", { className: clsx13(CODE_WELL, PLAIN_FILL, "overflow-auto p-3 max-h-[200px]"), children: formatJSON(data.arguments) })
        ] }),
        data.summary && /* @__PURE__ */ jsxs19("div", { children: [
          /* @__PURE__ */ jsx23(Text5, { variant: "subtitle-2", as: "h3", className: "mb-1", children: t("shared.chat.tool_result.section.summary", "Summary") }),
          /* @__PURE__ */ jsx23(Text5, { variant: "body-secondary", as: "p", children: data.summary })
        ] }),
        data.result && /* @__PURE__ */ jsxs19("div", { children: [
          /* @__PURE__ */ jsx23(Text5, { variant: "subtitle-2", as: "h3", className: "mb-1", children: t("shared.chat.tool_result.section.full_result", "Full Result (sent to AI)") }),
          /* @__PURE__ */ jsx23("pre", { className: clsx13(CODE_WELL, PLAIN_FILL, "overflow-auto p-3 max-h-[400px] whitespace-pre-wrap break-words"), children: formatJSON(data.result) })
        ] }),
        data.error && /* @__PURE__ */ jsxs19("div", { children: [
          /* @__PURE__ */ jsx23(Text5, { variant: "error", as: "h3", className: "mb-1 font-medium", children: t("shared.chat.tool_result.section.error", "Error") }),
          /* @__PURE__ */ jsx23("pre", { className: clsx13(CODE_WELL, "bg-[color-mix(in_srgb,var(--bs-lg-danger)_10%,transparent)]", "overflow-auto p-3 max-h-[200px]"), children: data.error })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx23(DialogFooter, { children: /* @__PURE__ */ jsx23(Button9, { onClick: onClose, variant: "outlined", color: "neutral", children: t("shared.chat.tool_result.close", "Close") }) })
  ] }) });
}
function CodeBlockSkeleton({ lines }) {
  return /* @__PURE__ */ jsx23("div", { className: clsx13(CODE_WELL, PLAIN_FILL, "flex flex-col p-3"), children: Array.from({ length: lines }, (_, index) => /* @__PURE__ */ jsx23(Skeleton, { className: "mb-1", width: index === lines - 1 ? "45%" : "100%" }, index)) });
}
function formatJSON(str) {
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
}
var ToolCallResultDialog_default = ToolCallResultDialog;

// src/components/chat/types.ts
import { AIScenario } from "@buyscout/components";

// src/components/chat/utils.ts
import dayjs2 from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
dayjs2.extend(localizedFormat);
function isToday(date) {
  return dayjs2(date).isSame(dayjs2(), "day");
}
function formatDateToMonthShort(value, toTimeForCurrentDay = true) {
  if (toTimeForCurrentDay && isToday(value)) {
    return dayjs2(value).format("LT");
  }
  return dayjs2(value).format("MMM D");
}
var statusColorMap = {
  busy: "error",
  away: "warning",
  online: "success",
  offline: "secondary"
};

// src/components/chrome/AppShell.tsx
import { forwardRef as forwardRef3, useCallback as useCallback7, useEffect as useEffect8, useId as useId3, useRef as useRef7 } from "react";
import { useTranslation as useTranslation15 } from "react-i18next";
import { Announcer } from "@buyscout/components";
import clsx14 from "clsx";
import { jsx as jsx24, jsxs as jsxs20 } from "react/jsx-runtime";
var ROOT5 = clsx14(
  "relative flex bg-(--color-canvas) gap-(--app-shell-gap)",
  "[--app-shell-inset-top:16px] [--app-shell-inset-end:22px] [--app-shell-gap:18px] max-[900px]:[--app-shell-inset-end:16px]",
  "pt-(--app-shell-inset-top) pe-(--app-shell-inset-end) pb-[22px] ps-4"
);
var SKIP_LINK = clsx14(
  "fixed top-3 start-3 z-[1400] -translate-y-[calc(100%+24px)] focus:translate-y-0",
  "rounded-full px-4 py-2 text-[13.5px] leading-[18px] font-medium text-(--color-ink) no-underline",
  "border border-[var(--color-glass-border,#ffffff)] bg-(--bs-lg-pane-glass-bg) [box-shadow:var(--bs-lg-pane-glass-shadow)]",
  "[-webkit-backdrop-filter:var(--bs-lg-pane-glass-filter)] [backdrop-filter:var(--bs-lg-pane-glass-filter)]",
  "focus-visible:[outline:var(--bs-lg-focus-ring-width)_solid_var(--bs-lg-focus-ring-color)] focus-visible:outline-offset-2"
);
var AppShell = forwardRef3(
  function AppShell2(props, ref) {
    const {
      sidebar,
      header,
      children,
      fillViewport = false,
      contentWidth = "full",
      className
    } = props;
    const { t } = useTranslation15();
    const mainId = useId3();
    const rootRef = useRef7(null);
    const sentinelRef = useRef7(null);
    const contentRef = useRef7(null);
    const columnRef = useRef7(null);
    const setRootRef = useCallback7((node) => {
      rootRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    }, [ref]);
    useEffect8(() => {
      const root = rootRef.current;
      const sentinel = sentinelRef.current;
      if (fillViewport || !root || !sentinel || typeof IntersectionObserver === "undefined") {
        return;
      }
      const observer = new IntersectionObserver(([entry]) => {
        root.toggleAttribute("data-scrolled", !entry.isIntersecting);
      });
      observer.observe(sentinel);
      return () => {
        observer.disconnect();
        root.removeAttribute("data-scrolled");
      };
    }, [fillViewport]);
    useEffect8(() => {
      const content = contentRef.current;
      if (!content || typeof ResizeObserver === "undefined") {
        return;
      }
      const header2 = columnRef.current?.querySelector(":scope > header") ?? null;
      const rootStyle = document.documentElement.style;
      const publish = () => {
        const rect = content.getBoundingClientRect();
        rootStyle.setProperty("--bs-toast-area-start", `${rect.left}px`);
        rootStyle.setProperty("--bs-toast-area-end", `${document.documentElement.clientWidth - rect.right}px`);
        if (header2) {
          rootStyle.setProperty("--bs-toast-area-top", `${header2.getBoundingClientRect().bottom}px`);
        }
      };
      publish();
      const observer = new ResizeObserver(publish);
      observer.observe(content);
      if (header2) {
        observer.observe(header2);
      }
      return () => {
        observer.disconnect();
        rootStyle.removeProperty("--bs-toast-area-start");
        rootStyle.removeProperty("--bs-toast-area-end");
        rootStyle.removeProperty("--bs-toast-area-top");
      };
    }, []);
    return /* @__PURE__ */ jsxs20(
      "div",
      {
        ref: setRootRef,
        "data-fill-viewport": fillViewport || void 0,
        className: clsx14(ROOT5, fillViewport ? "h-screen overflow-clip" : "min-h-screen", className),
        children: [
          /* @__PURE__ */ jsx24(
            "a",
            {
              href: `#${mainId}`,
              className: SKIP_LINK,
              onClick: (event) => {
                event.preventDefault();
                contentRef.current?.focus();
              },
              children: t("shared.chrome.skip_to_content", "Skip to content")
            }
          ),
          /* @__PURE__ */ jsx24(
            "span",
            {
              ref: sentinelRef,
              "aria-hidden": true,
              "data-scroll-sentinel": true,
              style: { position: "absolute", top: 12, left: 0, width: 1, height: 1 }
            }
          ),
          sidebar,
          /* @__PURE__ */ jsxs20("div", { ref: columnRef, className: clsx14("flex min-w-0 flex-auto flex-col gap-5", fillViewport && "h-full min-h-0 overflow-clip"), children: [
            header,
            /* @__PURE__ */ jsx24(
              "main",
              {
                ref: contentRef,
                id: mainId,
                tabIndex: -1,
                className: clsx14(
                  "min-w-0 flex-auto outline-none",
                  contentWidth === "boxed" && "mx-auto w-full max-w-[1440px]",
                  fillViewport && "h-full min-h-0 overflow-clip *:h-full"
                ),
                children
              }
            )
          ] }),
          /* @__PURE__ */ jsx24(Announcer, {})
        ]
      }
    );
  }
);
AppShell.displayName = "AppShell";
var AppShell_default = AppShell;

// src/components/chrome/BootScreen.tsx
import { LoadingState } from "@buyscout/components";
import clsx15 from "clsx";
import { jsx as jsx25 } from "react/jsx-runtime";
function BootScreen({ className }) {
  return /* @__PURE__ */ jsx25(
    "div",
    {
      className: clsx15("flex min-h-dvh flex-col items-center justify-center p-4", className),
      "data-boot-screen": true,
      children: /* @__PURE__ */ jsx25(LoadingState, {})
    }
  );
}

// src/components/chrome/Header.tsx
import { forwardRef as forwardRef4 } from "react";
import { useTranslation as useTranslation16 } from "react-i18next";
import { Button as Button10 } from "@buyscout/components";
import clsx16 from "clsx";
import { jsx as jsx26, jsxs as jsxs21 } from "react/jsx-runtime";
var BAR2 = "sticky top-(--app-shell-inset-top) [[data-fill-viewport]_&]:top-0 z-5 isolate flex items-center gap-2.5";
var SCROLL_EDGE = clsx16(
  "before:pointer-events-none before:absolute before:-z-1 before:h-[60px] before:content-['']",
  "before:-start-(--app-shell-gap) before:-end-(--app-shell-inset-end) before:-top-(--app-shell-inset-top)",
  "before:bg-(--bs-lg-scroll-edge-bg) before:[box-shadow:var(--bs-lg-scroll-edge-shadow)]",
  "before:[-webkit-backdrop-filter:var(--bs-lg-scroll-edge-filter)] before:[backdrop-filter:var(--bs-lg-scroll-edge-filter)]",
  "before:opacity-0 before:[transition:opacity_var(--bs-lg-spring-smooth-duration)_var(--bs-lg-spring-smooth)]",
  "[[data-scrolled]_&]:before:opacity-100"
);
var Header = forwardRef4(
  function Header2(props, ref) {
    const {
      leftSlot,
      actions,
      onToggleNav,
      showNavToggle = false,
      className
    } = props;
    const { t } = useTranslation16();
    return /* @__PURE__ */ jsxs21("header", { ref, className: clsx16(BAR2, SCROLL_EDGE, className), children: [
      showNavToggle ? /* @__PURE__ */ jsx26(
        Button10,
        {
          className: "bs-header-nav-toggle !flex-none",
          variant: "glass",
          color: "neutral",
          shape: "circle",
          size: "lg",
          iconOnly: true,
          "aria-label": t("shared.chrome.header.toggle_nav", "Toggle navigation"),
          onClick: onToggleNav,
          children: /* @__PURE__ */ jsx26(Icon, { icon: "lucide:menu" })
        }
      ) : null,
      leftSlot ? /* @__PURE__ */ jsx26("div", { className: "flex items-center gap-2.5", children: leftSlot }) : null,
      /* @__PURE__ */ jsx26("div", { className: "flex-auto" }),
      actions ? /* @__PURE__ */ jsx26("div", { className: "flex min-w-0 items-center gap-2.5", children: actions }) : null
    ] });
  }
);
Header.displayName = "Header";
var Header_default = Header;

// src/components/chrome/NotificationsPopover.tsx
import { useId as useId4 } from "react";
import { useTranslation as useTranslation17 } from "react-i18next";
import {
  Avatar,
  Badge as Badge2,
  BadgeOverlay,
  Button as Button11,
  getInitials,
  IconTile,
  Popover as Popover2,
  PopoverClose,
  PopoverContent as PopoverContent2,
  PopoverTrigger,
  Text as Text6
} from "@buyscout/components";
import { Fragment as Fragment9, jsx as jsx27, jsxs as jsxs22 } from "react/jsx-runtime";
function NotificationAvatar({ notification }) {
  const { i18n } = useTranslation17();
  const { avatarAlt, avatarImg, avatarIcon, avatarText, avatarColor } = notification;
  if (avatarImg) {
    return /* @__PURE__ */ jsx27(Avatar, { src: avatarImg, alt: avatarAlt, fallback: "", tone: "neutral", size: "md" });
  }
  if (avatarIcon) {
    return /* @__PURE__ */ jsx27(IconTile, { tone: avatarColor === "primary" ? "gold" : "neutral", shape: "circle", size: "md", children: avatarIcon });
  }
  return /* @__PURE__ */ jsx27(Avatar, { fallback: getInitials(avatarText ?? "", i18n.language), tone: "neutral", size: "md" });
}
function NotificationsPopover({ notifications: notifications2, direction = "ltr" }) {
  const { t } = useTranslation17();
  const titleId = useId4();
  const count = notifications2.length;
  const title = t("shared.chrome.notifications.title", "Notifications");
  return /* @__PURE__ */ jsxs22(Popover2, { children: [
    /* @__PURE__ */ jsx27(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx27(
      Button11,
      {
        variant: "glass",
        color: "neutral",
        shape: "circle",
        size: "lg",
        iconOnly: true,
        "aria-label": count > 0 ? t("shared.chrome.notifications.trigger_aria", { count, defaultValue: "Notifications, {{count}} new" }) : title,
        children: /* @__PURE__ */ jsx27(BadgeOverlay, { color: "error", dot: count > 0, children: /* @__PURE__ */ jsx27(Icon, { icon: "mdi:bell-outline" }) })
      }
    ) }),
    /* @__PURE__ */ jsxs22(
      PopoverContent2,
      {
        className: "!w-[min(380px,calc(100vw-32px))] !pt-3 !pe-2 !pb-3 !ps-4",
        align: direction === "rtl" ? "start" : "end",
        collisionPadding: 16,
        "aria-labelledby": titleId,
        children: [
          /* @__PURE__ */ jsxs22("div", { className: "flex items-center gap-2 [&>:not(:first-child):last-child]:!ms-auto", children: [
            /* @__PURE__ */ jsx27(Text6, { id: titleId, variant: "heading-5", as: "h2", children: title }),
            count > 0 && /* @__PURE__ */ jsxs22(Fragment9, { children: [
              /* @__PURE__ */ jsx27(Badge2, { variant: "soft", color: "primary", size: "sm", children: t("shared.chrome.notifications.new_count", { count, defaultValue: "{{count}} New" }) }),
              /* @__PURE__ */ jsx27(PopoverClose, { asChild: true, children: /* @__PURE__ */ jsx27(Button11, { variant: "borderless", color: "neutral", size: "sm", children: t("shared.chrome.notifications.read_all", "Mark All Read") }) })
            ] })
          ] }),
          count === 0 && /* @__PURE__ */ jsx27(Text6, { variant: "body", as: "p", style: { margin: "8px 0 0", color: "var(--bs-text-secondary)" }, children: t("shared.chrome.notifications.empty", "No notifications yet.") }),
          count > 0 && /* @__PURE__ */ jsx27("ul", { className: "mt-2 max-h-[344px] list-none overflow-x-hidden overflow-y-auto p-0 pe-2", children: notifications2.map((notification) => /* @__PURE__ */ jsxs22("li", { className: "flex items-center gap-4 border-t border-(--color-hairline) py-3", children: [
            /* @__PURE__ */ jsx27(NotificationAvatar, { notification }),
            /* @__PURE__ */ jsxs22("div", { className: "flex min-w-0 flex-1 flex-col *:overflow-hidden *:text-ellipsis *:whitespace-nowrap [&>:first-of-type]:!mb-1", children: [
              /* @__PURE__ */ jsx27(Text6, { variant: "body-bold", as: "p", children: notification.title }),
              /* @__PURE__ */ jsx27(Text6, { variant: "body", as: "p", children: notification.subtitle })
            ] }),
            /* @__PURE__ */ jsx27(Text6, { variant: "caption", children: notification.meta })
          ] }, notification.title)) })
        ]
      }
    )
  ] });
}
NotificationsPopover.displayName = "NotificationsPopover";
var NotificationsPopover_default = NotificationsPopover;

// src/components/chrome/PageHeader.tsx
import { forwardRef as forwardRef5, Fragment as Fragment10 } from "react";
import { useTranslation as useTranslation18 } from "react-i18next";
import { Button as Button12, Skeleton as Skeleton2 } from "@buyscout/components";
import clsx17 from "clsx";
import { Fragment as Fragment11, jsx as jsx28, jsxs as jsxs23 } from "react/jsx-runtime";
var renderAnchor = ({ children, ...props }) => /* @__PURE__ */ jsx28("a", { ...props, children });
var CRUMBS = clsx17(
  "mb-[7px] flex items-center gap-2 text-(length:--bs-lg-type-callout-size) leading-(--bs-lg-type-callout-leading) text-(--bs-text-secondary)",
  "[&_a]:text-inherit [&_a]:no-underline [&_a:hover]:text-(--bs-text-primary)",
  "[&_a:focus-visible]:[outline:var(--bs-lg-focus-ring-width)_solid_var(--bs-lg-focus-ring-color)] [&_a:focus-visible]:[outline-offset:var(--bs-lg-focus-ring-offset)]"
);
var PageHeader = forwardRef5(
  function PageHeader2(props, ref) {
    const {
      breadcrumb,
      title,
      back,
      status,
      renderLink = renderAnchor,
      subtitle,
      actions,
      loading = false,
      titlePending = loading,
      subtitlePending = loading && subtitle !== void 0,
      breadcrumbPending = false,
      statusPending = false,
      busy = false,
      className
    } = props;
    const { t } = useTranslation18();
    const titleArrival = useArrival(titlePending);
    const subtitleArrival = useArrival(subtitlePending);
    const breadcrumbArrival = useArrival(breadcrumbPending);
    const statusArrival = useArrival(statusPending);
    const pendingName = /* @__PURE__ */ jsx28(VisuallyHidden, { children: t("shared.chrome.page_header.loading", "Loading") });
    const crumbs = breadcrumb && breadcrumb.length > 0 ? /* @__PURE__ */ jsx28("nav", { className: CRUMBS, "aria-label": t("shared.chrome.page_header.breadcrumb", "Breadcrumb"), children: breadcrumb.map((crumb, index) => {
      const isLast = index === breadcrumb.length - 1;
      const label = crumb.href && !isLast ? renderLink({ href: crumb.href, children: crumb.label }) : /* @__PURE__ */ jsx28("span", { className: clsx17("bs-crumb-current font-medium", isLast && breadcrumbArrival.fade), "aria-current": isLast ? "page" : void 0, onAnimationEnd: isLast ? breadcrumbArrival.onAnimationEnd : void 0, children: isLast && breadcrumbPending ? /* @__PURE__ */ jsxs23(Fragment11, { children: [
        pendingName,
        /* @__PURE__ */ jsx28(Skeleton2, { inline: true, width: 96, style: { maxWidth: "100%" } })
      ] }) : crumb.label });
      return /* @__PURE__ */ jsxs23(Fragment10, { children: [
        label,
        !isLast ? /* @__PURE__ */ jsx28(Icon, { className: "bs-crumb-sep size-[13px] flex-none text-(--bs-text-tertiary)", icon: "lucide:chevron-right", "aria-hidden": true }) : null
      ] }, `${crumb.label}-${index}`);
    }) }) : null;
    const heading = /* @__PURE__ */ jsx28("h1", { className: clsx17(lgType["title-1"], "min-w-0 font-semibold text-(--bs-text-primary)", titleArrival.fade), onAnimationEnd: titleArrival.onAnimationEnd, children: titlePending ? /* @__PURE__ */ jsxs23(Fragment11, { children: [
      pendingName,
      /* @__PURE__ */ jsx28(Skeleton2, { width: 260, style: { maxWidth: "100%" } })
    ] }) : title });
    const hasStatus = status !== void 0 && status !== null;
    return /* @__PURE__ */ jsxs23(
      "div",
      {
        ref,
        className: clsx17("flex flex-col items-start gap-5", className),
        "aria-busy": titlePending || subtitlePending || breadcrumbPending || statusPending || busy || void 0,
        children: [
          /* @__PURE__ */ jsxs23("div", { className: "max-w-full", children: [
            back ? /* @__PURE__ */ jsxs23("div", { className: "mb-3 flex min-w-0 items-center gap-3 [&>nav]:mb-0", children: [
              /* @__PURE__ */ jsx28(Button12, { asChild: true, variant: "glass", color: "neutral", size: "lg", shape: "circle", iconOnly: true, "aria-label": back.label, title: back.label, children: renderLink({ href: back.href, children: /* @__PURE__ */ jsx28(Icon, { icon: "ic:round-arrow-back-ios-new", "aria-hidden": true }) }) }),
              crumbs
            ] }) : crumbs,
            hasStatus || statusPending ? /* @__PURE__ */ jsxs23("div", { className: "flex flex-wrap items-center gap-x-3 gap-y-1", children: [
              heading,
              hasStatus ? /* @__PURE__ */ jsx28("div", { className: clsx17("flex min-w-0 items-center", statusArrival.fade), "data-page-header-status": true, onAnimationEnd: statusArrival.onAnimationEnd, children: status }) : null
            ] }) : heading,
            subtitle !== void 0 || subtitlePending ? /* @__PURE__ */ jsx28("div", { className: clsx17("mt-1 text-(length:--bs-lg-type-body-size) leading-(--bs-lg-type-body-leading) text-(--bs-text-secondary)", subtitleArrival.fade), onAnimationEnd: subtitleArrival.onAnimationEnd, children: subtitlePending ? /* @__PURE__ */ jsx28(Skeleton2, { width: 320, style: { maxWidth: "100%" } }) : subtitle }) : null
          ] }),
          actions ? /* @__PURE__ */ jsx28("div", { className: "flex max-w-full flex-wrap items-center gap-2.5", children: actions }) : null
        ]
      }
    );
  }
);
PageHeader.displayName = "PageHeader";
var PageHeader_default = PageHeader;

// src/components/chrome/ScrollToTop.tsx
import { useTranslation as useTranslation19 } from "react-i18next";
import { Button as Button13 } from "@buyscout/components";
import clsx18 from "clsx";

// src/hooks/useScrolledPast.ts
import { useCallback as useCallback8, useSyncExternalStore as useSyncExternalStore2 } from "react";
function useScrolledPast(threshold) {
  const subscribe = useCallback8((onChange) => {
    window.addEventListener("scroll", onChange, { passive: true });
    return () => window.removeEventListener("scroll", onChange);
  }, []);
  return useSyncExternalStore2(subscribe, () => window.scrollY > threshold, () => false);
}

// src/components/chrome/ScrollToTop.tsx
import { jsx as jsx29 } from "react/jsx-runtime";
var WRAPPER = clsx18(
  "fixed end-6 bottom-10 z-11 invisible opacity-0 [transform:scale(.9)]",
  "[transition:opacity_var(--bs-lg-spring-smooth-duration)_var(--bs-lg-spring-smooth),transform_var(--bs-lg-spring-smooth-duration)_var(--bs-lg-spring-smooth),visibility_0s_var(--bs-lg-spring-smooth)_var(--bs-lg-spring-smooth-duration)]",
  "data-visible:visible data-visible:opacity-100 data-visible:[transform:none] data-visible:delay-0",
  "motion-reduce:[transition:none]"
);
function ScrollToTop({ className }) {
  const { t } = useTranslation19();
  const visible = useScrolledPast(400);
  const scrollToTop = () => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };
  return /* @__PURE__ */ jsx29("div", { className: clsx18("mui-fixed", "width-before-scroll-bar", WRAPPER, className), "data-visible": visible || void 0, children: /* @__PURE__ */ jsx29(
    Button13,
    {
      variant: "glass",
      color: "neutral",
      shape: "circle",
      size: "lg",
      iconOnly: true,
      "aria-label": t("shared.chrome.scroll_to_top", "Back to top"),
      onClick: scrollToTop,
      children: /* @__PURE__ */ jsx29(Icon, { icon: "lucide:arrow-up" })
    }
  ) });
}
ScrollToTop.displayName = "ScrollToTop";
var ScrollToTop_default = ScrollToTop;

// src/components/chrome/Sidebar.tsx
import { forwardRef as forwardRef6, useEffect as useEffect10, useState as useState13 } from "react";
import { useTranslation as useTranslation20 } from "react-i18next";
import { Badge as Badge3, Collapse, Sheet, SheetContent, SheetTitle } from "@buyscout/components";
import clsx19 from "clsx";

// src/components/chrome/RollingMark.tsx
import { useEffect as useEffect9, useRef as useRef8 } from "react";
import { jsx as jsx30 } from "react/jsx-runtime";
var DURATION = 1830;
var EASING = "cubic-bezier(.25,.1,.4,1)";
var KEYFRAMES = [
  { offset: 0, transform: "translateX(-83%)" },
  { offset: 0.033, transform: "translateX(-83%)", easing: EASING },
  { offset: 0.311, transform: "translateX(0)" },
  { offset: 0.656, transform: "translateX(0)", easing: EASING },
  { offset: 0.934, transform: "translateX(83%)" },
  { offset: 1, transform: "translateX(83%)" }
];
var REST_START = 0.311;
var REST_END = 0.656;
function RollingMark({ src, alt, rolling, className }) {
  const ref = useRef8(null);
  const runRef = useRef8(null);
  const reduced = useMediaQuery(REDUCED_MOTION_QUERY);
  useEffect9(() => {
    const current = runRef.current;
    if (reduced) {
      if (!current) return;
      window.clearTimeout(current.stop);
      current.animation.cancel();
      runRef.current = null;
      return;
    }
    if (rolling) {
      if (current) {
        window.clearTimeout(current.stop);
        current.stop = void 0;
        return;
      }
      const animation = ref.current?.animate?.(KEYFRAMES, { duration: DURATION, iterations: Infinity });
      if (!animation) return;
      animation.currentTime = REST_END * DURATION;
      runRef.current = { animation };
      return;
    }
    if (!current || current.stop !== void 0) return;
    const at = Number(current.animation.currentTime ?? 0) % DURATION / DURATION;
    const wait = at >= REST_START && at <= REST_END ? 0 : (REST_START - at + 1) % 1 * DURATION;
    current.stop = window.setTimeout(() => {
      current.animation.cancel();
      runRef.current = null;
    }, wait);
  }, [rolling, reduced]);
  useEffect9(() => () => {
    window.clearTimeout(runRef.current?.stop);
    runRef.current?.animation.cancel();
    runRef.current = null;
  }, []);
  return /* @__PURE__ */ jsx30("img", { ref, className, alt, src });
}

// src/components/chrome/Sidebar.tsx
import { Fragment as Fragment12, jsx as jsx31, jsxs as jsxs24 } from "react/jsx-runtime";
var PANEL = clsx19(
  "flex flex-col px-3.5 py-[18px] rounded-(--radius-bento) text-(--color-ink)",
  "border border-[var(--color-glass-border,#ffffff)] bg-(--color-glass-overlay) [box-shadow:var(--shadow-glass)]",
  "[-webkit-backdrop-filter:saturate(140%)_blur(16px)] [backdrop-filter:saturate(140%)_blur(16px)]",
  "not-[.buyscout-theme-dark]:bg-(--bs-lg-pane-glass-bg) not-[.buyscout-theme-dark]:[box-shadow:var(--bs-lg-pane-glass-shadow)]",
  "not-[.buyscout-theme-dark]:[-webkit-backdrop-filter:var(--bs-lg-pane-glass-filter)] not-[.buyscout-theme-dark]:[backdrop-filter:var(--bs-lg-pane-glass-filter)]",
  "[transition:width_var(--bs-lg-spring-smooth-duration)_var(--bs-lg-spring-smooth),flex-basis_var(--bs-lg-spring-smooth-duration)_var(--bs-lg-spring-smooth)]",
  "motion-reduce:[transition:none]",
  // The ring sits inside the row, so the scroll area's overflow cannot clip it.
  "[&_a:focus-visible]:outline-none"
);
var PANEL_IN_DRAWER = "h-full w-[236px] min-w-[236px] max-w-[236px] flex-auto";
var PANEL_INLINE = "sticky top-4 h-[calc(100vh-38px)] flex-none self-start";
var PANEL_WIDE = "w-[236px] min-w-[236px] max-w-[236px] basis-[236px]";
var PANEL_RAIL = "w-[76px] min-w-[76px] max-w-[76px] basis-[76px]";
var SECTION_LABEL = clsx19(
  "mt-[18px] mb-2 px-3 text-[10.5px] leading-[14px] font-semibold tracking-[.09em] uppercase text-(--bs-text-tertiary)",
  // The brand's bottom margin already spaces the first section.
  "[div:first-child>&]:mt-1.5"
);
var STACK = "flex flex-col gap-[3px]";
var ROW = clsx19(
  "bs-snav-item relative flex cursor-pointer items-center gap-[13px] rounded-[14px] py-[11px] no-underline",
  "[transition:background-color_var(--transition-fast,150ms),color_var(--transition-fast,150ms)] motion-reduce:[transition:none]",
  "active:bg-(--bs-lg-row-press) pointer-fine:active:bg-(--bs-lg-row-press)",
  "active:[transition-duration:var(--bs-motion-press-duration,120ms)] active:ease-[ease]",
  "focus-visible:[outline:var(--bs-lg-focus-ring-width)_solid_var(--bs-lg-focus-ring-color)] focus-visible:-outline-offset-2",
  "[a:focus-visible>&]:[outline:var(--bs-lg-focus-ring-width)_solid_var(--bs-lg-focus-ring-color)] [a:focus-visible>&]:-outline-offset-2"
);
function rowClass({ collapsed, active }) {
  return clsx19(
    ROW,
    collapsed ? "justify-center px-0" : "px-3",
    active ? "is-active bg-(--bs-lg-fill-3) text-(--color-primary)" : "text-(--color-ink) pointer-fine:hover:bg-(--bs-lg-row-hover)"
  );
}
var ICON = "bs-snav-icon size-5 flex-none text-[20px]";
function labelClass(strong) {
  return clsx19(
    "bs-snav-label min-w-0 flex-auto overflow-hidden text-ellipsis whitespace-nowrap text-[13.5px] leading-[18px] tracking-normal",
    strong ? "font-semibold" : "font-medium"
  );
}
function ItemContent({ item, collapsed }) {
  return /* @__PURE__ */ jsxs24("div", { className: rowClass({ collapsed, active: item.active }), children: [
    item.icon ? /* @__PURE__ */ jsx31(Icon, { className: ICON, icon: item.icon }) : null,
    /* @__PURE__ */ jsxs24("span", { className: collapsed ? "bs-snav-label sr-only" : labelClass(item.active), children: [
      item.title,
      collapsed && item.badge ? /* @__PURE__ */ jsxs24(Fragment12, { children: [
        " ",
        item.badge
      ] }) : null
    ] }),
    item.badge ? collapsed ? /* @__PURE__ */ jsx31("span", { className: "bs-snav-dot absolute top-[7px] start-[calc(50%+4px)] size-2 rounded-full bg-(--color-primary)", "aria-hidden": true }) : /* @__PURE__ */ jsx31(Badge3, { variant: "soft", color: "primary", size: "sm", count: true, children: item.badge }) : null
  ] });
}
function NavBranch({ item, collapsed, inDrawer, renderItem }) {
  const isCollapsed = collapsed && !inDrawer;
  const children = item.children ?? [];
  const hasActiveChild = children.some((child) => child.active);
  const [open, setOpen] = useState13(hasActiveChild);
  useEffect10(() => {
    if (hasActiveChild) {
      setOpen(true);
    }
  }, [hasActiveChild]);
  const expanded = isCollapsed || open;
  const toggle = () => setOpen((value) => !value);
  const header = /* @__PURE__ */ jsxs24(
    "div",
    {
      role: "button",
      tabIndex: 0,
      "aria-expanded": expanded,
      className: clsx19(rowClass({ collapsed: isCollapsed }), { "is-open": expanded, "is-branch-active": hasActiveChild }),
      onClick: toggle,
      onKeyDown: (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggle();
        }
      },
      children: [
        item.icon ? /* @__PURE__ */ jsx31(Icon, { className: ICON, icon: item.icon }) : null,
        /* @__PURE__ */ jsx31("span", { className: isCollapsed ? "sr-only" : labelClass(hasActiveChild), children: item.title }),
        !isCollapsed && /* @__PURE__ */ jsx31(
          Icon,
          {
            className: clsx19(
              "bs-snav-caret size-4 flex-none text-[16px] opacity-55",
              "[transition:transform_var(--bs-lg-spring-smooth-duration)_var(--bs-lg-spring-smooth)] motion-reduce:[transition:none]",
              expanded && "[transform:rotate(180deg)]"
            ),
            icon: "mdi:chevron-down"
          }
        )
      ]
    }
  );
  return /* @__PURE__ */ jsxs24("div", { className: STACK, children: [
    renderItem(item, header),
    /* @__PURE__ */ jsx31(Collapse, { in: expanded, children: /* @__PURE__ */ jsx31("div", { className: clsx19(STACK, "pt-[3px]", !isCollapsed && "ms-[26px] border-s border-(--color-hairline) ps-2.5"), children: children.map((child) => renderItem(child, /* @__PURE__ */ jsx31(ItemContent, { item: child, collapsed: isCollapsed }))) }) })
  ] });
}
var Sidebar = forwardRef6(
  function Sidebar2(props, ref) {
    const {
      brand,
      sections,
      footerItems,
      renderItem,
      mode,
      open = false,
      onClose,
      variant = "permanent",
      collapsed = false,
      className
    } = props;
    const { t } = useTranslation20();
    const isDark = mode === "dark" || mode === "semi-dark";
    const landmark = t("shared.chrome.sidebar.landmark", "Main navigation");
    const content = (inDrawer) => /* @__PURE__ */ jsxs24(
      "nav",
      {
        ref,
        "aria-label": landmark,
        className: clsx19(
          PANEL,
          inDrawer ? PANEL_IN_DRAWER : [PANEL_INLINE, collapsed ? PANEL_RAIL : PANEL_WIDE],
          { "buyscout-theme-dark": isDark },
          className
        ),
        children: [
          /* @__PURE__ */ jsxs24("div", { className: clsx19("mb-3 flex items-center gap-[11px] pt-1.5 pb-1", collapsed && !inDrawer ? "justify-center px-0" : "px-2"), children: [
            /* @__PURE__ */ jsx31(
              "div",
              {
                className: clsx19(
                  "grid size-[38px] flex-none place-items-center overflow-hidden rounded-xl",
                  !brand.logoSrc && "bg-(--color-primary) text-[18px] font-bold text-white"
                ),
                children: brand.logoSrc ? (
                  // Decorative while the name renders beside it; it becomes the only thing
                  // naming the brand once the label is suppressed.
                  /* @__PURE__ */ jsx31(
                    RollingMark,
                    {
                      className: "size-full object-contain",
                      alt: collapsed && !inDrawer ? brand.name : "",
                      src: brand.logoSrc,
                      rolling: !!brand.loading
                    }
                  )
                ) : brand.name.charAt(0)
              }
            ),
            (!collapsed || inDrawer) && /* @__PURE__ */ jsx31("b", { className: clsx19(lgType["title-3"], "font-semibold whitespace-nowrap"), children: brand.name })
          ] }),
          /* @__PURE__ */ jsx31("div", { className: "flex min-h-0 flex-auto flex-col overflow-x-hidden overflow-y-auto", children: sections.filter((section) => section.items.length > 0).map((section) => {
            const showLabel = section.label && (!collapsed || inDrawer);
            return /* @__PURE__ */ jsxs24("div", { children: [
              showLabel ? /* @__PURE__ */ jsx31("div", { className: clsx19("bs-snav-section", SECTION_LABEL), children: section.label }) : null,
              /* @__PURE__ */ jsx31("div", { className: STACK, children: section.items.map((item) => item.children && item.children.length > 0 ? /* @__PURE__ */ jsx31(
                NavBranch,
                {
                  item,
                  collapsed,
                  inDrawer,
                  renderItem
                },
                item.id
              ) : renderItem(item, /* @__PURE__ */ jsx31(ItemContent, { item, collapsed: collapsed && !inDrawer }))) })
            ] }, section.id);
          }) }),
          footerItems && footerItems.length > 0 ? /* @__PURE__ */ jsx31("div", { className: clsx19(STACK, "border-t border-(--color-hairline) pt-2.5"), children: footerItems.map((item) => renderItem(item, /* @__PURE__ */ jsx31(ItemContent, { item, collapsed: collapsed && !inDrawer }))) }) : null
        ]
      }
    );
    if (variant === "temporary") {
      return /* @__PURE__ */ jsx31(Sheet, { open, onOpenChange: (next) => !next && onClose?.(), children: /* @__PURE__ */ jsxs24(
        SheetContent,
        {
          side: "start",
          "aria-describedby": void 0,
          className: "![--buyscout-sheet-inset:8px] !w-[236px] !overflow-visible !rounded-none !bg-transparent ![box-shadow:none]",
          children: [
            /* @__PURE__ */ jsx31(SheetTitle, { className: "sr-only", children: landmark }),
            content(true)
          ]
        }
      ) });
    }
    return content(false);
  }
);
Sidebar.displayName = "Sidebar";
var Sidebar_default = Sidebar;

// src/components/layout/BlankLayout.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
function BlankLayout({ children }) {
  return /* @__PURE__ */ jsx32("div", { className: "layout-wrapper h-screen", children: /* @__PURE__ */ jsx32("div", { className: "app-content relative min-h-screen overflow-x-hidden", children }) });
}

// src/hooks/useSettings.ts
import { use } from "react";

// src/contexts/SettingsContext.tsx
import { createContext, useEffect as useEffect11, useMemo as useMemo6, useState as useState14 } from "react";

// src/configs/themeConfig.ts
var themeConfig = {
  // ** Layout Configs
  /** @deprecated Use different names for different apps. Please specify the application name in the corresponding app. */
  templateName: "",
  layout: "vertical",
  mode: "light",
  direction: "ltr",
  skin: "default",
  contentWidth: "boxed",
  footer: "static",
  // ** Routing Configs
  routingLoader: true,
  // ** Navigation (Menu) Configs
  navHidden: false,
  menuTextTruncate: true,
  navSubItemIcon: "mdi:circle",
  verticalNavToggleType: "accordion",
  navCollapsed: false,
  afterVerticalNavMenuContentPosition: "fixed",
  beforeVerticalNavMenuContentPosition: "fixed",
  horizontalMenuToggle: "hover",
  horizontalMenuAnimation: true,
  // ** AppBar Configs
  appBar: "fixed",
  appBarBlur: true,
  // ** Other Configs
  responsiveFontSizes: true,
  disableRipple: true,
  disableCustomizer: true
};
var themeConfig_default = themeConfig;

// src/contexts/SettingsContext.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var initialSettings = {
  themeColor: "primary",
  mode: themeConfig_default.mode,
  skin: themeConfig_default.skin,
  footer: themeConfig_default.footer,
  layout: themeConfig_default.layout,
  lastLayout: themeConfig_default.layout,
  direction: themeConfig_default.direction,
  navHidden: themeConfig_default.navHidden,
  appBarBlur: themeConfig_default.appBarBlur,
  navCollapsed: themeConfig_default.navCollapsed,
  contentWidth: themeConfig_default.contentWidth,
  verticalNavToggleType: themeConfig_default.verticalNavToggleType,
  appBar: themeConfig_default.layout === "horizontal" && themeConfig_default.appBar === "hidden" ? "fixed" : themeConfig_default.appBar
};
var staticSettings = {
  appBar: initialSettings.appBar,
  footer: initialSettings.footer,
  layout: initialSettings.layout,
  navHidden: initialSettings.navHidden,
  lastLayout: initialSettings.lastLayout
};
function restoreSettings() {
  let settings = null;
  try {
    const storedData = window.localStorage.getItem("settings");
    if (storedData) {
      settings = { ...JSON.parse(storedData), ...staticSettings };
    } else {
      settings = initialSettings;
    }
  } catch (error) {
    console.error(error);
  }
  return settings;
}
function storeSettings(settings) {
  const initSettings = Object.assign({}, settings);
  delete initSettings.appBar;
  delete initSettings.footer;
  delete initSettings.layout;
  delete initSettings.navHidden;
  delete initSettings.lastLayout;
  window.localStorage.setItem("settings", JSON.stringify(initSettings));
}
var SettingsContext = createContext({
  saveSettings: () => null,
  settings: initialSettings
});
function SettingsProvider({ children, pageSettings }) {
  const [settings, setSettings] = useState14(() => restoreSettings() ?? { ...initialSettings });
  useEffect11(() => {
    if (pageSettings) {
      setSettings((current) => ({ ...current, ...pageSettings }));
    }
  }, [pageSettings]);
  useEffect11(() => {
    if (settings.layout === "horizontal" && settings.mode === "semi-dark") {
      saveSettings({ ...settings, mode: "light" });
    }
    if (settings.layout === "horizontal" && settings.appBar === "hidden") {
      saveSettings({ ...settings, appBar: "fixed" });
    }
  }, [settings.layout]);
  const saveSettings = (updatedSettings) => {
    storeSettings(updatedSettings);
    setSettings(updatedSettings);
  };
  const value = useMemo6(() => ({ settings, saveSettings }), [settings]);
  return /* @__PURE__ */ jsx33(SettingsContext, { value, children });
}
var SettingsConsumer = SettingsContext.Consumer;

// src/hooks/useSettings.ts
var useSettings = () => use(SettingsContext);

// src/session/auth-context.ts
import { createContext as createContext2, use as use2 } from "react";
var AuthContext = createContext2(null);
function useAuth() {
  const auth = use2(AuthContext);
  if (!auth) {
    throw new Error("useAuth must be used inside the app's AuthProvider");
  }
  return auth;
}

// src/components/layout/DashboardAppBarContent.tsx
import { Fragment as Fragment13, jsx as jsx34, jsxs as jsxs25 } from "react/jsx-runtime";
var notifications = [];
function DashboardAppBarContent({ start, modeToggler, userMenu }) {
  const { settings } = useSettings();
  const auth = useAuth();
  return /* @__PURE__ */ jsxs25("div", { className: "flex min-w-0 items-center gap-[10px]", children: [
    start,
    modeToggler,
    auth.user && /* @__PURE__ */ jsxs25(Fragment13, { children: [
      /* @__PURE__ */ jsx34(NotificationsPopover_default, { direction: settings.direction, notifications }),
      userMenu
    ] })
  ] });
}

// src/components/layout/DashboardErrorPage.tsx
import { Link as Link3 } from "react-router-dom";
import { Button as Button14 } from "@buyscout/components";

// src/components/reusable/ErrorScaffold.tsx
import { Badge as Badge4 } from "@buyscout/components";
import clsx20 from "clsx";
import { jsx as jsx35, jsxs as jsxs26 } from "react/jsx-runtime";
var ROOT6 = "relative grid min-h-screen place-items-center overflow-hidden bg-(--bs-lg-surface) p-6 max-[900px]:p-5";
var GHOST2 = "pointer-events-none absolute z-0 select-none text-[length:clamp(320px,46vw,620px)] leading-none font-semibold tracking-[-0.06em] text-[color:color-mix(in_srgb,var(--color-ink)_5%,transparent)] max-[900px]:text-[length:clamp(240px,74vw,360px)]";
var SCRIM2 = "pointer-events-none absolute z-0 h-[min(440px,62vh)] w-[min(720px,94vw)] bg-[radial-gradient(ellipse_at_center,var(--bs-lg-surface)_0%,var(--bs-lg-surface)_46%,transparent_74%)] max-[600px]:h-[min(330px,46vh)] max-[600px]:w-[min(420px,88vw)]";
var INNER = "relative z-1 w-full max-w-[460px] text-center";
var TITLE2 = clsx20(lgType["large-title"], "mx-0 mt-4 mb-0 font-normal text-(--color-ink) [&_b]:block [&_b]:font-semibold");
var BODY = clsx20(lgType["title-3"], "mx-auto mt-2 mb-0 max-w-[380px] text-(--bs-text-secondary)");
var ACTIONS = "mt-6 flex flex-wrap items-center justify-center gap-2";
function ErrorScaffold(props) {
  const { code, titleLead, titleEmphasis, body, eyebrow, actions } = props;
  return /* @__PURE__ */ jsxs26("div", { className: ROOT6, children: [
    /* @__PURE__ */ jsx35("span", { className: GHOST2, "aria-hidden": true, children: code }),
    /* @__PURE__ */ jsx35("span", { className: SCRIM2, "aria-hidden": true }),
    /* @__PURE__ */ jsxs26("div", { className: INNER, children: [
      /* @__PURE__ */ jsx35(Badge4, { variant: "outlined", color: "neutral", size: "md", children: eyebrow }),
      /* @__PURE__ */ jsxs26("h1", { className: TITLE2, children: [
        titleLead,
        /* @__PURE__ */ jsx35("b", { children: titleEmphasis })
      ] }),
      /* @__PURE__ */ jsx35("p", { className: BODY, children: body }),
      actions ? /* @__PURE__ */ jsx35("div", { className: ACTIONS, children: actions }) : null
    ] })
  ] });
}
ErrorScaffold.displayName = "ErrorScaffold";

// src/components/layout/DashboardErrorPage.tsx
import { jsx as jsx36 } from "react/jsx-runtime";
function DashboardErrorPage({ code, text, homeRoute }) {
  return /* @__PURE__ */ jsx36(
    ErrorScaffold,
    {
      code,
      eyebrow: text.eyebrow,
      titleLead: text.titleLead,
      titleEmphasis: text.titleEmphasis,
      body: text.body,
      actions: /* @__PURE__ */ jsx36(Button14, { asChild: true, variant: "prominent", color: "primary", size: "lg", children: /* @__PURE__ */ jsx36(Link3, { to: homeRoute, children: text.backToHome }) })
    }
  );
}

// src/components/layout/DashboardLayout.tsx
import { use as use4, useEffect as useEffect12, useRef as useRef9, useState as useState16 } from "react";
import { Link as Link4, useLocation } from "react-router-dom";
import { LoadingActivityProvider, Tooltip } from "@buyscout/components";

// src/contexts/PageInsideScrollContext.tsx
import { createContext as createContext3, useMemo as useMemo7, useState as useState15 } from "react";
import { jsx as jsx37 } from "react/jsx-runtime";
var defaultProvider = {
  isPageInsideScroll: false,
  setIsPageInsideScroll: () => null
};
var PageInsideScrollContext = createContext3(defaultProvider);
function PageInsideScrollProvider({ children }) {
  const [isPageInsideScroll, setIsPageInsideScroll] = useState15(defaultProvider.isPageInsideScroll);
  const values = useMemo7(() => ({
    isPageInsideScroll,
    setIsPageInsideScroll
  }), [isPageInsideScroll]);
  return /* @__PURE__ */ jsx37(PageInsideScrollContext, { value: values, children });
}

// src/session/CanViewNavLink.tsx
import { use as use3 } from "react";

// src/session/Can.tsx
import { createContext as createContext4 } from "react";
import { createContextualCan } from "@casl/react";
var AbilityContext = createContext4(void 0);
var Can = createContextualCan(AbilityContext.Consumer);

// src/session/CanViewNavLink.tsx
import { Fragment as Fragment14, jsx as jsx38 } from "react/jsx-runtime";
function CanViewNavLink(props) {
  const { children, navLink } = props;
  const ability = use3(AbilityContext);
  if (navLink && navLink.auth === false) {
    return /* @__PURE__ */ jsx38(Fragment14, { children });
  } else {
    return ability && ability.can(navLink?.action, navLink?.subject) ? /* @__PURE__ */ jsx38(Fragment14, { children }) : null;
  }
}

// src/components/layout/navigation.ts
function isGroup(item) {
  return "children" in item && !!item.children;
}
function navEntries(items) {
  return items.filter((item) => !!item && !("sectionTitle" in item));
}
function activeNavPath(items, pathname) {
  return navEntries(items).flatMap((item) => isGroup(item) ? item.children : [item]).map((item) => item.path).filter((path) => !!path).filter((path) => pathname === path || pathname.startsWith(`${path}/`)).sort((a, b) => b.length - a.length)[0] ?? null;
}
function buildDashboardNav(items, pathname, isFooterItem) {
  const entries = navEntries(items);
  const activePath = activeNavPath(items, pathname);
  const navLinks = /* @__PURE__ */ new Map();
  const toLeaf = (link, parent) => {
    const id = link.path ?? link.title;
    navLinks.set(id, parent ? { title: link.title, path: link.path, action: link.action ?? parent.action, subject: link.subject ?? parent.subject, auth: link.auth } : link);
    return { id, title: link.title, icon: link.icon, badge: link.badgeContent, active: !!link.path && link.path === activePath };
  };
  const toItem = (item) => {
    if (!isGroup(item)) return toLeaf(item);
    navLinks.set(item.title, { title: item.title, action: item.action, subject: item.subject, auth: item.auth });
    return {
      id: item.title,
      title: item.title,
      icon: item.icon,
      children: item.children.map((child) => toLeaf(child, item))
    };
  };
  const inFooter = (item) => !!isFooterItem && !isGroup(item) && isFooterItem(item);
  return {
    sections: [{ id: "menu", items: entries.filter((item) => !inFooter(item)).map(toItem) }],
    footerItems: isFooterItem ? entries.filter(inFooter).map(toItem) : void 0,
    navLinks
  };
}

// src/components/layout/DashboardLayout.tsx
import { Fragment as Fragment15, jsx as jsx39, jsxs as jsxs27 } from "react/jsx-runtime";
function DashboardLayout(props) {
  const { children, contentHeightFixed, brandName, navItems, isFooterItem, appBarContent } = props;
  const { settings, saveSettings } = useSettings();
  const { isPageInsideScroll } = use4(PageInsideScrollContext);
  const location = useLocation();
  const { navHidden, contentWidth, mode } = settings;
  const hidden2 = useMediaQuery(BELOW_LG);
  const phone = useMediaQuery(BELOW_MD);
  const isCollapsedRef = useRef9(settings.navCollapsed);
  useEffect12(() => {
    if (hidden2) {
      if (settings.navCollapsed) {
        saveSettings({ ...settings, navCollapsed: false, layout: "vertical" });
        isCollapsedRef.current = true;
      }
    } else {
      if (isCollapsedRef.current) {
        saveSettings({ ...settings, navCollapsed: true, layout: settings.lastLayout });
        isCollapsedRef.current = false;
      } else {
        if (settings.lastLayout !== settings.layout) {
          saveSettings({ ...settings, layout: settings.lastLayout });
        }
      }
    }
  }, [hidden2]);
  const [navVisible, setNavVisible] = useState16(false);
  const [loading, setLoading] = useState16(false);
  if (!phone && navVisible) setNavVisible(false);
  const { sections, footerItems, navLinks } = buildDashboardNav(navItems, location.pathname, isFooterItem);
  const hasNav = sections[0].items.length > 0 || !!footerItems?.length;
  const rail = hidden2 && !phone;
  const renderItem = (item, content) => {
    const control = item.children?.length ? content : /* @__PURE__ */ jsx39(
      Link4,
      {
        to: item.id,
        "aria-current": item.active ? "page" : void 0,
        style: { textDecoration: "none" },
        onClick: () => {
          if (navVisible) {
            setNavVisible(false);
          }
        },
        children: content
      }
    );
    return /* @__PURE__ */ jsx39(CanViewNavLink, { navLink: navLinks.get(item.id), children: rail ? /* @__PURE__ */ jsx39(Tooltip, { title: item.title, placement: "right", describes: false, children: control }) : control }, item.id);
  };
  const fillViewport = !!(contentHeightFixed || isPageInsideScroll);
  return /* @__PURE__ */ jsxs27(Fragment15, { children: [
    /* @__PURE__ */ jsx39(
      AppShell_default,
      {
        fillViewport,
        contentWidth: contentWidth === "boxed" ? "boxed" : "full",
        sidebar: navHidden ? null : /* @__PURE__ */ jsx39(
          Sidebar_default,
          {
            brand: { name: brandName, logoSrc: "/images/misc/buyscout-icon.png", loading },
            sections,
            footerItems,
            renderItem,
            mode,
            variant: phone ? "temporary" : "permanent",
            collapsed: rail,
            open: navVisible,
            onClose: () => setNavVisible(false)
          }
        ),
        header: /* @__PURE__ */ jsx39(
          Header_default,
          {
            actions: appBarContent,
            onToggleNav: () => setNavVisible(true),
            showNavToggle: phone && !navHidden && hasNav
          }
        ),
        children: /* @__PURE__ */ jsx39(LoadingActivityProvider, { onActiveChange: setLoading, children })
      }
    ),
    /* @__PURE__ */ jsx39(ScrollToTop_default, {})
  ] });
}

// src/components/layout/DashboardUserMenu.tsx
import { useTranslation as useTranslation21 } from "react-i18next";
import { Avatar as Avatar2, Button as Button15, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, getInitials as getInitials2, Text as Text7 } from "@buyscout/components";
import { jsx as jsx40, jsxs as jsxs28 } from "react/jsx-runtime";
function DashboardUserMenu({ user, signOutLabel, onSignOut, children }) {
  const { settings } = useSettings();
  const { i18n } = useTranslation21();
  const initials = getInitials2(user.name, i18n.language);
  const align = settings.direction === "ltr" ? "end" : "start";
  return /* @__PURE__ */ jsxs28(DropdownMenu, { modal: false, children: [
    /* @__PURE__ */ jsx40(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx40(Button15, { variant: "glass", color: "neutral", shape: "circle", size: "lg", iconOnly: true, "aria-label": user.name, children: /* @__PURE__ */ jsx40("span", { "aria-hidden": true, className: "text-[13px] font-semibold leading-none text-[var(--color-primary)]", children: initials }) }) }),
    /* @__PURE__ */ jsxs28(DropdownMenuContent, { align, className: "w-[230px]", children: [
      /* @__PURE__ */ jsxs28("div", { className: "flex items-center px-2.5 pb-2 pt-1", children: [
        /* @__PURE__ */ jsx40(
          Avatar2,
          {
            fallback: initials,
            tone: "gold",
            size: "md"
          }
        ),
        /* @__PURE__ */ jsxs28("div", { className: "ms-3 flex flex-col items-start", children: [
          /* @__PURE__ */ jsx40(Text7, { variant: "body-bold", as: "p", children: user.name }),
          /* @__PURE__ */ jsx40(Text7, { variant: "body-xs", as: "p", style: { color: "var(--bs-text-secondary)" }, children: user.type })
        ] })
      ] }),
      /* @__PURE__ */ jsx40(DropdownMenuSeparator, {}),
      children,
      /* @__PURE__ */ jsxs28(DropdownMenuItem, { onSelect: () => onSignOut(), children: [
        /* @__PURE__ */ jsx40(Icon, { icon: "mdi:logout-variant", fontSize: "1.5rem" }),
        signOutLabel
      ] })
    ] })
  ] });
}

// src/components/patterns/DashboardCollectionPanel.tsx
import { forwardRef as forwardRef8, useEffect as useEffect13, useLayoutEffect, useRef as useRef10, useState as useState17 } from "react";
import { useTranslation as useTranslation22 } from "react-i18next";
import { Button as Button16, Collapse as Collapse2, Skeleton as Skeleton3, Text as Text8 } from "@buyscout/components";
import clsx22 from "clsx";

// src/components/patterns/DashboardHeldState.tsx
import { forwardRef as forwardRef7 } from "react";
import { LoadingActivityMute as LoadingActivityMute2 } from "@buyscout/components";
import clsx21 from "clsx";
import { jsx as jsx41, jsxs as jsxs29 } from "react/jsx-runtime";
var ROOT7 = clsx21(
  "grid min-w-0 *:min-w-0 *:self-center *:[grid-area:1/1]",
  "[&>[data-held-ghost]]:invisible [&>[data-held-ghost]]:self-stretch [&>[data-held-ghost]_*]:![animation:none]"
);
var DashboardHeldState = forwardRef7(
  function DashboardHeldState2({ hold, children, className, ...props }, ref) {
    return /* @__PURE__ */ jsxs29("div", { ...props, ref, className: clsx21(ROOT7, className), children: [
      /* @__PURE__ */ jsx41("div", { "aria-hidden": true, "data-held-ghost": true, children: /* @__PURE__ */ jsx41(LoadingActivityMute2, { children: hold }) }),
      children
    ] });
  }
);
DashboardHeldState.displayName = "DashboardHeldState";

// src/components/patterns/DashboardCollectionPanel.tsx
import { Fragment as Fragment16, jsx as jsx42, jsxs as jsxs30 } from "react/jsx-runtime";
var renderAnchor2 = ({ children, ...props }) => /* @__PURE__ */ jsx42("a", { ...props, children });
var OPEN_CLASS = clsx22(
  "bs-collection-open flex min-w-0 flex-auto max-[600px]:flex-[1_1_12rem] cursor-pointer border-0 bg-transparent bg-none p-0 text-start text-inherit no-underline [font:inherit]",
  "after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
);
var ROWS = "m-0 list-none p-0";
var ROW_LINE = clsx22(
  "[&+&]:border-t [&+&]:border-(--bs-lg-hairline) contrast-more:border-(--bs-text-secondary)",
  "[transition:border-color_var(--bs-lg-fold-close-duration,160ms)_ease-out] motion-reduce:[transition:none]"
);
var ROW_BODY = clsx22(
  "relative isolate flex min-h-[var(--bs-touch-target-min,44px)] items-center gap-4 py-3",
  "before:pointer-events-none before:absolute before:-inset-x-2 before:inset-y-0.5 before:-z-1 before:rounded-(--bs-lg-control-radius-md) before:bg-transparent before:content-['']",
  "before:[transition:background-color_var(--transition-fast,150ms)] motion-reduce:before:[transition:none]",
  "pointer-fine:[&:has(>.bs-collection-open:hover)]:before:bg-(--bs-lg-row-hover)",
  "[&:has(>.bs-collection-open:active)]:before:bg-(--bs-lg-row-press) [&:has(>.bs-collection-open:hover:active)]:before:bg-(--bs-lg-row-press)",
  "[&:has(>.bs-collection-open:active)]:before:[transition-duration:var(--bs-motion-press-duration,120ms)] [&:has(>.bs-collection-open:active)]:before:ease-[ease]",
  "[&:has(>.bs-collection-open:focus-visible)]:before:[outline:var(--bs-lg-focus-ring-width)_solid_var(--bs-lg-focus-ring-color)]",
  "[&:has(>.bs-collection-open:focus-visible)]:before:[outline-offset:calc(var(--bs-lg-focus-ring-width)*-1)]",
  "max-[600px]:flex-wrap max-[600px]:gap-x-3 max-[600px]:gap-y-2",
  "max-[600px]:[&>[data-collection-text]]:flex-[1_1_12rem] max-[600px]:[&>[data-collection-actions]]:ms-auto"
);
var ROW2 = clsx22(ROW_LINE, ROW_BODY);
var REMOVED_ROW = "!border-t-transparent";
var ROWS_WITH_REMOVED = "[&>[data-collection-removed]:first-child+li]:!border-t-transparent";
function FoldingRow({ onFolded, children }) {
  const [open, setOpen] = useState17(true);
  useEffect13(() => {
    const frame = requestAnimationFrame(() => setOpen(false));
    return () => cancelAnimationFrame(frame);
  }, []);
  const handleSettled = (settledOpen) => {
    if (!settledOpen) onFolded();
  };
  return /* @__PURE__ */ jsx42(Collapse2, { in: open, onSettled: handleSettled, children });
}
var ROW_TEXT = "flex min-w-0 flex-auto flex-col gap-1";
var PLACEHOLDER_LINE = "!flex min-h-[1lh] items-center";
var TITLE_WIDTHS = ["64%", "48%", "72%", "56%"];
var META_WIDTHS = ["40%", "32%", "46%", "36%"];
var PAGINATION_SLOT = "mt-4 flex justify-center border-t border-(--bs-lg-hairline) pt-4";
var DashboardCollectionPanel = forwardRef8(
  function DashboardCollectionPanel2({ items, renderLink = renderAnchor2, loading = false, skeletonRows = 3, skeletonMeta = true, skeletonTrailing, empty, error, pagination, listLabel, removedIds, ...props }, ref) {
    const { t } = useTranslation22();
    const [lastShown, setLastShown] = useState17();
    const shown = !loading && !error ? items.length : 0;
    if (shown > 0 && shown !== lastShown) setLastShown(shown);
    const [heldCount, setHeldCount] = useState17();
    const [held, setHeld] = useState17(false);
    if (loading !== held) {
      setHeld(loading);
      if (loading) setHeldCount(lastShown ?? skeletonRows);
    } else if (shown > 0 && heldCount !== void 0) {
      setHeldCount(void 0);
    }
    const placeholderCount = heldCount ?? lastShown ?? skeletonRows;
    const shownRowsRef = useRef10([]);
    const [folded, setFolded] = useState17(() => /* @__PURE__ */ new Set());
    const itemIds = new Set(items.map((item) => item.id));
    const isRemoved = (id) => removedIds?.includes(id) === true && !itemIds.has(id);
    if ([...folded].some((id) => !isRemoved(id))) setFolded(new Set([...folded].filter(isRemoved)));
    const rows = loading || error ? [] : items.map((item) => ({ item, removed: false }));
    if (rows.length > 0) {
      shownRowsRef.current.forEach((row, index) => {
        if (isRemoved(row.item.id) && !folded.has(row.item.id)) rows.splice(Math.min(index, rows.length), 0, { item: row.item, removed: true });
      });
    }
    useLayoutEffect(() => {
      shownRowsRef.current = rows;
    });
    const renderPlaceholderRows = (count) => Array.from({ length: count }, (_, index) => /* @__PURE__ */ jsxs30("li", { className: ROW2, children: [
      /* @__PURE__ */ jsxs30("span", { className: ROW_TEXT, "data-collection-text": true, children: [
        /* @__PURE__ */ jsx42(Text8, { as: "span", variant: "body", className: PLACEHOLDER_LINE, children: /* @__PURE__ */ jsx42(Skeleton3, { width: TITLE_WIDTHS[index % TITLE_WIDTHS.length] }) }),
        skeletonMeta ? /* @__PURE__ */ jsx42(Text8, { as: "span", variant: "body-sm", className: PLACEHOLDER_LINE, children: /* @__PURE__ */ jsx42(Skeleton3, { width: META_WIDTHS[index % META_WIDTHS.length] }) }) : null
      ] }),
      skeletonTrailing?.status !== void 0 ? /* @__PURE__ */ jsx42(Text8, { as: "span", variant: "body", className: "!block min-h-[1lh] shrink-0", style: { width: skeletonTrailing.status } }) : null,
      skeletonTrailing?.action !== void 0 ? /* @__PURE__ */ jsx42("div", { className: "min-h-(--bs-lg-control-height-sm) shrink-0", "data-collection-actions": true, style: { width: skeletonTrailing.action } }) : null
    ] }, index));
    const renderText = (item) => /* @__PURE__ */ jsxs30("span", { className: ROW_TEXT, "data-collection-text": true, children: [
      /* @__PURE__ */ jsx42(Text8, { as: "span", variant: "body", className: "!text-(--bs-text-primary) [overflow-wrap:anywhere]", children: item.title }),
      item.meta ? /* @__PURE__ */ jsx42(Text8, { as: "span", variant: "body-sm", className: "!text-(--bs-text-secondary) [overflow-wrap:anywhere]", children: item.meta }) : null
    ] });
    const renderRowContent = (item) => {
      const text = renderText(item);
      const opener = item.href ? renderLink({ "href": item.href, "className": OPEN_CLASS, "aria-label": item.openLabel, "children": text }) : item.onOpen ? /* @__PURE__ */ jsx42("button", { type: "button", className: OPEN_CLASS, "aria-label": item.openLabel, onClick: item.onOpen, children: text }) : text;
      return /* @__PURE__ */ jsxs30(Fragment16, { children: [
        opener,
        item.status ? /* @__PURE__ */ jsx42("div", { className: "flex shrink-0 items-center", children: item.status }) : null,
        item.actions ? /* @__PURE__ */ jsx42("div", { className: "relative z-1 flex shrink-0 items-center gap-2", "data-collection-actions": true, children: item.actions }) : null
      ] });
    };
    const renderRow = ({ item, removed }) => removed ? /* @__PURE__ */ jsx42("li", { className: clsx22(ROW_LINE, REMOVED_ROW), "aria-hidden": true, inert: true, "data-collection-removed": true, children: /* @__PURE__ */ jsx42(FoldingRow, { onFolded: () => setFolded((current) => new Set(current).add(item.id)), children: /* @__PURE__ */ jsx42("div", { className: ROW_BODY, children: renderRowContent(item) }) }) }, item.id) : /* @__PURE__ */ jsx42("li", { className: ROW2, children: renderRowContent(item) }, item.id);
    const stateBody = (state) => /* @__PURE__ */ jsx42(DashboardHeldState, { hold: /* @__PURE__ */ jsx42("ul", { className: ROWS, children: renderPlaceholderRows(placeholderCount) }), children: state });
    const emptyState = empty ? /* @__PURE__ */ jsx42(DashboardPageState, { title: empty.title, description: empty.description, action: empty.action }) : null;
    const renderBody = () => {
      if (error) {
        return stateBody(
          /* @__PURE__ */ jsx42(
            DashboardPageState,
            {
              tone: "error",
              plain: true,
              title: error.title,
              description: error.description,
              action: error.onRetry ? /* @__PURE__ */ jsx42(Button16, { variant: "bordered", size: "sm", onClick: error.onRetry, children: error.retryLabel ?? t("shared.collection.retry", "Retry") }) : void 0
            }
          )
        );
      }
      if (loading) {
        if (placeholderCount === 0 && emptyState) {
          return /* @__PURE__ */ jsx42(DashboardHeldState, { hold: emptyState });
        }
        return /* @__PURE__ */ jsxs30("div", { children: [
          /* @__PURE__ */ jsx42("ul", { className: ROWS, "aria-hidden": true, "data-collection-placeholder": true, children: renderPlaceholderRows(placeholderCount) }),
          pagination ? /* @__PURE__ */ jsx42("div", { className: PAGINATION_SLOT, children: pagination }) : null
        ] });
      }
      if (items.length === 0 && emptyState) {
        return stateBody(emptyState);
      }
      return /* @__PURE__ */ jsxs30("div", { children: [
        /* @__PURE__ */ jsx42("ul", { className: clsx22(ROWS, ROWS_WITH_REMOVED), "aria-label": listLabel, children: rows.map(renderRow) }),
        pagination && items.length > 0 ? /* @__PURE__ */ jsx42("div", { className: PAGINATION_SLOT, children: pagination }) : null
      ] });
    };
    return /* @__PURE__ */ jsx42(DashboardPanel, { ...props, ref, loading, children: renderBody() });
  }
);
DashboardCollectionPanel.displayName = "DashboardCollectionPanel";

// src/components/patterns/DashboardDisclosureRow.tsx
import { forwardRef as forwardRef9, useId as useId5 } from "react";
import { Collapse as Collapse3, Text as Text9 } from "@buyscout/components";
import clsx23 from "clsx";
import { jsx as jsx43, jsxs as jsxs31 } from "react/jsx-runtime";
var ROOT8 = clsx23(
  "w-full min-w-0",
  "[[data-dashboard-disclosure-row]+&]:border-t [[data-dashboard-disclosure-row]+&]:border-(--bs-lg-hairline) contrast-more:border-(--bs-text-secondary)"
);
var TRIGGER = clsx23(
  "relative isolate flex min-h-[var(--bs-touch-target-min,44px)] min-w-0 flex-1 cursor-pointer items-center gap-2",
  "rounded-(--bs-lg-control-radius-md) border-0 bg-transparent px-0 py-1.5 text-start text-(--bs-text-primary)",
  "before:pointer-events-none before:absolute before:inset-0 before:-z-1 before:rounded-[inherit] before:bg-transparent before:content-['']",
  "before:[transition:background-color_var(--transition-fast,150ms)] motion-reduce:before:[transition:none]",
  "pointer-fine:hover:before:bg-(--bs-lg-row-hover) active:before:bg-(--bs-lg-row-press) pointer-fine:active:before:bg-(--bs-lg-row-press)",
  "active:before:[transition-duration:var(--bs-motion-press-duration,120ms)] active:before:ease-[ease]",
  "focus-visible:[outline:var(--bs-lg-focus-ring-width)_solid_var(--bs-lg-focus-ring-color)] focus-visible:[outline-offset:var(--bs-lg-focus-ring-offset)]"
);
var CHEVRON = clsx23(
  "grid size-(--bs-lg-control-height-md) flex-none place-items-center text-(--bs-text-secondary)",
  "[transition:transform_var(--bs-lg-spring-smooth-duration)_var(--bs-lg-spring-smooth)] motion-reduce:[transition:none]",
  "[&>svg]:block [&>svg]:size-(--bs-lg-control-icon-md)"
);
var DashboardDisclosureRow = forwardRef9(
  function DashboardDisclosureRow2({ label, open, onOpenChange, detail, action, children, className, ...props }, ref) {
    const id = useId5();
    const triggerId = `${id}-trigger`;
    const contentId = `${id}-content`;
    return /* @__PURE__ */ jsxs31("div", { ...props, ref, className: clsx23(ROOT8, className), "data-dashboard-disclosure-row": true, "data-open": open, children: [
      /* @__PURE__ */ jsxs31("div", { className: "flex min-w-0 items-center gap-3", "data-dashboard-disclosure-header": true, children: [
        /* @__PURE__ */ jsxs31("button", { id: triggerId, type: "button", className: TRIGGER, "aria-expanded": open, "aria-controls": contentId, onClick: () => onOpenChange(!open), children: [
          /* @__PURE__ */ jsx43("span", { className: clsx23(CHEVRON, open && "[transform:rotate(90deg)]"), "data-dashboard-disclosure-chevron": true, "aria-hidden": true, children: /* @__PURE__ */ jsx43(Icon, { icon: "mdi:chevron-right" }) }),
          /* @__PURE__ */ jsx43(Text9, { variant: "subtitle-2", as: "span", children: label }),
          detail ? /* @__PURE__ */ jsx43(Text9, { variant: "caption", as: "span", className: "ms-auto flex-none ps-3 pe-3", children: detail }) : null
        ] }),
        action ? /* @__PURE__ */ jsx43("div", { className: "flex flex-none items-center gap-3", children: action }) : null
      ] }),
      /* @__PURE__ */ jsx43(Collapse3, { in: open, children: /* @__PURE__ */ jsx43("section", { className: "min-w-0 border-t border-(--bs-lg-hairline) [&>:first-of-type]:border-t-0", id: contentId, "aria-labelledby": triggerId, children }) })
    ] });
  }
);
DashboardDisclosureRow.displayName = "DashboardDisclosureRow";

// src/components/patterns/DashboardDocument.tsx
import { createContext as createContext5, forwardRef as forwardRef11, use as use5 } from "react";
import { useTranslation as useTranslation23 } from "react-i18next";
import { Accordion, AccordionContent, AccordionHeader, AccordionItem, AccordionTrigger, Button as Button17, Skeleton as Skeleton4, Text as Text10 } from "@buyscout/components";
import clsx25 from "clsx";

// src/components/patterns/DashboardPage.tsx
import { forwardRef as forwardRef10 } from "react";
import clsx24 from "clsx";
import { jsx as jsx44 } from "react/jsx-runtime";
var MAX_WIDTH = {
  settings: "max-w-[780px]",
  document: "max-w-[980px]",
  wide: "max-w-none"
};
var DashboardPage = forwardRef10(
  function DashboardPage2({ layout = "wide", className, ...props }, ref) {
    return /* @__PURE__ */ jsx44(
      "div",
      {
        ...props,
        ref,
        className: clsx24("flex w-full min-w-0 flex-col gap-[18px]", MAX_WIDTH[layout], className),
        "data-dashboard-page": true,
        "data-layout": layout
      }
    );
  }
);
DashboardPage.displayName = "DashboardPage";

// src/components/patterns/DashboardDocument.tsx
import { Fragment as Fragment17, jsx as jsx45, jsxs as jsxs32 } from "react/jsx-runtime";
var DocumentLoadingContext = createContext5(false);
var GUIDANCE_RAIL_QUERY = "(min-width: 761px)";
var SURFACE = clsx25(
  "min-w-0 overflow-clip rounded-2xl bg-(--bs-lg-surface) [box-shadow:var(--bs-lg-card-shadow)]",
  "contrast-more:[box-shadow:inset_0_0_0_1px_var(--bs-lg-ink-secondary)]"
);
var WORKBENCH_WITH_RAIL = "grid-cols-[minmax(0,1fr)_268px] max-[1101px]:grid-cols-[minmax(0,1fr)_230px] max-[761px]:grid-cols-[minmax(0,1fr)]";
var CANVAS_TEXT = clsx25(
  "box-border flex min-h-[420px] flex-col px-(--document-canvas-inset) pt-6 pb-8 max-[761px]:min-h-[320px]",
  "text-(length:--bs-lg-type-body-size) leading-(--bs-lg-type-body-leading)",
  "[&>:nth-of-type(1)]:!w-[92%] [&>:nth-of-type(2)]:!w-[84%] [&>:nth-of-type(3)]:!w-[58%]"
);
function DocumentCanvasPlaceholder() {
  return /* @__PURE__ */ jsxs32("div", { className: "[--document-canvas-inset:24px] max-[761px]:[--document-canvas-inset:16px]", "aria-hidden": true, "data-dashboard-document-placeholder": true, children: [
    /* @__PURE__ */ jsx45("div", { className: "border-b border-(--bs-lg-hairline) py-1.5", children: /* @__PURE__ */ jsx45("div", { className: "min-h-(--bs-lg-control-height-md) pointer-coarse:min-h-[var(--bs-touch-target-min,44px)]" }) }),
    /* @__PURE__ */ jsxs32("div", { className: CANVAS_TEXT, children: [
      /* @__PURE__ */ jsx45(Skeleton4, {}),
      /* @__PURE__ */ jsx45(Skeleton4, {}),
      /* @__PURE__ */ jsx45(Skeleton4, {})
    ] })
  ] });
}
var RAIL = clsx25(
  "min-w-0 border-s border-(--bs-lg-hairline) bg-(--bs-lg-fill-4) p-5",
  "max-[761px]:border-s-0 max-[761px]:border-t max-[761px]:bg-transparent max-[761px]:bg-none max-[761px]:p-0"
);
var DashboardDocument = forwardRef11(
  function DashboardDocument2({ header, status, titleField, guidance, footer, loading = false, children, ...props }, ref) {
    const showRail = useMediaQuery(GUIDANCE_RAIL_QUERY);
    return /* @__PURE__ */ jsxs32(DashboardPage, { ...props, ref, layout: "document", children: [
      header,
      /* @__PURE__ */ jsxs32("div", { className: SURFACE, "data-dashboard-document": true, "aria-busy": loading || void 0, children: [
        status ? /* @__PURE__ */ jsx45("div", { className: "flex flex-col gap-3 px-5 pt-5", children: status }) : null,
        /* @__PURE__ */ jsxs32("div", { className: clsx25("grid", guidance ? WORKBENCH_WITH_RAIL : "grid-cols-[minmax(0,1fr)]"), "data-guidance": guidance ? "" : void 0, children: [
          /* @__PURE__ */ jsxs32("div", { className: "min-w-0", children: [
            titleField ? /* @__PURE__ */ jsx45("div", { className: "px-6 pt-5 pb-3", children: titleField }) : null,
            loading ? /* @__PURE__ */ jsx45(DocumentCanvasPlaceholder, {}) : children
          ] }),
          guidance ? /* @__PURE__ */ jsx45("aside", { className: RAIL, "aria-label": guidance.title, children: showRail ? /* @__PURE__ */ jsxs32(Fragment17, { children: [
            /* @__PURE__ */ jsx45(Text10, { variant: "overline", as: "h2", className: "!mb-4", children: guidance.title }),
            guidance.content
          ] }) : /* @__PURE__ */ jsx45(Accordion, { type: "single", collapsible: true, children: /* @__PURE__ */ jsxs32(AccordionItem, { value: "guidance", children: [
            /* @__PURE__ */ jsx45(AccordionHeader, { asChild: true, children: /* @__PURE__ */ jsx45("h2", { children: /* @__PURE__ */ jsx45(AccordionTrigger, { className: "!min-h-[var(--bs-touch-target-min,44px)]", children: guidance.title }) }) }),
            /* @__PURE__ */ jsx45(AccordionContent, { children: guidance.content })
          ] }) }) }) : null
        ] }),
        footer ? /* @__PURE__ */ jsx45("div", { className: "sticky bottom-0 z-1 bg-(--bs-lg-surface) px-5 py-4 [box-shadow:var(--bs-lg-footer-edge)]", children: /* @__PURE__ */ jsx45(DocumentLoadingContext, { value: loading, children: footer }) }) : null
      ] })
    ] });
  }
);
DashboardDocument.displayName = "DashboardDocument";
var FOOTER_ACTIONS = "ms-auto flex gap-2 max-[761px]:w-full max-[761px]:flex-wrap max-[761px]:*:!min-w-max max-[761px]:*:!flex-auto";
var DashboardDocumentFooter = forwardRef11(
  function DashboardDocumentFooter2({ counter, error, dirty, saving, disabled = false, allowPristineSave = false, onCancel, onSave, saveLabel, cancelLabel, className, ...props }, ref) {
    const { t } = useTranslation23();
    const documentLoading = use5(DocumentLoadingContext);
    return /* @__PURE__ */ jsxs32("div", { ...props, ref, className: clsx25("flex flex-wrap items-center justify-between gap-4", className), children: [
      /* @__PURE__ */ jsxs32("div", { className: "flex min-w-0 flex-col gap-1 [overflow-wrap:anywhere] max-[761px]:w-full", children: [
        counter ? /* @__PURE__ */ jsx45(
          Text10,
          {
            variant: "body-sm",
            className: "!text-(--bs-text-secondary) tabular-nums",
            "aria-hidden": documentLoading || void 0,
            style: documentLoading ? { visibility: "hidden" } : void 0,
            children: counter
          }
        ) : null,
        error ? /* @__PURE__ */ jsx45(Text10, { variant: "error", as: "p", role: "alert", children: error }) : null
      ] }),
      /* @__PURE__ */ jsxs32("div", { className: FOOTER_ACTIONS, children: [
        /* @__PURE__ */ jsx45(Button17, { variant: "outlined", color: "neutral", disabled: saving || documentLoading, onClick: onCancel, children: cancelLabel ?? t("shared.document.cancel", "Cancel") }),
        /* @__PURE__ */ jsx45(Button17, { variant: "prominent", color: "primary", loading: saving, disabled: !saving && (documentLoading || disabled || !dirty && !allowPristineSave), onClick: onSave, children: saveLabel ?? t("shared.document.save", "Save") })
      ] })
    ] });
  }
);
DashboardDocumentFooter.displayName = "DashboardDocumentFooter";

// src/components/patterns/DashboardInfoPopover.tsx
import { useEffect as useEffect14, useId as useId6, useRef as useRef11, useState as useState18 } from "react";
import { Popover as Popover3, PopoverContent as PopoverContent3, PopoverTrigger as PopoverTrigger2, Text as Text11 } from "@buyscout/components";
import clsx26 from "clsx";
import { jsx as jsx46, jsxs as jsxs33 } from "react/jsx-runtime";
var TRIGGER2 = clsx26(
  "relative m-0 inline-grid size-5 flex-none cursor-pointer appearance-none place-items-center rounded-full border-0 bg-transparent p-0 text-(--bs-text-tertiary)",
  "[transition:background-color_120ms_ease,color_120ms_ease] [&_svg]:size-[15px]",
  "hover:bg-(--bs-lg-fill-3) hover:text-(--bs-text-secondary) aria-expanded:bg-(--bs-lg-fill-3) aria-expanded:text-(--bs-text-secondary) [&:active]:bg-(--bs-lg-fill-active)",
  "focus-visible:[outline:var(--bs-lg-focus-ring-width)_solid_var(--bs-lg-focus-ring-color)] focus-visible:[outline-offset:var(--bs-lg-focus-ring-offset)]",
  "before:absolute before:-inset-3 before:rounded-[inherit] before:content-['']"
);
function DashboardInfoPopover({ title, children }) {
  const [open, setOpen] = useState18(false);
  const contentId = useId6();
  const titleId = useId6();
  const descriptionId = useId6();
  const triggerRef = useRef11(null);
  const contentRef = useRef11(null);
  const closeTimerRef = useRef11(null);
  const pointerInsideRef = useRef11(false);
  const keyboardFocusRef = useRef11(false);
  const pointerTypeRef = useRef11("");
  const returningFocusRef = useRef11(false);
  function cancelClose() {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }
  function scheduleClose() {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      const focused = triggerRef.current?.ownerDocument.activeElement;
      const focusInside = focused && (triggerRef.current?.contains(focused) || contentRef.current?.contains(focused));
      if (!pointerInsideRef.current && !(keyboardFocusRef.current && focusInside)) setOpen(false);
    }, 150);
  }
  useEffect14(() => () => {
    if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
  }, []);
  return /* @__PURE__ */ jsxs33(
    Popover3,
    {
      open,
      onOpenChange: (nextOpen) => {
        cancelClose();
        setOpen(nextOpen);
      },
      children: [
        /* @__PURE__ */ jsx46(
          PopoverTrigger2,
          {
            ref: triggerRef,
            className: TRIGGER2,
            type: "button",
            "aria-label": title,
            "aria-controls": open ? contentId : void 0,
            onPointerDown: (event) => {
              pointerTypeRef.current = event.pointerType;
              keyboardFocusRef.current = false;
            },
            onPointerEnter: (event) => {
              if (event.pointerType !== "mouse") return;
              pointerInsideRef.current = true;
              pointerTypeRef.current = event.pointerType;
              cancelClose();
              setOpen(true);
            },
            onPointerLeave: (event) => {
              if (event.pointerType !== "mouse") return;
              pointerInsideRef.current = false;
              scheduleClose();
            },
            onFocus: (event) => {
              if (returningFocusRef.current) {
                returningFocusRef.current = false;
                return;
              }
              if (!event.currentTarget.matches(":focus-visible")) return;
              keyboardFocusRef.current = true;
              cancelClose();
              setOpen(true);
            },
            onBlur: scheduleClose,
            onClick: (event) => {
              if (open && (event.detail === 0 || pointerTypeRef.current === "mouse")) event.preventDefault();
            },
            children: /* @__PURE__ */ jsx46(Icon, { icon: "mdi:information-outline", "aria-hidden": true })
          }
        ),
        /* @__PURE__ */ jsxs33(
          PopoverContent3,
          {
            ref: contentRef,
            className: "!flex !w-[min(300px,calc(100vw-32px))] !flex-col !gap-1",
            id: contentId,
            side: "bottom",
            align: "start",
            collisionPadding: 16,
            "aria-labelledby": titleId,
            "aria-describedby": descriptionId,
            onOpenAutoFocus: (event) => event.preventDefault(),
            onCloseAutoFocus: (event) => event.preventDefault(),
            onEscapeKeyDown: () => {
              if (!contentRef.current?.contains(contentRef.current.ownerDocument.activeElement)) return;
              returningFocusRef.current = true;
              triggerRef.current?.focus();
              returningFocusRef.current = false;
            },
            onPointerEnter: (event) => {
              if (event.pointerType !== "mouse") return;
              pointerInsideRef.current = true;
              cancelClose();
            },
            onPointerLeave: (event) => {
              if (event.pointerType !== "mouse") return;
              pointerInsideRef.current = false;
              scheduleClose();
            },
            onFocus: () => {
              keyboardFocusRef.current = true;
              cancelClose();
            },
            onBlur: scheduleClose,
            children: [
              /* @__PURE__ */ jsx46(Text11, { variant: "heading-5", as: "p", id: titleId, children: title }),
              /* @__PURE__ */ jsx46(Text11, { variant: "body-secondary", as: "div", id: descriptionId, children })
            ]
          }
        )
      ]
    }
  );
}
DashboardInfoPopover.displayName = "DashboardInfoPopover";

// src/components/patterns/DashboardLocalizableField.tsx
import { forwardRef as forwardRef12 } from "react";
import { TextField as TextField6 } from "@buyscout/components";
import clsx28 from "clsx";

// src/components/patterns/DashboardTranslationToolbar.tsx
import { Button as Button18, Select as Select3, SelectContent as SelectContent3, SelectItem as SelectItem3, SelectTrigger as SelectTrigger3, Text as Text12, Tooltip as Tooltip2 } from "@buyscout/components";
import clsx27 from "clsx";

// src/components/patterns/localeStatus.ts
import { useTranslation as useTranslation24 } from "react-i18next";
function isSourceLocale(option) {
  return !!option.source || option.value === "default";
}
function useLocaleStatusLabels(statusLabels) {
  const { t } = useTranslation24();
  return statusLabels ?? {
    source: t("shared.translation_status.source", "Source"),
    translated: t("shared.translation_status.translated", "Translated"),
    needsTranslation: t("shared.translation_status.needs_translation", "Needs translation")
  };
}

// src/components/patterns/DashboardTranslationToolbar.tsx
import { Fragment as Fragment18, jsx as jsx47, jsxs as jsxs34 } from "react/jsx-runtime";
var FULL_LABEL = "[@container_(max-width:400px)]:!hidden";
var SHORT_LABEL = "hidden [@container_(max-width:400px)]:inline";
function DashboardLocaleStatusDot() {
  return /* @__PURE__ */ jsx47("span", { className: "inline-block size-[7px] shrink-0 rounded-full bg-(--bs-lg-accent)", "aria-hidden": true, "data-locale-status-dot": true });
}
function shortLabel(option) {
  return option.shortLabel ?? (option.value === "default" ? option.label : option.value.toUpperCase());
}
function DashboardLocalePicker({ value, onValueChange, options, disabled, label, variant = "subtle", statusLabels }) {
  const status = useLocaleStatusLabels(statusLabels);
  const current = options.find((option) => option.value === value);
  const borderless = variant === "borderless";
  return /* @__PURE__ */ jsxs34(Select3, { value, onValueChange, disabled, children: [
    /* @__PURE__ */ jsx47(
      SelectTrigger3,
      {
        size: borderless ? "xs" : "sm",
        variant,
        "aria-label": label,
        style: borderless ? void 0 : { width: "min(190px, 100%)" },
        children: /* @__PURE__ */ jsxs34("span", { className: clsx27("inline-flex min-w-0 items-center", borderless ? "gap-[inherit]" : "gap-1.5"), children: [
          borderless && current?.needsContent && !isSourceLocale(current) ? /* @__PURE__ */ jsx47(DashboardLocaleStatusDot, {}) : /* @__PURE__ */ jsx47(Icon, { icon: "ic:baseline-language", "aria-hidden": true }),
          borderless && current ? /* @__PURE__ */ jsxs34(Fragment18, { children: [
            /* @__PURE__ */ jsx47("span", { className: FULL_LABEL, children: current.label }),
            /* @__PURE__ */ jsx47("span", { className: SHORT_LABEL, "aria-hidden": true, children: shortLabel(current) })
          ] }) : current?.label
        ] })
      }
    ),
    /* @__PURE__ */ jsx47(SelectContent3, { children: options.map((option) => /* @__PURE__ */ jsx47(SelectItem3, { value: option.value, children: /* @__PURE__ */ jsxs34("span", { className: "flex w-full min-w-[220px] items-center justify-between gap-4", children: [
      /* @__PURE__ */ jsx47("span", { children: option.label }),
      /* @__PURE__ */ jsx47(Text12, { variant: "caption", as: "span", children: isSourceLocale(option) ? status.source : option.needsContent ? status.needsTranslation : status.translated })
    ] }) }, option.value)) })
  ] });
}
DashboardLocalePicker.displayName = "DashboardLocalePicker";
function DashboardLocalizeButton({ localize, disabled }) {
  return /* @__PURE__ */ jsx47(Tooltip2, { title: localize.description ?? "Generate translations from the default text.", children: /* @__PURE__ */ jsxs34(
    Button18,
    {
      type: "button",
      variant: "borderless",
      color: "neutral",
      size: "xs",
      "aria-label": localize.ariaLabel ?? localize.label,
      disabled: disabled || localize.disabled,
      onClick: localize.onClick,
      children: [
        /* @__PURE__ */ jsx47(Icon, { icon: "ic:baseline-translate", "aria-hidden": true }),
        /* @__PURE__ */ jsx47("span", { className: FULL_LABEL, children: localize.label })
      ]
    }
  ) });
}
DashboardLocalizeButton.displayName = "DashboardLocalizeButton";

// src/components/patterns/DashboardLocalizableField.tsx
import { jsx as jsx48, jsxs as jsxs35 } from "react/jsx-runtime";
var DashboardLocalizableField = forwardRef12(
  function DashboardLocalizableField2({ field, localize, locale, children, className, ...props }, ref) {
    const controlsDisabled = !!field.disabled || !!locale?.disabled;
    const accessory = locale || localize ? /* @__PURE__ */ jsxs35("div", { className: "contents", "data-dashboard-translation-toolbar": true, children: [
      locale ? /* @__PURE__ */ jsx48(DashboardLocalePicker, { ...locale, variant: "borderless", disabled: controlsDisabled }) : /* @__PURE__ */ jsx48("span", {}),
      localize ? /* @__PURE__ */ jsx48(DashboardLocalizeButton, { localize, disabled: controlsDisabled }) : null
    ] }) : void 0;
    return /* @__PURE__ */ jsxs35(
      "div",
      {
        ...props,
        ref,
        className: clsx28("flex w-full min-w-0 flex-col gap-2", className),
        "data-dashboard-localizable-field": true,
        "data-locale-editor": !!locale,
        children: [
          /* @__PURE__ */ jsx48(TextField6, { dir: "auto", ...field, fullWidth: true, accessory, accessoryLayout: "inline" }),
          children
        ]
      }
    );
  }
);
DashboardLocalizableField.displayName = "DashboardLocalizableField";

// src/components/patterns/DashboardLocalizableGroup.tsx
import { forwardRef as forwardRef13 } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger, Text as Text13 } from "@buyscout/components";
import clsx29 from "clsx";
import { jsx as jsx49, jsxs as jsxs36 } from "react/jsx-runtime";
var LANGUAGE_TABS = "flex-auto [@container_(max-width:400px)]:!hidden";
var COMPACT_PICKER = "hidden flex-auto -ms-2 [@container_(max-width:400px)]:flex";
var DashboardLocalizableGroup = forwardRef13(
  function DashboardLocalizableGroup2({ label, locale, localize, description, children, className, ...props }, ref) {
    const status = useLocaleStatusLabels(locale?.statusLabels);
    const rootClass = clsx29("flex w-full min-w-0 flex-col gap-6", className);
    if (!locale) {
      return /* @__PURE__ */ jsx49("div", { ...props, ref, className: rootClass, role: "group", "aria-label": label, "data-dashboard-localizable-group": true, children });
    }
    return /* @__PURE__ */ jsx49("div", { ...props, ref, className: rootClass, role: "group", "aria-label": label, "data-dashboard-localizable-group": true, children: /* @__PURE__ */ jsx49(Tabs, { value: locale.value, onValueChange: locale.onValueChange, children: /* @__PURE__ */ jsxs36("div", { className: "@container w-full min-w-0 rounded-2xl [box-shadow:inset_0_0_0_1px_var(--bs-lg-hairline)]", children: [
      /* @__PURE__ */ jsxs36("div", { className: "flex min-h-10 items-center gap-2 border-b border-(--bs-lg-hairline) ps-3.5 pe-1.5", "data-dashboard-translation-toolbar": true, children: [
        /* @__PURE__ */ jsx49(TabsList, { scrollable: true, scrollerClassName: LANGUAGE_TABS, variant: "text", "aria-label": locale.label, children: locale.options.map((option) => {
          const needsContent = option.needsContent && !isSourceLocale(option);
          return /* @__PURE__ */ jsx49(TabsTrigger, { value: option.value, disabled: locale.disabled, children: /* @__PURE__ */ jsxs36("span", { className: "inline-flex items-center gap-1.5", children: [
            option.label,
            needsContent ? /* @__PURE__ */ jsx49(DashboardLocaleStatusDot, {}) : null,
            /* @__PURE__ */ jsx49(VisuallyHidden, { children: `, ${isSourceLocale(option) ? status.source : needsContent ? status.needsTranslation : status.translated}` })
          ] }) }, option.value);
        }) }),
        /* @__PURE__ */ jsx49("div", { className: COMPACT_PICKER, children: /* @__PURE__ */ jsx49(DashboardLocalePicker, { ...locale, variant: "borderless" }) }),
        localize ? /* @__PURE__ */ jsx49(DashboardLocalizeButton, { localize, disabled: locale.disabled }) : null
      ] }),
      /* @__PURE__ */ jsxs36(TabsContent, { className: "!flex !flex-col !gap-6 !p-4 !outline-none", value: locale.value, children: [
        description ? /* @__PURE__ */ jsx49(Text13, { variant: "caption", as: "p", children: description }) : null,
        children
      ] })
    ] }) }) });
  }
);
DashboardLocalizableGroup.displayName = "DashboardLocalizableGroup";

// src/components/patterns/DashboardPageLoading.tsx
import { LoadingState as LoadingState2 } from "@buyscout/components";
import { jsx as jsx50, jsxs as jsxs37 } from "react/jsx-runtime";
function DashboardPageLoading({ header, layout }) {
  return /* @__PURE__ */ jsxs37(DashboardPage, { layout, className: "min-h-full", "aria-busy": "true", children: [
    header,
    /* @__PURE__ */ jsx50("div", { className: "flex flex-auto items-center justify-center", children: /* @__PURE__ */ jsx50(LoadingState2, {}) })
  ] });
}

// src/components/patterns/DashboardPending.tsx
import { Skeleton as Skeleton5 } from "@buyscout/components";
import clsx30 from "clsx";
import { jsx as jsx51 } from "react/jsx-runtime";
var FIGURE_BAND = "before:!h-[0.72em] supports-[height:1cap]:before:!h-[1cap] before:!rounded-(--bs-lg-control-radius-sm)";
function DashboardPendingBar({ width, className }) {
  return /* @__PURE__ */ jsx51(Skeleton5, { inline: true, width, className, style: { maxWidth: "100%" } });
}
function DashboardPendingFigure({ width = "3em", className }) {
  return /* @__PURE__ */ jsx51(Skeleton5, { inline: true, width, className: clsx30(FIGURE_BAND, className), style: { maxWidth: "100%" } });
}

// src/components/patterns/DashboardRouteFallback.tsx
import { useReportLoading } from "@buyscout/components";
import { jsx as jsx52, jsxs as jsxs38 } from "react/jsx-runtime";
function DashboardRouteFallback({ header, layout, minHeight = "60vh" }) {
  useReportLoading();
  return /* @__PURE__ */ jsxs38(DashboardPage, { layout, children: [
    header,
    /* @__PURE__ */ jsx52("div", { className: "flex-auto", "aria-busy": "true", style: { minHeight }, "data-dashboard-route-fallback": true })
  ] });
}

// src/components/patterns/DashboardSaveFooter.tsx
import { forwardRef as forwardRef14, useEffect as useEffect15, useRef as useRef12 } from "react";
import { useTranslation as useTranslation25 } from "react-i18next";
import { Button as Button19, Collapse as Collapse4, Text as Text14 } from "@buyscout/components";
import clsx31 from "clsx";
import { jsx as jsx53, jsxs as jsxs39 } from "react/jsx-runtime";
var DashboardSaveFooter = forwardRef14(
  function DashboardSaveFooter2({ dirty, saving = false, disabled = false, error, onSave, onCancel, saveLabel, cancelLabel, statusLabel, className, onFocus, onBlur, ...props }, ref) {
    const { t } = useTranslation25();
    const open = dirty || Boolean(error);
    const rootRef = useRef12(null);
    const hadFocusRef = useRef12(false);
    useEffect15(() => {
      if (open || saving || !hadFocusRef.current) return;
      hadFocusRef.current = false;
      const root = rootRef.current;
      const active = document.activeElement;
      if (!root || active && active !== document.body && !root.contains(active)) return;
      const heading = root.closest("[data-dashboard-panel]")?.querySelector("[data-dashboard-panel-heading] :is(h1, h2, h3)");
      if (!heading) return;
      if (!heading.hasAttribute("tabindex")) heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }, [open, saving]);
    return /* @__PURE__ */ jsx53(
      "div",
      {
        ...props,
        ref: (node) => {
          rootRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        },
        "data-dashboard-save-footer": true,
        "data-state": open ? "open" : "closed",
        onFocus: (event) => {
          hadFocusRef.current = true;
          onFocus?.(event);
        },
        onBlur: (event) => {
          if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) hadFocusRef.current = false;
          onBlur?.(event);
        },
        children: /* @__PURE__ */ jsx53(Collapse4, { in: open, children: /* @__PURE__ */ jsxs39("div", { className: clsx31("flex flex-wrap items-center justify-end gap-4 pt-6", className), children: [
          /* @__PURE__ */ jsx53("div", { className: "flex min-w-0 items-center text-(--bs-text-secondary)", "aria-live": "polite", children: error ? /* @__PURE__ */ jsx53(Text14, { variant: "caption", role: "alert", className: "!text-(--color-error) [overflow-wrap:anywhere]", children: error }) : open ? /* @__PURE__ */ jsx53(Text14, { variant: "caption", children: statusLabel ?? (saving ? t("shared.save_footer.saving", "Saving changes\u2026") : t("shared.save_footer.unsaved", "Unsaved changes")) }) : null }),
          /* @__PURE__ */ jsxs39("div", { className: "flex shrink-0 items-center gap-2", children: [
            /* @__PURE__ */ jsx53(Button19, { type: "button", variant: "bordered", size: "sm", disabled: disabled || saving || !dirty, onClick: onCancel, children: cancelLabel ?? t("shared.save_footer.cancel", "Cancel") }),
            /* @__PURE__ */ jsx53(Button19, { type: "button", variant: "prominent", color: "primary", size: "sm", loading: saving, disabled: !saving && (disabled || !dirty), onClick: onSave, children: saveLabel ?? t("shared.save_footer.save", "Save") })
          ] })
        ] }) })
      }
    );
  }
);
DashboardSaveFooter.displayName = "DashboardSaveFooter";

// src/components/patterns/DashboardSettingRow.tsx
import { forwardRef as forwardRef15 } from "react";
import { SettingsRow, SettingsRowContent, SettingsRowDescription, SettingsRowTitle, SettingsRowTrailing } from "@buyscout/components";
import { jsx as jsx54, jsxs as jsxs40 } from "react/jsx-runtime";
var DashboardSettingRow = forwardRef15(
  function DashboardSettingRow2({ label, description, children, controlWidth = "default", labelId, separator = true, ...props }, ref) {
    return /* @__PURE__ */ jsxs40(SettingsRow, { ...props, ref, separator, "data-dashboard-setting-row": true, "data-control-width": controlWidth, children: [
      /* @__PURE__ */ jsxs40(SettingsRowContent, { "data-dashboard-setting-label": true, children: [
        /* @__PURE__ */ jsx54(SettingsRowTitle, { id: labelId, children: label }),
        description ? /* @__PURE__ */ jsx54(SettingsRowDescription, { children: description }) : null
      ] }),
      /* @__PURE__ */ jsx54(SettingsRowTrailing, { width: controlWidth, "data-dashboard-setting-control": true, children })
    ] });
  }
);
DashboardSettingRow.displayName = "DashboardSettingRow";

// src/components/patterns/fillSlots.tsx
import { Fragment as Fragment19 } from "react";
import { jsxs as jsxs41 } from "react/jsx-runtime";
var SLOT2 = "\u2063";
function fillSlots(text, ...nodes) {
  return text.split(SLOT2).map((part, index) => (
    // eslint-disable-next-line react/no-array-index-key
    /* @__PURE__ */ jsxs41(Fragment19, { children: [
      part,
      index < nodes.length ? nodes[index] : null
    ] }, index)
  ));
}

// src/components/patterns/UnsavedChangesDialog.tsx
import { useTranslation as useTranslation26 } from "react-i18next";
import { Button as Button20, Dialog as Dialog2, DialogBody as DialogBody2, DialogContent as DialogContent2, DialogDescription, DialogFooter as DialogFooter2, DialogHeader as DialogHeader2, DialogTitle as DialogTitle2 } from "@buyscout/components";
import { jsx as jsx55, jsxs as jsxs42 } from "react/jsx-runtime";
function UnsavedChangesDialog({ open, onStay, onLeave, onSaveAndLeave, saving = false, busy = false, saveDisabled = false, title, description, children }) {
  const { t } = useTranslation26();
  return /* @__PURE__ */ jsx55(
    Dialog2,
    {
      open,
      onOpenChange: (next) => {
        if (!next && !saving) onStay();
      },
      children: /* @__PURE__ */ jsxs42(DialogContent2, { children: [
        /* @__PURE__ */ jsxs42(DialogHeader2, { children: [
          /* @__PURE__ */ jsx55(DialogTitle2, { children: title ?? t("shared.leave_guard.title", "Save your changes?") }),
          /* @__PURE__ */ jsx55(DialogDescription, { children: description ?? t("shared.leave_guard.description", "You have unsaved changes. Save them before leaving, discard them, or keep editing.") })
        ] }),
        children ? /* @__PURE__ */ jsx55(DialogBody2, { children }) : null,
        /* @__PURE__ */ jsxs42(DialogFooter2, { children: [
          /* @__PURE__ */ jsx55(Button20, { variant: "outlined", color: "neutral", disabled: saving, onClick: onStay, children: t("shared.leave_guard.stay", "Stay") }),
          /* @__PURE__ */ jsx55(Button20, { variant: "tinted", color: "error", disabled: saving || busy, onClick: onLeave, children: t("shared.leave_guard.discard", "Discard and leave") }),
          onSaveAndLeave ? /* @__PURE__ */ jsx55(Button20, { variant: "prominent", color: "primary", loading: saving, disabled: !saving && (busy || saveDisabled), onClick: onSaveAndLeave, children: t("shared.leave_guard.save_and_leave", "Save and leave") }) : null
        ] })
      ] })
    }
  );
}
UnsavedChangesDialog.displayName = "UnsavedChangesDialog";

// src/components/patterns/useLeaveGuard.ts
import { useCallback as useCallback9, useEffect as useEffect16, useRef as useRef13, useState as useState19 } from "react";
import { useBlocker, useLocation as useLocation2 } from "react-router-dom";

// src/components/patterns/leaveGuard.ts
function shouldBlockLeave({ guarded, bypassed, currentPathname, nextPathname }) {
  return guarded && !bypassed && currentPathname !== nextPathname;
}
function resolveLeave({ blocked, pending }) {
  if (blocked) return "proceed";
  return pending ? "run" : "none";
}
function savedForLeave(result) {
  return result !== false;
}

// src/components/patterns/useLeaveGuard.ts
function useLeaveGuard({ when, onSave, register }) {
  const location = useLocation2();
  const bypassRef = useRef13(false);
  const savingRef = useRef13(false);
  const pendingRef = useRef13(null);
  const [pending, setPending] = useState19(null);
  const [saving, setSaving] = useState19(false);
  const shouldBlock = useCallback9(({ currentLocation, nextLocation }) => shouldBlockLeave({
    guarded: when || savingRef.current,
    bypassed: bypassRef.current,
    currentPathname: currentLocation.pathname,
    nextPathname: nextLocation.pathname
  }), [when]);
  const blocker = useBlocker(shouldBlock);
  useEffect16(() => {
    bypassRef.current = false;
  }, [location.key]);
  useEffect16(() => {
    if (!when) return;
    const onUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [when]);
  const hold = useCallback9((action) => {
    pendingRef.current = action;
    setPending(() => action);
  }, []);
  const bypass = useCallback9((navigate) => {
    bypassRef.current = true;
    try {
      navigate();
    } finally {
      bypassRef.current = false;
    }
  }, []);
  const run = useCallback9((action) => {
    if (when || savingRef.current) hold(action);
    else action();
  }, [hold, when]);
  useEffect16(() => register?.(run), [register, run]);
  const stay = () => {
    hold(null);
    if (blocker.state === "blocked") blocker.reset();
  };
  const leave = () => {
    const action = pendingRef.current;
    hold(null);
    const resolution = resolveLeave({ blocked: blocker.state === "blocked", pending: action !== null });
    if (resolution === "proceed") {
      bypassRef.current = true;
      blocker.proceed?.();
    } else if (resolution === "run" && action) {
      bypass(action);
    }
  };
  const saveAndLeave = async () => {
    if (!onSave || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    let result;
    try {
      result = await onSave();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
    if (savedForLeave(result)) leave();
  };
  return {
    open: blocker.state === "blocked" || pending !== null,
    saving,
    stay,
    leave,
    saveAndLeave,
    run,
    bypass
  };
}

// src/components/reset-password/ResetPasswordView.tsx
import { useState as useState22 } from "react";
import { useTranslation as useTranslation29 } from "react-i18next";

// src/components/reset-password/ResetPasswordEmailForm.tsx
import { useEffect as useEffect17, useMemo as useMemo8, useRef as useRef14, useState as useState20 } from "react";
import { Controller as Controller5, useForm as useForm5 } from "react-hook-form";
import { useTranslation as useTranslation27 } from "react-i18next";
import { Button as Button21, TextField as TextField7 } from "@buyscout/components";
import { yupResolver as yupResolver5 } from "@hookform/resolvers/yup";
import * as yup5 from "yup";
import { Fragment as Fragment20, jsx as jsx56, jsxs as jsxs43 } from "react/jsx-runtime";
function ResetPasswordEmailFormInner(props) {
  const { initialEmail, callbacks } = props;
  const { t } = useTranslation27();
  const schema = useMemo8(() => yup5.object().shape({
    email: yup5.string().email(t("shared.reset_password.email.validation.email_invalid", "Please enter a valid email address.")).required(t("shared.reset_password.email.validation.email_required", "Email is required."))
  }), [t]);
  const [isSubmitting, setIsSubmitting] = useState20(false);
  const [announcement, setAnnouncement] = useState20("");
  const focusEmailRef = useRef14(false);
  const {
    control,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors, isValid }
  } = useForm5({
    defaultValues: {
      email: initialEmail || ""
    },
    mode: "onChange",
    resolver: yupResolver5(schema)
  });
  useEffect17(() => {
    if (isSubmitting || !focusEmailRef.current) {
      return;
    }
    focusEmailRef.current = false;
    setFocus("email");
  }, [isSubmitting, setFocus]);
  const handleSubmitForm = async (data) => {
    if (!data.email.trim()) return;
    setAnnouncement("");
    setIsSubmitting(true);
    try {
      await callbacks.onSubmit({ email: data.email.trim() });
    } catch (error) {
      const message = error instanceof ResetPasswordError && error.kind === "no-account" ? t("shared.reset_password.email.error.no_account", "We couldn't find an account with this email.") : t("shared.reset_password.email.error.send_failed", "We couldn't send a code to this email. Please try again.");
      setError("email", { type: "server", message });
      setAnnouncement(message);
      focusEmailRef.current = true;
    } finally {
      setIsSubmitting(false);
    }
  };
  return /* @__PURE__ */ jsxs43(Fragment20, { children: [
    /* @__PURE__ */ jsx56(
      AuthFormHeading,
      {
        title: t("shared.reset_password.email.title", "Forgot Password?"),
        subtitle: t("shared.reset_password.email.subtitle", "Enter your email and we'll send you a verification code to reset your password.")
      }
    ),
    /* @__PURE__ */ jsxs43("form", { noValidate: true, onSubmit: handleSubmit(handleSubmitForm), children: [
      /* @__PURE__ */ jsx56(
        Controller5,
        {
          name: "email",
          control,
          rules: { required: true },
          render: ({ field: { value, onChange, onBlur, ref } }) => /* @__PURE__ */ jsx56(
            TextField7,
            {
              ref,
              fullWidth: true,
              reserveHelperSpace: true,
              size: "xl",
              type: "email",
              autoComplete: "username",
              startIcon: "mail",
              autoFocus: true,
              label: t("shared.reset_password.email.field.email_label", "Email"),
              value,
              onBlur,
              onChange,
              disabled: isSubmitting,
              error: Boolean(errors.email),
              helperText: errors.email?.message,
              placeholder: t("shared.reset_password.email.field.email_placeholder", "name@example.com")
            }
          )
        }
      ),
      /* @__PURE__ */ jsx56("p", { role: "alert", className: "sr-only", children: announcement }),
      /* @__PURE__ */ jsx56(
        Button21,
        {
          className: "mt-8",
          variant: "prominent",
          color: "primary",
          size: "xl",
          fullWidth: true,
          type: "submit",
          loading: isSubmitting,
          disabled: !isValid && errors.email?.type !== "server",
          children: t("shared.reset_password.email.button.submit", "Send reset link")
        }
      )
    ] })
  ] });
}
function ResetPasswordEmailForm(props) {
  const { i18n } = useTranslation27();
  return /* @__PURE__ */ jsx56(ResetPasswordEmailFormInner, { ...props }, i18n.language);
}

// src/components/reset-password/ResetPasswordVerificationForm.tsx
import { useCallback as useCallback10, useEffect as useEffect18, useMemo as useMemo9, useRef as useRef15, useState as useState21 } from "react";
import { Controller as Controller6, useForm as useForm6 } from "react-hook-form";
import { useTranslation as useTranslation28 } from "react-i18next";
import { Button as Button22, TextField as TextField8 } from "@buyscout/components";
import { yupResolver as yupResolver6 } from "@hookform/resolvers/yup";
import * as yup6 from "yup";
import { Fragment as Fragment21, jsx as jsx57, jsxs as jsxs44 } from "react/jsx-runtime";
function ResetPasswordVerificationFormInner(props) {
  const { email, resend, callbacks } = props;
  const { t } = useTranslation28();
  const schema = useMemo9(() => yup6.object().shape({
    code: yup6.string().transform(normalizeVerificationCode).length(6, t("shared.reset_password.verification.validation.code_length", "Code must be exactly 6 characters long.")).required(t("shared.reset_password.verification.validation.code_required", "Code is required.")),
    password: yup6.string().min(8, t("shared.reset_password.verification.validation.password_min", "Password must be at least 8 characters long.")).required(t("shared.reset_password.verification.validation.password_required", "Password is required."))
  }), [t]);
  const [isSubmitting, setIsSubmitting] = useState21(false);
  const [announcement, setAnnouncement] = useState21("");
  const focusCodeRef = useRef15(false);
  const {
    control,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors, isValid }
  } = useForm6({
    defaultValues: {
      code: "",
      password: ""
    },
    mode: "onChange",
    resolver: yupResolver6(schema)
  });
  const focusCode = useCallback10(() => setFocus("code"), [setFocus]);
  useEffect18(() => {
    if (isSubmitting || !focusCodeRef.current) {
      return;
    }
    focusCodeRef.current = false;
    focusCode();
  }, [isSubmitting, focusCode]);
  const handleSubmitForm = async (data) => {
    setAnnouncement("");
    setIsSubmitting(true);
    try {
      await callbacks.onSubmit({ code: data.code, password: data.password });
    } catch (error) {
      const message = error instanceof ResetPasswordError && error.kind === "invalid-code" ? t("shared.reset_password.verification.error.invalid_code", "This code is wrong or has expired. Check the latest email, or send a new code.") : t("shared.reset_password.verification.error.failed", "We couldn't reset your password. Please try again.");
      setError("code", { type: "server", message });
      setAnnouncement(message);
      focusCodeRef.current = true;
    } finally {
      setIsSubmitting(false);
    }
  };
  return /* @__PURE__ */ jsxs44(Fragment21, { children: [
    /* @__PURE__ */ jsx57(
      AuthFormHeading,
      {
        title: t("shared.reset_password.verification.title", "Reset Password"),
        subtitle: t("shared.reset_password.verification.subtitle", "A verification code has been sent to: {{email}}", { email })
      }
    ),
    /* @__PURE__ */ jsxs44("form", { noValidate: true, onSubmit: handleSubmit(handleSubmitForm), children: [
      /* @__PURE__ */ jsx57("input", { type: "email", name: "username", autoComplete: "username", value: email, readOnly: true, hidden: true }),
      /* @__PURE__ */ jsx57(
        Controller6,
        {
          name: "code",
          control,
          rules: { required: true },
          render: ({ field: { value, onChange, onBlur, ref } }) => /* @__PURE__ */ jsx57(
            TextField8,
            {
              ref,
              className: "mb-4",
              fullWidth: true,
              size: "xl",
              autoFocus: true,
              autoComplete: "one-time-code",
              inputMode: "numeric",
              label: t("shared.reset_password.verification.field.code_label", "Code"),
              spellCheck: false,
              disabled: isSubmitting,
              value,
              onBlur,
              onChange: (event) => onChange(normalizeVerificationCode(event.target.value)),
              error: Boolean(errors.code),
              helperText: errors.code?.message,
              placeholder: t("shared.reset_password.verification.field.code_placeholder", "\u2022 \u2022 \u2022 \u2022 \u2022 \u2022")
            }
          )
        }
      ),
      /* @__PURE__ */ jsx57(
        Controller6,
        {
          name: "password",
          control,
          rules: { required: true },
          render: ({ field: { value, onChange, onBlur } }) => /* @__PURE__ */ jsx57(
            TextField8,
            {
              fullWidth: true,
              reserveHelperSpace: true,
              size: "xl",
              type: "password",
              autoComplete: "new-password",
              startIcon: "lock",
              disabled: isSubmitting,
              label: t("shared.reset_password.verification.field.password_label", "New Password"),
              value,
              onBlur,
              onChange,
              error: Boolean(errors.password),
              helperText: errors.password?.message,
              placeholder: t("shared.reset_password.verification.field.password_placeholder", "Required")
            }
          )
        }
      ),
      /* @__PURE__ */ jsx57("p", { role: "alert", className: "sr-only", children: announcement }),
      /* @__PURE__ */ jsx57(
        Button22,
        {
          className: "mt-8",
          variant: "prominent",
          color: "primary",
          size: "xl",
          fullWidth: true,
          type: "submit",
          loading: isSubmitting,
          disabled: !isValid && errors.code?.type !== "server",
          children: t("shared.reset_password.verification.button.submit", "Reset Password")
        }
      )
    ] }),
    /* @__PURE__ */ jsx57(ResendCodeRow, { email, secondsLeft: resend.secondsLeft, onResend: resend.onResend, focusCode })
  ] });
}
function ResetPasswordVerificationForm(props) {
  const { i18n } = useTranslation28();
  return /* @__PURE__ */ jsx57(ResetPasswordVerificationFormInner, { ...props }, i18n.language);
}

// src/components/reset-password/ResetPasswordView.tsx
import { jsx as jsx58 } from "react/jsx-runtime";
function ResetPasswordView(props) {
  const { callbacks, templateName, initialEmail } = props;
  const { t } = useTranslation29();
  const [currentStep, setCurrentStep] = useState22(0 /* Email */);
  const [email, setEmail] = useState22(initialEmail || "");
  const resendCountdown = useResendCountdown(true);
  const handleEmailSubmit = async (data) => {
    const result = await callbacks.onEmailSubmit(data);
    setEmail(data.email);
    resendCountdown.restart(result);
    setCurrentStep(1 /* Verification */);
  };
  const handleResend = async () => {
    const result = await callbacks.onEmailSubmit({ email });
    resendCountdown.restart(result);
    return result;
  };
  const handleVerificationSubmit = async (data) => {
    await callbacks.onVerificationSubmit({ ...data, email });
  };
  const handleBackToEmail = () => {
    setCurrentStep(0 /* Email */);
  };
  const handleBackToLogin = () => {
    callbacks.onBackToLogin();
  };
  const back = currentStep === 1 /* Verification */ ? { label: t("shared.reset_password.back_button.back", "Back"), onClick: handleBackToEmail } : { label: t("shared.reset_password.back_button.sign_in", "Sign In"), onClick: handleBackToLogin };
  const renderCurrentForm = () => {
    if (currentStep === 1 /* Verification */) {
      return /* @__PURE__ */ jsx58(
        ResetPasswordVerificationForm,
        {
          email,
          resend: { secondsLeft: resendCountdown.secondsLeft, onResend: handleResend },
          callbacks: {
            onSubmit: handleVerificationSubmit
          }
        }
      );
    }
    return /* @__PURE__ */ jsx58(
      ResetPasswordEmailForm,
      {
        initialEmail: email,
        callbacks: {
          onSubmit: handleEmailSubmit
        }
      }
    );
  };
  return /* @__PURE__ */ jsx58(AuthScaffold, { templateName, back, children: renderCurrentForm() });
}

// src/components/reusable/AppErrorFallback.tsx
import { jsx as jsx59, jsxs as jsxs45 } from "react/jsx-runtime";
var styles = `
.bs-app-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  box-sizing: border-box;
  background: var(--color-canvas, #f7f7f9);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}
.bs-app-error__card {
  width: 100%;
  max-width: 420px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 22px 18px;
  text-align: start;
  background: var(--color-surface, #ffffff);
  border: 0;
  border-radius: 20px;
  box-shadow: 0 0 0 0.5px rgba(0, 0, 0, 0.1), 0 18px 48px rgba(0, 0, 0, 0.22);
}
.bs-app-error__title {
  margin: 0;
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--color-ink, #1a1a1b);
}
.bs-app-error__text {
  margin: -16px 0 0;
  font-size: 13px;
  line-height: 19px;
  color: var(--color-text-secondary, rgba(26, 27, 27, 0.6));
}
.bs-app-error__btn {
  appearance: none;
  cursor: pointer;
  font: inherit;
  align-self: flex-end;
  height: 32px;
  padding: 0 14px;
  border: 0;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.006em;
  color: #ffffff;
  background: var(--color-primary, #ffc000);
  box-shadow:
    inset 0 1px 1px rgba(255, 255, 255, 0.4),
    inset 0 -1px 1px rgba(255, 255, 255, 0.14),
    inset 0 0 0 0.5px rgba(0, 0, 0, 0.05),
    0 1px 2px rgba(0, 0, 0, 0.06),
    0 4px 12px -4px color-mix(in srgb, var(--color-primary, #ffc000) 65%, transparent);
  transition: background-color 120ms ease;
}
.bs-app-error__btn:hover {
  background: color-mix(in srgb, var(--color-primary, #ffc000), #000 4%);
}
.bs-app-error__btn:active {
  background: color-mix(in srgb, var(--color-primary, #ffc000), #000 9%);
}
.bs-app-error__btn:focus-visible {
  outline: 2px solid var(--color-primary, #ffc000);
  outline-offset: 2px;
}
`;
function AppErrorFallback({ onReload }) {
  return /* @__PURE__ */ jsxs45("div", { className: "bs-app-error", children: [
    /* @__PURE__ */ jsx59("style", { children: styles }),
    /* @__PURE__ */ jsxs45("div", { className: "bs-app-error__card", children: [
      /* @__PURE__ */ jsx59("h1", { className: "bs-app-error__title", children: "Something went wrong" }),
      /* @__PURE__ */ jsx59("p", { className: "bs-app-error__text", children: "The application failed to load. Reloading usually fixes this." }),
      /* @__PURE__ */ jsx59(
        "button",
        {
          type: "button",
          className: "bs-app-error__btn",
          onClick: onReload ?? (() => window.location.reload()),
          children: "Reload"
        }
      )
    ] })
  ] });
}

// src/components/stats/HeroStatCard.tsx
import { forwardRef as forwardRef16 } from "react";
import { IconTile as IconTile2 } from "@buyscout/components";
import clsx34 from "clsx";

// src/components/stats/StatCardLabel.tsx
import clsx32 from "clsx";
import { jsx as jsx60 } from "react/jsx-runtime";
var LABEL = clsx32(lgType["card-title"], "font-semibold text-(--bs-text-primary)");
function StatCardLabel({ className, ...props }) {
  return /* @__PURE__ */ jsx60("div", { ...props, className: clsx32(LABEL, className) });
}
var StatCardLabel_default = StatCardLabel;

// src/components/stats/StatCardShell.tsx
import { Card } from "@buyscout/components";
import clsx33 from "clsx";
import { jsx as jsx61 } from "react/jsx-runtime";
var SHELL = clsx33(
  "h-full w-full !rounded-2xl !border-0 !bg-(--bs-lg-surface) ![box-shadow:var(--bs-lg-card-shadow)]",
  "*:!p-[var(--stat-card-padding,18px_20px)]",
  "contrast-more:![box-shadow:inset_0_0_0_1px_var(--bs-lg-ink-secondary)]"
);
function StatCardShell({ className, ...props }) {
  return /* @__PURE__ */ jsx61(Card, { ...props, className: clsx33(SHELL, className) });
}
var StatCardShell_default = StatCardShell;

// src/components/stats/TrendChip.tsx
import { useTranslation as useTranslation30 } from "react-i18next";
import { Badge as Badge5 } from "@buyscout/components";
import { jsx as jsx62, jsxs as jsxs46 } from "react/jsx-runtime";
function UpArrow() {
  return /* @__PURE__ */ jsx62("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2.3, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, children: /* @__PURE__ */ jsx62("path", { d: "M7 17 17 7M9 7h8v8" }) });
}
function DownArrow() {
  return /* @__PURE__ */ jsx62("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2.3, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, children: /* @__PURE__ */ jsx62("path", { d: "M17 7 7 17M15 17H7V9" }) });
}
function TrendChip({ trend, className }) {
  const { t } = useTranslation30();
  const down = trend.direction === "down";
  return /* @__PURE__ */ jsxs46(Badge5, { variant: "soft", color: down ? "neutral" : "primary", size: "sm", className, children: [
    down ? /* @__PURE__ */ jsx62(DownArrow, {}) : /* @__PURE__ */ jsx62(UpArrow, {}),
    /* @__PURE__ */ jsxs46(VisuallyHidden, { children: [
      down ? t("shared.stats.trend_down", "Down") : t("shared.stats.trend_up", "Up"),
      " "
    ] }),
    trend.value
  ] });
}
TrendChip.displayName = "TrendChip";
var TrendChip_default = TrendChip;

// src/components/stats/HeroStatCard.tsx
import { Fragment as Fragment22, jsx as jsx63, jsxs as jsxs47 } from "react/jsx-runtime";
var HEAD = "flex items-start justify-between gap-3";
var CAPTION = clsx34(lgType.callout, "mt-3 text-(--bs-text-secondary)");
var TREND_WRAP = "mt-3 flex min-h-[18px] [--stat-trend-chip-height:18px]";
var ROW3 = "flex items-end justify-between gap-6";
var BIG_NUM = clsx34(
  lgType["large-title"],
  "mt-1.5 font-semibold tabular-nums text-(--bs-text-primary)",
  "[&_.bs-affix]:text-[length:inherit] [&_.bs-affix]:font-semibold [&_.bs-affix]:text-(--bs-text-secondary)",
  "[&_.bs-affix--prefix]:me-0 [&_.bs-affix--prefix]:tracking-[inherit]"
);
var MINI_STATS = "mt-3.5 flex gap-4 border-t border-(--bs-lg-hairline) pt-3.5";
var MINI_STAT = "flex flex-[1_1_0] items-center gap-3";
var MINI_VALUE_ROW = "flex min-h-(--bs-lg-type-title-3-leading) items-baseline gap-1.5";
var MINI_VALUE = clsx34(lgType["title-3"], "font-semibold tabular-nums text-(--bs-text-primary)");
var MINI_LABEL = clsx34(lgType.callout, "text-(--bs-text-primary)");
var HeroStatCard = forwardRef16(
  function HeroStatCard2(props, ref) {
    const {
      label,
      caption,
      trend,
      value,
      valuePrefix,
      valueSuffix,
      miniStats,
      loading,
      className
    } = props;
    const { fade, onAnimationEnd } = useArrival(Boolean(loading));
    return /* @__PURE__ */ jsxs47(StatCardShell_default, { ref, className: clsx34("bs-hero-stat-card", className), "aria-busy": loading || void 0, onAnimationEnd, children: [
      /* @__PURE__ */ jsx63("div", { className: HEAD, children: /* @__PURE__ */ jsxs47("div", { children: [
        /* @__PURE__ */ jsx63(StatCardLabel_default, { children: label }),
        caption ? /* @__PURE__ */ jsx63("div", { className: CAPTION, children: caption }) : null
      ] }) }),
      /* @__PURE__ */ jsx63("div", { className: clsx34(TREND_WRAP, fade), children: !loading && trend ? /* @__PURE__ */ jsx63(TrendChip_default, { trend }) : null }),
      loading ? /* @__PURE__ */ jsx63("div", { className: ROW3, children: /* @__PURE__ */ jsx63("div", { className: BIG_NUM, children: /* @__PURE__ */ jsx63(DashboardPendingFigure, { width: 180 }) }) }) : /* @__PURE__ */ jsx63("div", { className: clsx34(ROW3, fade), children: /* @__PURE__ */ jsxs47("div", { className: BIG_NUM, children: [
        valuePrefix ? /* @__PURE__ */ jsx63("span", { className: "bs-affix bs-affix--prefix", children: valuePrefix }) : null,
        value,
        valueSuffix ? /* @__PURE__ */ jsx63("span", { className: "bs-affix bs-affix--suffix", children: valueSuffix }) : null
      ] }) }),
      miniStats && miniStats.length > 0 ? /* @__PURE__ */ jsx63("div", { className: MINI_STATS, children: miniStats.map((stat, index) => (
        // eslint-disable-next-line react/no-array-index-key
        /* @__PURE__ */ jsxs47("div", { className: MINI_STAT, children: [
          stat.icon ? /* @__PURE__ */ jsx63(IconTile2, { tone: "gold", size: "sm", children: stat.icon }) : null,
          /* @__PURE__ */ jsxs47("div", { children: [
            /* @__PURE__ */ jsx63("div", { className: clsx34(MINI_VALUE_ROW, fade), children: loading ? /* @__PURE__ */ jsx63("div", { className: MINI_VALUE, children: /* @__PURE__ */ jsx63(DashboardPendingFigure, { width: 56 }) }) : /* @__PURE__ */ jsxs47(Fragment22, { children: [
              /* @__PURE__ */ jsx63("div", { className: MINI_VALUE, children: stat.value }),
              stat.trend ? /* @__PURE__ */ jsx63(TrendChip_default, { trend: stat.trend }) : null
            ] }) }),
            /* @__PURE__ */ jsx63("div", { className: MINI_LABEL, children: stat.label })
          ] })
        ] }, index)
      )) }) : null
    ] });
  }
);
HeroStatCard.displayName = "HeroStatCard";
var HeroStatCard_default = HeroStatCard;

// src/components/stats/StatCardFailure.tsx
import { useTranslation as useTranslation31 } from "react-i18next";
import { Button as Button23 } from "@buyscout/components";
import clsx35 from "clsx";
import { jsx as jsx64, jsxs as jsxs48 } from "react/jsx-runtime";
function StatCardFailure({ ref, label, title, message, onRetry, className }) {
  const { t } = useTranslation31();
  return /* @__PURE__ */ jsxs48(StatCardShell_default, { ref, className: clsx35("bs-stat-card-failure", className), children: [
    label ? /* @__PURE__ */ jsx64(StatCardLabel_default, { "data-stat-card-label": true, children: label }) : null,
    /* @__PURE__ */ jsx64("div", { className: "[[data-stat-card-label]+&]:mt-3.5", children: /* @__PURE__ */ jsx64(
      DashboardPageState,
      {
        tone: "error",
        plain: true,
        title,
        description: message ?? t("shared.data_state.request_failed", "The request failed. Try again in a moment."),
        action: onRetry ? /* @__PURE__ */ jsx64(Button23, { variant: "bordered", size: "sm", onClick: onRetry, children: t("shared.data_state.retry", "Retry") }) : void 0
      }
    ) })
  ] });
}
var StatCardFailure_default = StatCardFailure;

// src/components/stats/StatStrip.tsx
import { Children, createContext as createContext6, use as use6 } from "react";
import { useTranslation as useTranslation32 } from "react-i18next";
import clsx36 from "clsx";
import { jsx as jsx65, jsxs as jsxs49 } from "react/jsx-runtime";
var StripStateContext = createContext6({ loading: false });
var STRIP = clsx36(
  "grid min-w-0 grid-cols-[repeat(var(--stat-strip-columns),minmax(0,1fr))] rounded-2xl bg-(--bs-lg-surface) [box-shadow:var(--bs-lg-card-shadow)]",
  "[&>[data-stat]+[data-stat]]:before:absolute [&>[data-stat]+[data-stat]]:before:inset-y-0 [&>[data-stat]+[data-stat]]:before:start-0 [&>[data-stat]+[data-stat]]:before:w-px [&>[data-stat]+[data-stat]]:before:bg-(--bs-lg-hairline)",
  "max-[600px]:grid-cols-[repeat(2,minmax(0,1fr))]",
  "max-[600px]:[&>[data-stat]:nth-of-type(odd)]:before:content-none",
  "max-[600px]:[&>[data-stat]:nth-of-type(n+3)]:[box-shadow:inset_0_1px_0_var(--bs-lg-hairline)]",
  "max-[600px]:[&>[data-stat]:last-child:nth-of-type(odd)]:col-span-full"
);
var STAT = "relative flex min-w-0 flex-col items-center justify-center gap-0.5 px-2 py-3 text-center";
var VALUE = clsx36(lgType["title-3"], "font-semibold tabular-nums text-(--bs-text-primary)");
var FAILURE = clsx36(lgType.callout, "flex min-h-(--bs-lg-type-title-3-leading) items-center text-(--bs-text-secondary)");
var LABEL2 = clsx36(lgType.callout, "text-(--bs-text-secondary)");
function StatStrip({ ref, loading = false, error, children, style, className, ...props }) {
  const columns = Math.max(1, Children.toArray(children).length);
  const failed = error !== void 0 && error !== null && error !== false;
  return /* @__PURE__ */ jsx65("div", { ...props, ref, className: clsx36(STRIP, className), "aria-busy": loading && !failed || void 0, style: { "--stat-strip-columns": columns, ...style }, children: /* @__PURE__ */ jsx65(StripStateContext, { value: { loading, error: failed ? error : void 0 }, children }) });
}
function Stat({ value, label, loading, error, className, onAnimationEnd: onOwnAnimationEnd, ...props }) {
  const { t } = useTranslation32();
  const strip = use6(StripStateContext);
  const failure = error !== void 0 && error !== null && error !== false ? error : strip.error;
  const pending = loading ?? strip.loading;
  const { fade, onAnimationEnd } = useArrival(pending && failure === void 0);
  let slot;
  if (failure !== void 0) slot = /* @__PURE__ */ jsx65("span", { className: clsx36(FAILURE, fade), role: "status", children: failure === true ? t("shared.stats.tile_error", "Couldn\u2019t load") : failure });
  else if (pending) slot = /* @__PURE__ */ jsx65("span", { className: VALUE, "data-stat-value": true, "aria-hidden": true, children: /* @__PURE__ */ jsx65(DashboardPendingFigure, { width: "3.5em" }) });
  else slot = /* @__PURE__ */ jsx65("span", { className: clsx36(VALUE, fade), "data-stat-value": true, children: value });
  return /* @__PURE__ */ jsxs49(
    "div",
    {
      ...props,
      className: clsx36(STAT, className),
      "aria-busy": loading && failure === void 0 || void 0,
      "data-stat": true,
      onAnimationEnd: (event) => {
        onAnimationEnd(event);
        onOwnAnimationEnd?.(event);
      },
      children: [
        slot,
        /* @__PURE__ */ jsx65("span", { className: LABEL2, "data-stat-label": true, children: label })
      ]
    }
  );
}

// src/components/stats/StatTile.tsx
import { forwardRef as forwardRef17, useId as useId7 } from "react";
import { useTranslation as useTranslation33 } from "react-i18next";
import { Button as Button24, IconTile as IconTile3 } from "@buyscout/components";
import clsx37 from "clsx";
import { Fragment as Fragment23, jsx as jsx66, jsxs as jsxs50 } from "react/jsx-runtime";
var HEAD2 = "flex items-center justify-between gap-2.5";
var LABEL3 = clsx37(lgType.callout, "font-medium text-(--bs-text-primary)");
var NUM_ROW = "mt-1.5 flex flex-wrap items-baseline gap-2";
var NUM = clsx37(lgType["title-1"], "font-semibold tabular-nums text-(--bs-text-primary)");
var SUB_VALUE = clsx37(lgType.callout, "mt-1 min-h-[1lh] font-semibold text-(--bs-text-secondary)");
var FAILURE_ROW = "mt-1.5 flex min-h-(--bs-lg-type-title-1-leading) min-w-0 items-center gap-2";
var FAILURE_TEXT = clsx37(lgType.callout, "min-w-0 truncate text-(--bs-text-secondary)");
var CAP = clsx37(lgType.callout, "mt-1.5 text-(--bs-text-secondary)");
var LABEL_ROW = "flex min-w-0 items-center gap-1";
var CHART_SLOT = "mt-2.5 min-h-[var(--stat-tile-chart-height,32px)] min-w-0";
var StatTile = forwardRef17(
  function StatTile2(props, ref) {
    const { label, value, caption, icon, trend, subValue, loading, error, empty, info, chart, className } = props;
    const { t } = useTranslation33();
    const { fade, onAnimationEnd } = useArrival(Boolean(loading) && !error);
    const labelId = useId7();
    return /* @__PURE__ */ jsxs50(
      StatCardShell_default,
      {
        ref,
        className: clsx37("bs-stat-tile [--stat-card-padding:14px_16px]", className),
        "aria-busy": loading && !error || void 0,
        onAnimationEnd,
        children: [
          /* @__PURE__ */ jsxs50("div", { className: HEAD2, children: [
            info ? /* @__PURE__ */ jsxs50("div", { className: LABEL_ROW, children: [
              /* @__PURE__ */ jsx66("div", { className: LABEL3, id: error ? labelId : void 0, children: label }),
              info
            ] }) : /* @__PURE__ */ jsx66("div", { className: LABEL3, id: error ? labelId : void 0, children: label }),
            icon ? /* @__PURE__ */ jsx66(IconTile3, { tone: "gold", size: "sm", children: icon }) : null
          ] }),
          error ? /* @__PURE__ */ jsxs50(Fragment23, { children: [
            /* @__PURE__ */ jsxs50("div", { className: clsx37(FAILURE_ROW, fade), children: [
              /* @__PURE__ */ jsx66("span", { className: FAILURE_TEXT, role: "status", children: error.message ?? t("shared.stats.tile_error", "Couldn\u2019t load") }),
              error.onRetry ? /* @__PURE__ */ jsx66(Button24, { variant: "bordered", size: "sm", "aria-describedby": labelId, onClick: error.onRetry, children: t("shared.data_state.retry", "Retry") }) : null
            ] }),
            subValue !== void 0 ? /* @__PURE__ */ jsx66("div", { className: SUB_VALUE, "aria-hidden": true }) : null
          ] }) : loading ? /* @__PURE__ */ jsxs50(Fragment23, { children: [
            /* @__PURE__ */ jsx66("div", { className: NUM_ROW, children: /* @__PURE__ */ jsx66("div", { className: NUM, children: /* @__PURE__ */ jsx66(DashboardPendingFigure, { width: 96 }) }) }),
            subValue !== void 0 ? /* @__PURE__ */ jsx66("div", { className: SUB_VALUE, children: /* @__PURE__ */ jsx66(DashboardPendingBar, { width: 104 }) }) : null
          ] }) : empty !== void 0 ? /* @__PURE__ */ jsxs50(Fragment23, { children: [
            /* @__PURE__ */ jsx66("div", { className: clsx37(FAILURE_ROW, fade), children: /* @__PURE__ */ jsx66("span", { className: FAILURE_TEXT, children: empty }) }),
            subValue !== void 0 ? /* @__PURE__ */ jsx66("div", { className: SUB_VALUE, "aria-hidden": true }) : null
          ] }) : /* @__PURE__ */ jsxs50(Fragment23, { children: [
            /* @__PURE__ */ jsxs50("div", { className: clsx37(NUM_ROW, fade), children: [
              /* @__PURE__ */ jsx66("div", { className: NUM, children: value }),
              trend ? /* @__PURE__ */ jsx66(TrendChip_default, { trend }) : null
            ] }),
            subValue !== void 0 ? /* @__PURE__ */ jsx66("div", { className: clsx37(SUB_VALUE, fade), children: subValue }) : null
          ] }),
          caption ? /* @__PURE__ */ jsx66("div", { className: CAP, children: caption }) : null,
          chart !== void 0 ? /* @__PURE__ */ jsx66("div", { className: clsx37(CHART_SLOT, fade), children: error || loading || empty !== void 0 ? null : chart }) : null
        ]
      }
    );
  }
);
StatTile.displayName = "StatTile";
var StatTile_default = StatTile;

// src/components/stats/VisitorFunnelCard.tsx
import { forwardRef as forwardRef18, Fragment as Fragment24, useId as useId8 } from "react";
import { Progress } from "@buyscout/components";
import clsx38 from "clsx";
import { Fragment as Fragment25, jsx as jsx67, jsxs as jsxs51 } from "react/jsx-runtime";
var HEAD3 = "flex items-start justify-between gap-3";
var ROWS2 = "mt-[18px] flex flex-col gap-3.5";
var ROW4 = "relative flex flex-col gap-1.5";
var FORK = "absolute start-0 top-0.5 text-[13px] leading-[18px] text-(--bs-text-secondary)";
var ROW_HEAD = "flex items-baseline justify-between gap-2.5";
var ROW_LABEL = "text-[13px] font-semibold text-(--bs-text-primary)";
var ROW_VALUE = "text-[14px] font-semibold tabular-nums text-(--bs-text-primary)";
var ROW_SHARE = "ms-1.5 text-[12px] font-medium tabular-nums text-(--bs-text-secondary)";
var CAPTION2 = "text-[12px] text-(--bs-text-secondary)";
var BRANCH_TRACK = "*:![background:color-mix(in_srgb,var(--color-primary)_42%,transparent)]";
var GAP_ROW = "flex items-baseline justify-between gap-2.5 text-[12px] tabular-nums text-(--bs-text-secondary)";
var EMPTY_STATE = "text-[12px] italic text-(--bs-text-secondary)";
var VisitorFunnelCard = forwardRef18(
  function VisitorFunnelCard2(props, ref) {
    const { label, action, layers, gap, loading, className } = props;
    const base = layers[0]?.value ?? 0;
    const idPrefix = useId8();
    const { fade, onAnimationEnd } = useArrival(Boolean(loading));
    const renderLayer = (layer) => {
      const labelId = `${idPrefix}-${layer.key}`;
      const empty = !loading && layer.emptyState !== void 0;
      const track = layer.branch ? BRANCH_TRACK : void 0;
      return /* @__PURE__ */ jsxs51("div", { className: clsx38(ROW4, layer.branch && "ps-[18px]"), children: [
        layer.branch ? /* @__PURE__ */ jsx67("span", { className: FORK, "aria-hidden": true, children: "\u21B3" }) : null,
        /* @__PURE__ */ jsxs51("div", { className: ROW_HEAD, children: [
          /* @__PURE__ */ jsx67("div", { className: ROW_LABEL, id: labelId, children: layer.label }),
          empty ? null : /* @__PURE__ */ jsx67("div", { className: clsx38(ROW_VALUE, fade), children: loading ? /* @__PURE__ */ jsx67(DashboardPendingBar, { width: 72 }) : /* @__PURE__ */ jsxs51(Fragment25, { children: [
            layer.value.toLocaleString(),
            layer.shareLabel != null ? /* @__PURE__ */ jsx67("span", { className: ROW_SHARE, children: layer.shareLabel }) : null
          ] }) })
        ] }),
        loading ? /* @__PURE__ */ jsx67(Progress, { pending: true, size: "sm", className: track, "aria-labelledby": labelId }) : empty ? /* @__PURE__ */ jsx67("div", { className: clsx38(EMPTY_STATE, fade), children: layer.emptyState }) : /* @__PURE__ */ jsx67(Progress, { value: layer.value, max: base, size: "sm", className: clsx38(track, fade), "aria-labelledby": labelId }),
        layer.caption ? /* @__PURE__ */ jsx67("div", { className: CAPTION2, children: layer.caption }) : null
      ] }, layer.key);
    };
    return /* @__PURE__ */ jsxs51(StatCardShell_default, { ref, className: clsx38("bs-visitor-funnel", className), "aria-busy": loading || void 0, onAnimationEnd, children: [
      /* @__PURE__ */ jsxs51("div", { className: HEAD3, children: [
        /* @__PURE__ */ jsx67(StatCardLabel_default, { children: label }),
        action
      ] }),
      /* @__PURE__ */ jsx67("div", { className: ROWS2, children: layers.map((layer, index) => /* @__PURE__ */ jsxs51(Fragment24, { children: [
        renderLayer(layer),
        gap && index === 0 ? /* @__PURE__ */ jsxs51("div", { className: GAP_ROW, children: [
          /* @__PURE__ */ jsx67("span", { children: gap.label }),
          /* @__PURE__ */ jsx67("span", { className: fade, children: loading ? /* @__PURE__ */ jsx67(DashboardPendingBar, { width: 40 }) : gap.value.toLocaleString() })
        ] }) : null
      ] }, layer.key)) })
    ] });
  }
);
VisitorFunnelCard.displayName = "VisitorFunnelCard";
var VisitorFunnelCard_default = VisitorFunnelCard;

// src/hooks/useKeyedRequest.ts
import { useCallback as useCallback11, useEffect as useEffect19, useLayoutEffect as useLayoutEffect2, useRef as useRef16, useState as useState23 } from "react";

// src/hooks/keyedRequest.ts
var IDLE = { status: "idle" };
function isPending(state, key) {
  return key == null || state.status === "idle" || state.key !== key;
}
function dataFor(state, key) {
  return key != null && state.status === "data" && state.key === key ? state.data : void 0;
}
function errorFor(state, key) {
  return key != null && state.status === "error" && state.key === key ? state.error : void 0;
}
function settleData(key, data) {
  return { status: "data", key, data };
}
function settleError(state, key, error) {
  if (state.status === "data" && state.key === key) return { ...state, refreshError: error };
  return { status: "error", key, error };
}
function beginRetry(state, key) {
  return state.status === "error" && state.key === key ? IDLE : state;
}

// src/hooks/useKeyedRequest.ts
function useKeyedRequest(key, fetcher) {
  const [state, setState] = useState23(IDLE);
  const [attempt, setAttempt] = useState23(0);
  const fetcherRef = useRef16(fetcher);
  useLayoutEffect2(() => {
    fetcherRef.current = fetcher;
  });
  useEffect19(() => {
    if (key == null) return;
    const controller = new AbortController();
    fetcherRef.current(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState(settleData(key, data));
      },
      (error) => {
        if (!controller.signal.aborted) setState((previous) => settleError(previous, key, error));
      }
    );
    return () => controller.abort();
  }, [key, attempt]);
  const retry = useCallback11(() => {
    setState((previous) => beginRetry(previous, key));
    setAttempt((value) => value + 1);
  }, [key]);
  const refresh = useCallback11(() => setAttempt((value) => value + 1), []);
  return {
    data: dataFor(state, key),
    pending: isPending(state, key) && errorFor(state, key) === void 0,
    error: errorFor(state, key),
    refreshError: state.status === "data" && state.key === key ? state.refreshError : void 0,
    retry,
    refresh
  };
}

// src/session/AclGuard.tsx
import { useEffect as useEffect20 } from "react";
import { useLocation as useLocation3, useNavigate } from "react-router-dom";
import { Fragment as Fragment26, jsx as jsx68 } from "react/jsx-runtime";
function AclGuard(props) {
  const { aclAbilities, buildAbility, children, guestGuard = false, authGuard = true, homeRoute, notAuthorized, pending } = props;
  const auth = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation3();
  useEffect20(() => {
    if (auth.user && auth.user.type && !guestGuard && pathname === "/") {
      navigate(homeRoute, { replace: true });
    }
  }, [auth.user, guestGuard, pathname, homeRoute, navigate]);
  const ability = auth.user ? buildAbility(auth.user) : void 0;
  if (auth.user && pathname === "/") {
    return pending;
  }
  if (guestGuard || pathname === "/404" || pathname === "/500" || !authGuard) {
    if (auth.user && ability) {
      return /* @__PURE__ */ jsx68(AbilityContext, { value: ability, children });
    } else {
      return /* @__PURE__ */ jsx68(Fragment26, { children });
    }
  }
  if (ability && auth.user && ability.can(aclAbilities.action, aclAbilities.subject)) {
    return /* @__PURE__ */ jsx68(AbilityContext, { value: ability, children });
  }
  return notAuthorized;
}

// src/session/auth-events.ts
import mitt from "mitt";
var authEventEmitter = mitt();
authEventEmitter.once = function(type, fn) {
  this.on(type, fn);
  this.on(type, this.off.bind(this, type, fn));
};
var AUTH_EVENTS = {
  UNAUTHORIZED: "unauthorized"
};

// src/session/auth-provider.ts
import { startTransition, useCallback as useCallback12, useEffect as useEffect21, useRef as useRef17, useState as useState24 } from "react";
import { useNavigate as useNavigate2 } from "react-router-dom";
import { toast as toast3 } from "@buyscout/components";
function decodeGoogleToken(idToken) {
  const base64Url = idToken.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64).split("").map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`).join("")
  );
  return JSON.parse(jsonPayload);
}
async function loadSessionUser(options) {
  try {
    await options.loadUser?.();
  } catch (error) {
    console.warn("Error occurred when loading user data", error);
    options.clearSession();
  }
}
function endRefusedSession(options, pathname) {
  if (!options.hasSession()) {
    return;
  }
  options.clearSession();
  if (options.guestRoutes?.includes(pathname)) {
    return;
  }
  toast3.dismiss();
  toast3.error(options.sessionExpiredMessage(), { duration: 5e3 });
}
function signOutOfDashboard(options, navigate, markSignedOut) {
  const run = options.runSignOut ?? ((signOut) => signOut());
  run(() => startTransition(() => {
    navigate(options.signInPath());
    markSignedOut();
  }));
}
function useDashboardAuth(options) {
  const [loading, setLoading] = useState24(options.loadUser !== void 0);
  const [signingOut, setSigningOut] = useState24(false);
  const navigate = useNavigate2();
  const latestRef = useRef17({ options, navigate });
  useEffect21(() => {
    latestRef.current = { options, navigate };
  });
  useEffect21(() => {
    const { options: options2 } = latestRef.current;
    if (!options2.loadUser) {
      return;
    }
    void loadSessionUser(options2).finally(() => setLoading(false));
  }, []);
  useEffect21(() => {
    const handleUnauthorized = () => endRefusedSession(latestRef.current.options, window.location.pathname);
    authEventEmitter.on(AUTH_EVENTS.UNAUTHORIZED, handleUnauthorized);
    return () => {
      authEventEmitter.off(AUTH_EVENTS.UNAUTHORIZED, handleUnauthorized);
    };
  }, []);
  useEffect21(() => {
    if (!signingOut) {
      return;
    }
    latestRef.current.options.clearSession();
    setSigningOut(false);
  }, [signingOut]);
  const signOut = useCallback12(() => signOutOfDashboard(latestRef.current.options, latestRef.current.navigate, () => setSigningOut(true)), []);
  return { loading, setLoading, signingOut, decodeGoogleToken, signOut };
}

// src/session/AuthGuard.tsx
import { useEffect as useEffect22 } from "react";
import { useLocation as useLocation4, useNavigate as useNavigate3 } from "react-router-dom";

// src/session/redirects.ts
function signInRedirect(location) {
  if (location.pathname === "/") {
    return "/welcome";
  }
  const returnUrl = location.pathname + location.search + location.hash;
  return `/welcome?returnUrl=${encodeURIComponent(returnUrl)}`;
}

// src/session/AuthGuard.tsx
import { Fragment as Fragment27, jsx as jsx69 } from "react/jsx-runtime";
function AuthGuard(props) {
  const { children, fallback } = props;
  const { user, loading } = useAuth();
  const navigate = useNavigate3();
  const { pathname, search, hash } = useLocation4();
  useEffect22(
    () => {
      if (user === null && !loading) {
        navigate(signInRedirect({ pathname, search, hash }));
      }
    },
    [user, loading, pathname, search, hash, navigate]
  );
  if (loading || user === null) {
    return fallback;
  }
  return /* @__PURE__ */ jsx69(Fragment27, { children });
}

// src/session/dashboard-api.ts
import { toast as toast4 } from "@buyscout/components";
import dayjs3 from "dayjs";

// src/session/web-storage.ts
var WebStorage = class {
  key;
  storage;
  guard;
  constructor(type, key, guard) {
    this.key = key;
    this.storage = typeof window === "undefined" ? void 0 : window?.[type];
    this.guard = guard;
  }
  get() {
    if (!this.storage) return null;
    try {
      return JSON.parse(this.storage.getItem(this.key) || "") ?? null;
    } catch {
      return null;
    }
  }
  safeGet() {
    if (!this.storage) return null;
    const data = this.get();
    if (!this.guard) {
      throw new Error("Cannot use getSafely without assign a storage guard");
    }
    if (!this.guard(data)) {
      console.warn(`Invalid data in ${this.key}`, { data });
      return null;
    }
    return data;
  }
  set(value) {
    if (!this.storage) return;
    this.storage.setItem(this.key, JSON.stringify(value));
  }
  remove() {
    if (!this.storage) return;
    this.storage.removeItem(this.key);
  }
};

// src/session/storage-manager.ts
var StorageManager = class {
  localStorage;
  sessionStorage;
  defaultType;
  defaultValue;
  constructor(fieldName, defaultType, defaultValue = null) {
    this.localStorage = new WebStorage("localStorage", fieldName);
    this.sessionStorage = new WebStorage("sessionStorage", fieldName);
    this.defaultType = defaultType;
    this.defaultValue = defaultValue || null;
  }
  /**
   * Without a type, this tab's own session storage wins: a value another tab later saves
   * to local storage must not replace the one this tab was given.
   */
  get(type) {
    if (type) {
      return this[type].get() || this.defaultValue;
    } else if (this.defaultType) {
      return this[this.defaultType].get() || this.defaultValue;
    } else {
      return this.sessionStorage.get() || this.localStorage.get() || this.defaultValue;
    }
  }
  set(value, type) {
    if (type) {
      this[type].set(value);
    } else if (this.defaultType) {
      this[this.defaultType].set(value);
    } else {
      this.localStorage.set(value);
    }
  }
  update(value) {
    if (this.defaultType) {
      this[this.defaultType].set(value);
    } else if (this.sessionStorage.get() !== null && this.sessionStorage.get() !== void 0) {
      this.sessionStorage.set(value);
    } else {
      this.localStorage.set(value);
    }
  }
  clear(type) {
    if (type !== "sessionStorage") this.localStorage.remove();
    if (type !== "localStorage") this.sessionStorage.remove();
  }
};
var dualTokenStorage = new StorageManager("dualToken");

// src/session/dashboard-api.ts
function isFetchError(e) {
  return e instanceof Object && "error" in e;
}
function errorMessage(error, fallback) {
  const message = isFetchError(error) ? error.error?.message : null;
  return message || fallback;
}
function requestContext(input, init) {
  const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  let path;
  try {
    path = new URL(raw, window.location.origin).pathname;
  } catch {
    path = raw.split("?")[0];
  }
  return { method: init?.method ?? "GET", path };
}
function withExtraHeaders(init, extraHeaders) {
  const headers = new Headers(init?.headers ?? {});
  for (const [name, value] of Object.entries(extraHeaders)) {
    if (!headers.has(name) && value) headers.set(name, value);
  }
  return { ...init, headers };
}
function parseToken(accessToken) {
  if (!accessToken) return null;
  try {
    const payloadPart = accessToken.split(".")[1];
    const payload = JSON.parse(atob(payloadPart));
    return {
      userID: payload.sub,
      email: payload.email,
      role: payload.role,
      issuedAt: dayjs3.unix(payload.iat).toDate(),
      expiresAt: dayjs3.unix(payload.exp).toDate()
    };
  } catch (error) {
    console.error(`Failed to parse token`, { error });
    return null;
  }
}
function checkTokenIsValid(accessToken) {
  const payload = parseToken(accessToken);
  if (!payload) return false;
  const isValid = dayjs3().isBefore(dayjs3(payload.expiresAt).add(-1, "minute"));
  return isValid;
}
function createDashboardApi(options) {
  const { createClient, apiHost, extraHeaders, onForbidden, requestFailedMessage } = options;
  const api = createClient({
    baseUrl: "",
    securityWorker: async () => {
      const accessToken = await refreshTokenIfNeeded();
      if (!accessToken) {
        authEventEmitter.emit(AUTH_EVENTS.UNAUTHORIZED);
        return {};
      }
      return { headers: { Authorization: `Bearer ${String(accessToken)}` } };
    },
    customFetch: async (input, init) => {
      try {
        const requestInit = extraHeaders ? withExtraHeaders(init, extraHeaders()) : init;
        let response;
        if (typeof input === "string" && input.startsWith("/")) {
          const baseURL = (apiHost || "").replace(/\/$/, "");
          response = await fetch(`${baseURL}${input}`, requestInit);
        } else {
          response = await fetch(input, requestInit);
        }
        if (response.status === 401) {
          console.error("401 Unauthorized - emitting auth event", requestContext(input, init));
          authEventEmitter.emit(AUTH_EVENTS.UNAUTHORIZED);
        } else if (response.status === 403) {
          console.error("403 Forbidden", requestContext(input, init));
          await onForbidden?.(response);
        } else if (response.status >= 500) {
          console.error(`Failed to fetch`, { status: response.status, ...requestContext(input, init) });
        }
        return response;
      } catch (error) {
        if (init?.signal?.aborted) throw error;
        console.error(`Failed to fetch`, { error });
        toast4.error(requestFailedMessage(), { duration: 5e3 });
        throw error;
      }
    }
  });
  const refreshTokenPromise = { promise: null };
  async function refreshTokenIfNeeded() {
    if (!refreshTokenPromise.promise) {
      const dualToken = dualTokenStorage.get();
      if (!dualToken) return "";
      if (checkTokenIsValid(dualToken.accessToken)) return dualToken.accessToken;
      refreshTokenPromise.promise = refreshToken(dualToken.refreshToken);
    }
    try {
      return await refreshTokenPromise.promise;
    } finally {
      refreshTokenPromise.promise = null;
    }
  }
  async function refreshToken(refreshToken2) {
    console.debug(`Refreshing token...`);
    try {
      const dualToken = await api.auth.refreshToken({
        headers: {
          Authorization: `Bearer ${refreshToken2}`
        }
      });
      if (dualTokenStorage.get()?.refreshToken === refreshToken2) {
        dualTokenStorage.update(dualToken);
      }
      return dualToken.accessToken;
    } catch (error) {
      console.error(`Failed to refresh token`, { error });
      throw error;
    }
  }
  return { api, refreshTokenIfNeeded };
}

// src/session/GuestGuard.tsx
import { useEffect as useEffect23 } from "react";
import { useLocation as useLocation5, useNavigate as useNavigate4 } from "react-router-dom";
import { Fragment as Fragment28, jsx as jsx70 } from "react/jsx-runtime";
function GuestGuard(props) {
  const { children, fallback, homeRoute } = props;
  const auth = useAuth();
  const navigate = useNavigate4();
  const location = useLocation5();
  useEffect23(() => {
    if (auth.user && !auth.loading) {
      navigate(getReturnUrl(location.search, homeRoute), { replace: true });
    }
  }, [auth.user, auth.loading, location.search, homeRoute, navigate]);
  if (auth.loading || !auth.loading && auth.user !== null) {
    return fallback;
  }
  return /* @__PURE__ */ jsx70(Fragment28, { children });
}
export {
  AIScenario,
  AUTH_EVENTS,
  AbilityContext,
  AclGuard,
  AppErrorFallback,
  AppShell_default as AppShell,
  AuthBrandLockup,
  AuthContext,
  AuthFormHeading,
  AuthFormStage,
  AuthGuard,
  AuthScaffold,
  AuthView,
  BELOW_LG,
  BELOW_MD,
  BELOW_SM,
  BUYSCOUT_URLS,
  BarChart,
  BlankLayout,
  BootScreen,
  Can,
  CanViewNavLink,
  ChartCard,
  ChartLegend,
  ChartScaffold,
  ChatWrapper_default as ChatWrapper,
  DEFAULT_MAX_CUSTOM_DAYS,
  DEFAULT_TIME_RANGE_PRESETS,
  DashboardAppBarContent,
  DashboardCollectionPanel,
  DashboardDisclosureRow,
  DashboardDocument,
  DashboardDocumentFooter,
  DashboardErrorPage,
  DashboardHeldState,
  DashboardInfoPopover,
  DashboardLayout,
  DashboardLocalePicker,
  DashboardLocalizableField,
  DashboardLocalizableGroup,
  DashboardPage,
  DashboardPageLoading,
  DashboardPageState,
  DashboardPanel,
  DashboardPendingBar,
  DashboardPendingFigure,
  DashboardRouteFallback,
  DashboardSaveFooter,
  DashboardSettingRow,
  DashboardUserMenu,
  ErrorScaffold,
  GuestGuard,
  Header_default as Header,
  HeroStatCard_default as HeroStatCard,
  LineChart,
  NotificationsPopover_default as NotificationsPopover,
  PageHeader_default as PageHeader,
  PageInsideScrollContext,
  PageInsideScrollProvider,
  REDUCED_MOTION_QUERY,
  RESEND_COOLDOWN_SECONDS,
  ResetPasswordError,
  ResetPasswordStep,
  ResetPasswordView,
  SLOT2 as SLOT,
  SUPPORTED_COUNTRIES,
  ScrollToTop_default as ScrollToTop,
  SettingsConsumer,
  SettingsContext,
  SettingsProvider,
  Sidebar_default as Sidebar,
  Sparkline,
  Stat,
  StatCardFailure_default as StatCardFailure,
  StatCardLabel_default as StatCardLabel,
  StatCardShell_default as StatCardShell,
  StatStrip,
  StatTile_default as StatTile,
  StatisticsTimeRange,
  StorageManager,
  TimeRangePicker,
  ToolCallResultDialog_default as ToolCallResultDialog,
  TrendChip_default as TrendChip,
  UnsavedChangesDialog,
  VisitorFunnelCard_default as VisitorFunnelCard,
  WebStorage,
  acceptVerificationCooldown,
  areaPath,
  authEventEmitter,
  axisTickIndexes,
  axisTickValues,
  bestIndex,
  checkCustomRange,
  clampCustomRange,
  createDashboardApi,
  customTimeRange,
  decodeGoogleToken,
  detectUserCountry,
  dualTokenStorage,
  endRefusedSession,
  errorMessage,
  fillSlots,
  formatCountdown,
  formatDateToMonthShort,
  formatMessageContent,
  formatTimeRangeLabel,
  formatToolName,
  getDateRange,
  getPreviousStatisticsTimeRangeDates,
  getReturnUrl,
  getStatisticsTimeRangeDates,
  installChunkReloadHandler,
  isFetchError,
  isSameTimeRange,
  linePath,
  loadSessionUser,
  niceAxis,
  niceAxisTop,
  normalizeVerificationCode,
  parseDateKey,
  parseTimeRangeSearchParams,
  percentShares,
  resendCooldownSeconds,
  resetPasswordFailure,
  runningTotals,
  sendPasswordResetCode,
  seriesMax,
  signInRedirect,
  signOutOfDashboard,
  statusColorMap,
  themeConfig_default as themeConfig,
  timeRangeBounds,
  timeRangeSearchParams,
  toChannelChatSession,
  toDateKey,
  useAuth,
  useDashboardAuth,
  useKeyedRequest,
  useLeaveGuard,
  useMediaQuery,
  useSettings
};
//# sourceMappingURL=index.js.map